"use client"

import type React from "react"

import { useState, useEffect, useRef, useCallback } from "react"
import { useRouter } from "next/navigation"
import { useConfig, type AppConfig } from "@/lib/config-provider"
import type { LEDConfig } from "@/types/synoptique"
import { useIsMobile } from "@/hooks/use-mobile"
import { useDelayedGridCounter } from "@/hooks/use-delayed-grid-counter"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { MathInput } from "@/app/components/math-input"
import { Label } from "@/components/ui/label"
import { Slider } from "@/components/ui/slider"
import { Switch } from "@/components/ui/switch"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import { Textarea } from "@/components/ui/textarea"
import { toast } from "sonner"
import {
  Download,
  Play,
  Pause,
  RotateCcw,
  Maximize,
  Moon,
  Sun,
  Type,
  Plus,
  Trash2,
  AlignCenter,
  AlignLeft,
  AlignRight,
  Move,
  Upload,
  ImageIcon,
  Save,
  FolderOpen,
  Grid,
  Grid3X3,
  Info,
  History,
  Gamepad2,
  Monitor,
  X,
  ChevronDown,
  CheckCircle,
  Zap,
  MonitorCog,
  ScreenShare,
  ScreenShareOff,
  Smartphone,
  Send,
  Video,
  AlertCircle,
  Loader2,
} from "lucide-react"
import { ThemeProvider } from "@/components/theme-provider"
import { useTheme } from "next-themes"
import useElectron from '@/hooks/useElectron'
import { isTauriApp } from '@/lib/tauri-auth'
import { Checkbox } from "@/components/ui/checkbox"
import { LanguageSwitcher } from "@/components/language-switcher"
import { useLanguage } from "@/lib/i18n/language-provider"
import { DynamicTitle } from "@/components/dynamic-title"
import { AuthButton } from "@/components/auth/auth-button"
import { useAuth } from "@/lib/auth-provider"
import { drawStandardPattern } from "@/lib/standard-patterns"
import {
  renderGradient, renderGradient4, renderSolid, renderCheckerboard,
  renderRainbow, renderColorBars, renderPulse, renderSpiral,
  renderOrangeTeal, renderMagentaSummer, renderSummer, renderBluePurple,
  renderSnake,
} from "@/lib/patterns/custom-patterns"
import {
  renderGrid, renderCyanPanels, renderSelfTestLEDNumbers,
  renderStandardLEDNumbers, renderClassicSonar,
  renderLogoElements, renderTextElements,
  renderResolution, renderDefaultLogo, renderIconLight, renderPixelMapMode,
  renderNovaCustomNumbers,
} from "@/lib/patterns/render-overlays"
import { ensureCompleteClassicConfig } from "@/lib/config-compatibility"
import { AppLogo } from "@/components/app-logo"
import Link from "next/link"
import SmartLink from "@/components/smart-link"
import { Footer } from "@/components/ui/footer"
import { useSnakeGame, SnakeGameState } from "@/components/ui/snake-game"
import { SnakeLeaderboard, SnakeLeaderboardHandle } from "@/components/ui/snake-leaderboard"
import { SnakeScoreSubmit } from "@/components/ui/snake-score-submit"
import { FuturisticMessage } from "@/components/futuristic-message"
import SessionIntegration, { useSessionIntegration } from "@/components/sessions/session-integration"
import "@/app/lib/init-sessions" // NOUVEAU : Auto-initialisation sessions
import { BurgerMenu } from "@/components/ui/burger-menu"
import MobileSelfTestControls from "@/components/mobile-self-test-controls"
import { useWebCodecsVideoExport } from "@/hooks/use-webcodecs-video-export"
import { FfmpegExportDialog } from "@/components/ffmpeg-export-dialog"
import useTauri from "@/hooks/useTauri"
import NovaPortSelector from "@/components/grid/NovaPortSelector"
import { useNdi } from "@/hooks/use-ndi"
import { NdiControl } from "@/components/ndi-control"
import { captureEvent } from "@/lib/posthog-helper"
import { uploadTestPatternToSupabase } from "@/lib/supabase-storage"
// Ancien système d'export (gardé en commentaire pour rollback si nécessaire)
// import { useVideoExport } from "@/hooks/use-video-export"
// import { ExportProgressWindow } from "@/components/export-progress-window"
// import { ExportVideoDialog } from "@/components/export-video-dialog"
import { getOptimalLoopSettings, formatSettingsChangeMessage } from "@/lib/video-loop-settings"
import { DEFAULT_CONFIG, MIN_COLUMNS_ROWS, MAX_PIXELS } from "@/lib/default-config"
import { useWebGLRenderer } from "@/hooks/use-webgl-renderer"
import { useSelfTest, type SelfTestMode } from "@/hooks/use-self-test"

interface TextElement {
  id: string
  text: string
  x: number
  y: number
  fontSize: number
  fontFamily: string
  fontWeight: number
  fontStyle: string
  textAlign: string
  color: string
  strokeWidth?: number
}

interface LogoElement {
  id: string
  image: HTMLImageElement
  x: number
  y: number
  width: number
  height: number
  originalWidth: number
  originalHeight: number
  opacity: number
  isGif?: boolean
  imageSrc?: string
}

// Logo sérialisé (config sauvegardée ou mire) — l'image est un data URL
interface SerializedLogoData {
  id: string
  x: number
  y: number
  width: number
  height: number
  opacity?: number
  dataUrl?: string
  image?: string
  imageSrc?: string
  originalWidth?: number
  originalHeight?: number
  isGif?: boolean
}

// Snapshot de configuration chargeable dans l'éditeur
// (config sauvegardée classique ou mire Synoptique)
interface GridConfigSnapshot {
  canvas?: Partial<AppConfig>
  textElements?: TextElement[]
  logoElements?: SerializedLogoData[]
  activePatternType?: 'custom' | 'solid' | 'standard'
  selfTestMode?: Omit<SelfTestMode, 'activePanels'> & { activePanels?: string[] }
  cyanPanels?: string[]
  subdivisionMode?: boolean
  redZones?: string[]
  selectedQuadrant?: 'tl' | 'tr' | 'bl' | 'br'
  disablePreviewWhenExternal?: boolean
  isAnimating?: boolean
  columnsDisplayValue?: string
  rowsDisplayValue?: string
}

function LEDTestPatternGeneratorContent() {
  // États pour l'affichage multi-écran
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null)
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const [externalDisplayWindow, setExternalDisplayWindow] = useState<Window | { id: any; isElectron: boolean; close: () => any; } | null>(null)
  const [isExternalDisplayConnected, setIsExternalDisplayConnected] = useState(false)
  const [isExternalDisplayFullscreen, setIsExternalDisplayFullscreen] = useState(false)
  const [availableScreens, setAvailableScreens] = useState<any[]>([])
  const [selectedScreenIndex, setSelectedScreenIndex] = useState(0)
  const [showScreenSelector, setShowScreenSelector] = useState(false)
  const [isScreensLoaded, setIsScreensLoaded] = useState(false)

  // Déclarer useLanguage tôt pour être disponible dans les useEffect
  const { t, language } = useLanguage()

  // Auth pour vérifier si l'utilisateur est Pro (gestion du logo)
  const { isPro } = useAuth()

  const [disablePreviewWhenExternal, setDisablePreviewWhenExternal] = useState(false)
  const [isExternalDisplay, setIsExternalDisplay] = useState(false) // NOUVEAU: Mode affichage externe
  const isExternalDisplayRef = useRef(false) // Ref pour accès dans les closures

  // Self-Test state management (hook custom)
  const {
    selfTestMode, setSelfTestMode,
    cyanPanels, setCyanPanels,
    subdivisionMode, setSubdivisionMode,
    redZones, setRedZones,
    selectedQuadrant, setSelectedQuadrant,
    resetSelfTest, enableSelfTest, disableSelfTest,
    toggleCyanPanel, toggleRedZone,
  } = useSelfTest()

  // État pour la sélection GPU - Auto-détection côté client via useEffect (évite SSR issues)
  const [powerPreference, setPowerPreference] = useState<'low-power' | 'high-performance' | 'default'>('default')

  // Ref pour éviter les boucles de synchronisation
  const isReceivingExternalUpdate = useRef(false)

  // États pour sauvegarder les valeurs avant désactivation automatique pour mires standards
  const [previousUIStates, setPreviousUIStates] = useState<{
    showTechnicalDetails: boolean;
    showGrid: boolean;
    showLogo: boolean;
    showLEDNumbers: boolean;
    showResolution: boolean;
    showSonar: boolean;
  } | null>(null)

  // Initialiser au moins un écran pour permettre les tests (même avec un seul moniteur)
  useEffect(() => {
    if (availableScreens.length === 0) {
      setAvailableScreens([{
        id: 'primary',
        name: t("external.screenName"),
        width: window.screen.width,
        height: window.screen.height,
        isPrimary: true
      }]);
      setIsScreensLoaded(true);
    }
  }, [availableScreens.length, t]);

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const fullscreenContainerRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const animationRef = useRef<number | null>(null)
  const startTimeRef = useRef<number>(Date.now())
  const defaultLogoRef = useRef<HTMLImageElement | null>(null)
  const iconLightRef = useRef<HTMLImageElement | null>(null)

  // Optimisation Advanced Sonar Mode - Pool COMPLET de canvases réutilisables
  // Canvas principal hors-écran pour la composition finale
  const sonarOffscreenCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const sonarOffscreenCtxRef = useRef<CanvasRenderingContext2D | null>(null)
  // Canvas de background pour stocker l'image avant la grille (évite getImageData/putImageData)
  const sonarBackgroundCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const sonarBackgroundCtxRef = useRef<CanvasRenderingContext2D | null>(null)
  const sonarLastDimensionsRef = useRef<{ width: number; height: number } | null>(null)

  // Références pour le module Self-Test (gestion immédiate)
  const redrawTimeoutRef = useRef<number | null>(null)
  const tempSelfTestModeRef = useRef<any>(null)
  const keyPressTimeoutRef = useRef<number | null>(null)
  const isNavigatingRef = useRef<boolean>(false)
  const { theme, setTheme } = useTheme()
  const [mounted, setMounted] = useState(false)
  const { isElectronApp, toggleFullscreen, saveConfig: saveConfigElectron, loadConfig: loadConfigElectron, exportCanvas: exportCanvasElectron, deleteConfig: deleteConfigElectron, savedConfigs: savedConfigsElectron, loadConfigList: loadConfigListElectron, exportConfigFile: exportConfigFileElectron, importConfigFile: importConfigFileElectron } = useElectron()
  
  // Tauri detection for platform-specific features (déclaré tôt pour être disponible dans les useEffect)
  const { isTauriApp, getDisplays: getTauriDisplays, openExternalDisplay: openTauriExternalDisplay, closeExternalDisplay: closeTauriExternalDisplay, saveConfig: saveConfigTauri, loadConfig: loadConfigTauri, exportCanvas: exportCanvasTauri, deleteConfig: deleteConfigTauri, savedConfigs: savedConfigsTauri, loadConfigList: loadConfigListTauri, exportConfigFile: exportConfigFileTauri, importConfigFile: importConfigFileTauri } = useTauri()

  // NDI texture sharing (via Tauri)
  const ndi = useNdi()

  // Utiliser les fonctions Tauri si on est sur Tauri, sinon Electron/Web
  const saveConfig = isTauriApp ? saveConfigTauri : saveConfigElectron;
  const loadConfig = isTauriApp ? loadConfigTauri : loadConfigElectron;
  const exportCanvas = isTauriApp ? exportCanvasTauri : exportCanvasElectron;
  const deleteConfig = isTauriApp ? deleteConfigTauri : deleteConfigElectron;
  const savedConfigs = isTauriApp ? savedConfigsTauri : savedConfigsElectron;
  const loadConfigList = isTauriApp ? loadConfigListTauri : loadConfigListElectron;
  const exportConfigFile = isTauriApp ? exportConfigFileTauri : exportConfigFileElectron;
  const importConfigFile = isTauriApp ? importConfigFileTauri : importConfigFileElectron;

  const router = useRouter()
  const isMobile = useIsMobile()

  // WebGL Renderer avec GPU selection
  const { isWebGLAvailable, renderPattern: renderPatternGPU } = useWebGLRenderer(canvasRef, powerPreference)

  // Détection si on est dans l'iframe du Synoptique
  const [isEmbeddedInSynoptic, setIsEmbeddedInSynoptic] = useState(false)

  // Boucle de capture/envoi NDI dédiée et throttlée (60 FPS cible).
  // La capture lourde (getImageData GPU→CPU) est déportée dans un Web Worker
  // via OffscreenCanvas pour libérer le thread principal (preview fluide).
  // createImageBitmap est asynchrone et ne bloque pas le thread principal.
  // NOTE: Capture à 30 fps pour réduire la charge de transfert IPC de 50%.
  // Le worker Rust NDI envoie à 60 fps cadence fixe avec renvoi de la dernière frame,
  // donc le flux NDI reste fluide même avec moins de frames uniques.
  useEffect(() => {
    if (!ndi.isActive || !isTauriApp) return

    const TARGET_FPS = 60
    const FRAME_INTERVAL = 1000 / TARGET_FPS
    let lastFrameTime = 0
    let animationFrameId: number | null = null
    let captureInFlight = false
    let disposed = false

    const worker = new Worker(
      new URL('../../hooks/workers/ndi-capture.worker.ts', import.meta.url),
      { type: 'module' }
    )

    worker.onmessage = (event: MessageEvent<{ type: 'captured'; buffer: ArrayBuffer; width: number; height: number }>) => {
      captureInFlight = false
      if (disposed || event.data.type !== 'captured') return

      const { buffer, width, height } = event.data
      ndi.sendFrame({
        width,
        height,
        data: new Uint8Array(buffer),
      }).catch(err => {
        console.error('Erreur envoi frame NDI:', err)
      })
    }

    const sendNdiFrame = (timestamp: number) => {
      if (!ndi.isActive || disposed) {
        animationFrameId = null
        return
      }

      const elapsed = timestamp - lastFrameTime
      // Ne capturer que si: intervalle atteint, aucune capture en cours dans le
      // worker, et le pipeline IPC peut accepter une nouvelle frame.
      if (elapsed >= FRAME_INTERVAL && !captureInFlight && ndi.canSendFrame()) {
        lastFrameTime = timestamp - (elapsed % FRAME_INTERVAL)

        const canvas = canvasRef.current
        if (canvas && canvas.width > 0 && canvas.height > 0) {
          const width = canvas.width
          const height = canvas.height
          captureInFlight = true
          // createImageBitmap est asynchrone: le snapshot GPU ne bloque pas.
          createImageBitmap(canvas).then(bitmap => {
            if (disposed) {
              bitmap.close()
              captureInFlight = false
              return
            }
            worker.postMessage({ type: 'frame', bitmap, width, height }, [bitmap])
          }).catch(err => {
            captureInFlight = false
            console.error('Erreur createImageBitmap pour NDI:', err)
          })
        }
      }

      animationFrameId = requestAnimationFrame(sendNdiFrame)
    }

    animationFrameId = requestAnimationFrame(sendNdiFrame)

    return () => {
      disposed = true
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId)
      }
      worker.terminate()
    }
  }, [ndi.isActive, isTauriApp, ndi.sendFrame, ndi.canSendFrame])

  // États pour gérer l'affichage mobile/desktop
  const [showMobileBanner, setShowMobileBanner] = useState(false)
  const [displayMode, setDisplayMode] = useState<'auto' | 'mobile' | 'desktop'>('auto')
  const [effectiveIsMobile, setEffectiveIsMobile] = useState(false)
  const [urlKey, setUrlKey] = useState(0) // Pour forcer le re-render sur changement d'URL

  // Gérer les paramètres d'URL et le mode d'affichage
  useEffect(() => {
    if (typeof window === "undefined" || !mounted) return

    const updateDisplayMode = () => {
      const urlParams = new URLSearchParams(window.location.search)
      const forceDesktop = urlParams.get("forceDesktop") === "true"
      const forceMobile = urlParams.get("forceMobile") === "true"
      const forceExternal = urlParams.get("forceExternal") === "true" // NOUVEAU: Détecter mode externe
      const isInIframe = window.self !== window.top

      isExternalDisplayRef.current = forceExternal // Synchroniser la ref

      // Envoyer un signal READY quand la page externe est chargée
      if (forceExternal) {
        setTimeout(() => {
          const channel = new BroadcastChannel('visualgrid-sync')
          channel.postMessage({
            type: 'EXTERNAL_DISPLAY_READY',
            data: { timestamp: Date.now() }
          })
          channel.close()
        }, 1000)
      }

      // NOUVEAU : Si mobile arrive sans paramètres, rediriger vers forceDesktop=true
      if (isMobile && !forceDesktop && !forceMobile && !isInIframe) {
        window.history.replaceState(null, '', '/grid?forceDesktop=true')
        // Mettre à jour les paramètres après la redirection
        urlParams.set('forceDesktop', 'true')
      }

      // Déterminer le mode d'affichage
      let mode: 'auto' | 'mobile' | 'desktop' = 'auto'
      if (forceDesktop) mode = 'desktop'
      else if (forceMobile) mode = 'mobile'

      // Calculer l'affichage effectif
      let effective = isMobile
      if (mode === 'desktop') effective = false
      else if (mode === 'mobile') effective = true

      // Pousser les mises à jour d'état hors du contexte synchrone pour éviter
      // les warnings "useInsertionEffect must not schedule updates"
      setTimeout(() => {
        setIsExternalDisplay(forceExternal)
        setDisplayMode(mode)
        setEffectiveIsMobile(effective)

        // Afficher le bandeau si on est sur mobile (détection) et pas dans iframe
        if (isMobile && !isInIframe) {
          setShowMobileBanner(true)
        }
      }, 0)
    }

    // Mettre à jour au chargement
    updateDisplayMode()

    // Écouter les changements d'URL (popstate et pushstate)
    const handleUrlChange = () => {
      updateDisplayMode()
      // Reporter la mise à jour d'état hors du contexte synchrone de history.replaceState
      setTimeout(() => {
        setUrlKey(prev => prev + 1)
      }, 0)
    }

    window.addEventListener('popstate', handleUrlChange)

    // Intercepter pushState et replaceState pour détecter les changements
    const originalPushState = window.history.pushState
    const originalReplaceState = window.history.replaceState

    window.history.pushState = function (...args) {
      originalPushState.apply(window.history, args)
      handleUrlChange()
    }

    window.history.replaceState = function (...args) {
      originalReplaceState.apply(window.history, args)
      handleUrlChange()
    }

    return () => {
      window.removeEventListener('popstate', handleUrlChange)
      window.history.pushState = originalPushState
      window.history.replaceState = originalReplaceState
    }
  }, [isMobile, mounted])

  // Gérer CTRL+W pour fermer la fenêtre externe
  useEffect(() => {
    if (typeof window === "undefined" || !mounted) return

    const urlParams = new URLSearchParams(window.location.search)
    const forceExternal = urlParams.get("forceExternal") === "true"

    if (!forceExternal) return

    const handleKeyDown = async (e: KeyboardEvent) => {
      // CTRL+W ou CMD+W (Mac)
      if ((e.ctrlKey || e.metaKey) && e.key === 'w') {
        e.preventDefault()
        e.stopPropagation()

        // Fermer la fenêtre selon le type d'application
        if (isTauriApp && closeTauriExternalDisplay) {
          try {
            // Obtenir l'ID de la fenêtre actuelle depuis Tauri
            const { getCurrentWebviewWindow } = await import('@tauri-apps/api/window')
            const currentWindow = getCurrentWebviewWindow()
            if (currentWindow) {
              const windowLabel = currentWindow.label()
              // Si c'est une fenêtre externe (commence par "external_")
              if (windowLabel.startsWith('external_')) {
                await closeTauriExternalDisplay(windowLabel)
              } else {
                // Sinon, fermer la fenêtre actuelle
                await currentWindow.close()
              }
            }
          } catch (error) {
            // console.error('Erreur lors de la fermeture Tauri:', error)
          }
        } else if (isElectronApp && (window as any).electronAPI) {
          // Pour Electron, on peut fermer la fenêtre directement
          try {
            window.close()
          } catch (error) {
            // console.error('Erreur lors de la fermeture Electron:', error)
          }
        } else {
          // Pour Web, fermer la fenêtre
          window.close()
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [mounted, isTauriApp, isElectronApp, closeTauriExternalDisplay])

  // Constantes importées de @/lib/default-config

  // États pour les erreurs de validation
  const [columnsError, setColumnsError] = useState<string | null>(null)
  const [rowsError, setRowsError] = useState<string | null>(null)
  const [renderError, setRenderError] = useState<string | null>(null)

  // États pour le mode Nova Screen Config
  const [novaPanelConnections, setNovaPanelConnections] = useState<PanelConnection[]>([])
  const [selectedNovaPort, setSelectedNovaPort] = useState<number | null>(null)
  
  // Ref pour éviter les problèmes de fermeture dans le handler de clic
  const selectedNovaPortRef = useRef<number | null>(null)
  selectedNovaPortRef.current = selectedNovaPort
  
  // Ref pour éviter les problèmes de stale closure dans drawPattern
  const novaPanelConnectionsRef = useRef<PanelConnection[]>([])
  
  // Refs pour le drag-to-select en mode Nova Screen Config
  const novaDragStartRef = useRef<{ col: number; row: number } | null>(null)
  const novaDragCurrentRef = useRef<{ col: number; row: number } | null>(null)
  const isNovaDraggingRef = useRef(false)
  const novaDragPathRef = useRef<{ col: number; row: number }[]>([]) // Stocke tous les panneaux survolés

  // États pour l'affichage des inputs (permettent de vider complètement les champs)
  const [columnsDisplayValue, setColumnsDisplayValue] = useState<string>("10")
  const [rowsDisplayValue, setRowsDisplayValue] = useState<string>("6")
  const [showExportDialog, setShowExportDialog] = useState(false)

  // Hook pour gérer le comptage différé des grilles
  const gridCounter = useDelayedGridCounter({ mode: 'classicMode' });

  // Définir le type de l'état de configuration
  type ConfigState = {
    width: number;
    height: number;
    gridSize: number;
    rotationDeg: 0 | 90 | 180 | -90;
    animationSpeed: number;
    sonarSpeed: number;
    patternSeed: number;
    showGrid: boolean;
    gridEvenOpacity: number;
    gridOddOpacity: number;
    patternType: string;
    standardPatternType: string;
    rampDirection: "horizontal" | "vertical";
    showResolution: boolean;
    resolutionFontSize: number;
    showLEDNumbers: boolean;
    showTechnicalDetails: boolean; // Nouvelle option unifiée
    showLogo: boolean;
    showSonar: boolean;
    sonarThickness: number;
    showVisualEffects: boolean; // Nouvelle option unifiée Logo + Sonar
    showDefaultLogo: boolean; // Option pour afficher le logo VisualGrid au centre
    ledWallMode: {
      enabled: boolean;
      columns: number;
      rows: number;
      panelWidth: number;
      panelHeight: number;
      borderWidth: number;
    };
    sonarRevealMode: boolean;
    allowFullscreenScroll: boolean;
    stretchFullscreen: boolean;
    gridOffset: {
      x: number;
      y: number;
    };
    pixelMapMode: {
      enabled: boolean;
      mainScreenText: string;
      posX: number;
      posY: number;
      resW: number;
      resH: number;
      panelW: number;
      panelH: number;
      size: number;
    };
    novaScreenConfigMode: boolean; // Mode Nova Screen Config pour numérotation personnalisée
    novaShowArrows: boolean; // Afficher les flèches entre les connexions
    enableAnamorphosis: boolean; // Mode anamorphose pour compensation LST
    novaShowLines: boolean; // Afficher les lignes entre les connexions
  }

  interface PanelConnection {
    panelId: string;
    port: number;
    order: number;
  }

  // Effet pour éviter les erreurs d'hydratation avec le thème
  useEffect(() => {
    setMounted(true)
  }, [])

  // Auto-détection du GPU côté client
  useEffect(() => {
    if (typeof document === 'undefined') return;

    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('webgl2');

      if (!gl) {
        setPowerPreference('default');
        return;
      }

      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (!debugInfo) {
        setPowerPreference('high-performance');
        return;
      }

      const rendererName = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL).toLowerCase();

      // Détecter la marque du GPU et sélectionner la préférence appropriée
      if (rendererName.includes('nvidia') || rendererName.includes('amd') || rendererName.includes('radeon') || rendererName.includes('apple') || rendererName.includes('metal')) {
        setPowerPreference('high-performance');
      } else if (rendererName.includes('intel')) {
        setPowerPreference('low-power');
      } else {
        setPowerPreference('default');
      }

    } catch (error) {
      setPowerPreference('default');
    }
  }, [])

  // Détecter si on est dans l'iframe du Synoptique
  useEffect(() => {
    // Vérifier si on est dans un iframe
    const isInIframe = window.self !== window.top

    // Vérifier le paramètre URL
    const urlParams = new URLSearchParams(window.location.search)
    const embedParam = urlParams.get('embed')

    setIsEmbeddedInSynoptic(isInIframe && embedParam === 'synoptic')
  }, [])

  // Synchronisation BroadcastChannel pour la page principale
  useEffect(() => {
    // Créer le canal de communication
    broadcastChannelRef.current = new BroadcastChannel('visualgrid-sync');

    // Écouter les messages de l'affichage externe
    const handleSync = (event: MessageEvent) => {
      const { type, data } = event.data;
      const isExternal = isExternalDisplayRef.current; // Utiliser la ref pour avoir la valeur à jour
      switch (type) {
        case 'EXTERNAL_DISPLAY_READY':
          // Seulement traiter sur la page PRINCIPALE (pas sur la page externe elle-même)
          if (!isExternal) {
            setIsExternalDisplayConnected(true);

            // Délai plus long pour Electron (fenêtres BrowserWindow)
            const syncDelay = isElectronApp ? 2000 : 500;

            setTimeout(() => {
              syncAllToExternal();

              // Retry supplémentaire pour Electron après 2 secondes
              if (isElectronApp) {
                setTimeout(() => {
                  syncAllToExternal();
                }, 2000);
              }
            }, syncDelay);
          }
          break;
        case 'REQUEST_INITIAL_SYNC':
          syncAllToExternal();

          // Double synchronisation pour Electron après un délai
          if (data.isElectron) {
            setTimeout(() => {
              syncAllToExternal();
            }, 1000);
          }
          break;
        case 'EXTERNAL_DISPLAY_STATE':
          setIsExternalDisplayFullscreen(data.isFullscreen);
          break;

        // === Handlers pour la page External Display (reçoit les données de la page principale) ===
        case 'CONFIG_UPDATE':
          // Recevoir la configuration de la page principale
          if (isExternal) {
            setConfig(data);
          }
          break;
        case 'TEXT_ELEMENTS_UPDATE':
          // Recevoir les éléments de texte de la page principale
          if (isExternal) {
            setTextElements(data);
          }
          break;
        case 'LOGO_ELEMENTS_UPDATE':
          // Recevoir les éléments de logo de la page principale
          if (isExternal && data) {
            // Reconstruire les HTMLImageElement depuis les URLs
            const reconstructedLogos = data.map((logoData: any) => {
              const img = new Image();
              const imageSrc = logoData.image || logoData.imageSrc || logoData.dataUrl;
              img.src = imageSrc;
              return {
                ...logoData,
                image: img,
                isGif: logoData.isGif || false,
                imageSrc: logoData.isGif ? imageSrc : undefined
              };
            });
            setLogoElements(reconstructedLogos);
          }
          break;
        case 'ANIMATION_STATE_UPDATE':
          // Recevoir l'état d'animation de la page principale
          if (isExternal) {
            setIsAnimating(data.isAnimating);
          }
          break;
        case 'ACTIVE_PATTERN_TYPE_UPDATE':
          // Recevoir le type de pattern actif de la page principale
          if (isExternal) {
            setActivePatternType(data.activePatternType);
          }
          break;
        case 'PREVIEW_DISABLE_UPDATE':
          // Recevoir le paramètre de désactivation de l'aperçu
          if (isExternal) {
            setDisablePreviewWhenExternal(data.disablePreviewWhenExternal);
          }
          break;
        case 'SNAKE_STATE_UPDATE':
          // Recevoir l'état du jeu Snake de la page principale
          if (isExternal && data) {
            setSnakeGameState({
              ...data,
              visitedCells: new Set(data.visitedCells)
            });
          }
          break;
        // === Fin handlers External Display ===

        case 'SELF_TEST_UPDATE':
          // Recevoir les mises à jour Self-Test (bidirectionnel)
          isReceivingExternalUpdate.current = true;
          setSelfTestMode(prev => ({
            ...prev,
            enabled: data.enabled,
            selectedPanelX: data.selectedPanelX,
            selectedPanelY: data.selectedPanelY,
            trailMode: data.trailMode,
            showBorders: data.showBorders,
            activePanels: new Set(data.activePanels)
          }));
          setTimeout(() => {
            isReceivingExternalUpdate.current = false;
          }, 100);
          break;
        case 'CYAN_PANELS_UPDATE':
          // Recevoir les panneaux cyan (bidirectionnel)
          isReceivingExternalUpdate.current = true;
          setCyanPanels(new Set(data.cyanPanels));
          setTimeout(() => {
            isReceivingExternalUpdate.current = false;
          }, 100);
          break;
        case 'SUBDIVISION_UPDATE':
          // Recevoir les mises à jour de subdivision (bidirectionnel)
          isReceivingExternalUpdate.current = true;
          setSubdivisionMode(data.subdivisionMode);
          setRedZones(new Set(data.redZones));
          setSelectedQuadrant(data.selectedQuadrant);
          setTimeout(() => {
            isReceivingExternalUpdate.current = false;
          }, 100);
          break;
        case 'EXTERNAL_DISPLAY_CLOSING':
          // L'affichage externe se ferme
          setExternalDisplayWindow(null);
          setIsExternalDisplayConnected(false);
          setIsExternalDisplayFullscreen(false);
          break;
        case 'MAIN_PAGE_CLOSING':
          // Si nous sommes en mode externe ET que la page principale se ferme, se fermer aussi
          if (isExternal) {
            window.close();
          }
          break;
      }
    };

    broadcastChannelRef.current.addEventListener('message', handleSync);

    // Vérifier périodiquement si la fenêtre externe est fermée
    const checkExternalWindow = setInterval(async () => {
      if (externalDisplayWindow) {
        // Cas 1: Fenêtre Web (window.open)
        if ('closed' in externalDisplayWindow && externalDisplayWindow.closed) {
          setExternalDisplayWindow(null);
          setIsExternalDisplayConnected(false);
          setIsExternalDisplayFullscreen(false);
        }
        // Cas 2: Fenêtre Electron 
        else if ('isElectron' in externalDisplayWindow && externalDisplayWindow.isElectron && (window as any).electronAPI) {
          try {
            const state = await (window as any).electronAPI.getExternalDisplayState(externalDisplayWindow.id);
            if (!state.exists) {
              setExternalDisplayWindow(null);
              setIsExternalDisplayConnected(false);
              setIsExternalDisplayFullscreen(false);
            }
          } catch (error) {
            // Si erreur, la fenêtre est probablement fermée
            setExternalDisplayWindow(null);
            setIsExternalDisplayConnected(false);
            setIsExternalDisplayFullscreen(false);
          }
        }
      }
    }, 1000);

    // Détecter la fermeture de la page principale
    const handleBeforeUnload = () => {
      if (broadcastChannelRef.current) {
        // Envoyer un signal de fermeture
        broadcastChannelRef.current.postMessage({
          type: 'MAIN_PAGE_CLOSING',
          data: { timestamp: Date.now() }
        });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // Nettoyage
    return () => {
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.removeEventListener('message', handleSync);
        broadcastChannelRef.current.close();
        broadcastChannelRef.current = null;
      }
      clearInterval(checkExternalWindow);
      window.removeEventListener('beforeunload', handleBeforeUnload);

      // Nettoyer le heartbeat
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
    };
  }, [externalDisplayWindow]);

  // Détecter les écrans disponibles (côté client uniquement)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const detectScreens = async () => {
      const screens = [];

      let useTauriAPI = false;
      let useElectronAPI = false;

      // Priorité 1: API Tauri (priorité maximale)
      if (isTauriApp && getTauriDisplays) {
        try {
          const tauriDisplays = await getTauriDisplays();

          if (Array.isArray(tauriDisplays) && tauriDisplays.length > 0) {
            tauriDisplays.forEach((screen: any) => {
              screens.push({
                id: screen.id,
                name: screen.name || (screen.is_primary ? t("external.screenName") : t("external.screenNumber", { number: screen.id })),
                width: screen.width,
                height: screen.height,
                left: screen.left,
                top: screen.top,
                isPrimary: screen.is_primary || false,
                scaleFactor: screen.scale_factor || 1,
                source: 'tauri'
              });
            });

            useTauriAPI = true;
          }
        } catch (error) {
          // console.error('Erreur lors de la récupération des écrans Tauri:', error);
        }
      }

      // Priorité 2: API Electron native (Windows/macOS)
      if (!useTauriAPI && isElectronApp && typeof window !== 'undefined' && (window as any).electronAPI) {
        try {
          const electronScreens = await (window as any).electronAPI.getDisplays();

          electronScreens.forEach((screen: any) => {
            screens.push({
              id: screen.id,
              name: screen.name || (screen.isPrimary ? t("external.screenName") : t("external.screenNumber", { number: screen.id })),
              width: screen.width,
              height: screen.height,
              left: screen.left,
              top: screen.top,
              isPrimary: screen.isPrimary,
              scaleFactor: screen.scaleFactor || 1,
              source: 'electron'
            });
          });

          useElectronAPI = true;

          // Écouter les changements d'écrans Electron
          if ((window as any).electronAPI.onDisplayChanged) {
            (window as any).electronAPI.onDisplayChanged(() => {
              detectScreens();
            });
          }

        } catch (error) {
        }
      }

      // Priorité 3: API Screen Details Web (si Tauri et Electron non disponibles)
      if (!useTauriAPI && !useElectronAPI && 'getScreenDetails' in window && window.isSecureContext) {
        try {
          const screenDetails = await (window as any).getScreenDetails();

          screenDetails.screens.forEach((screen: any, index: number) => {
            screens.push({
              id: `web-screen-${index}`,
              name: screen.isPrimary ? t("external.screenName") : t("external.screenNumber", { number: index + 1 }),
              width: screen.width,
              height: screen.height,
              left: screen.left,
              top: screen.top,
              isPrimary: screen.isPrimary,
              source: 'web-api'
            });
          });

          // Écouter les changements d'écrans Web
          screenDetails.addEventListener('screenschange', () => {
            detectScreens();
          });

        } catch (error) {
        }
      }

      // Fallback: Écran principal uniquement
      if (screens.length === 0) {
        screens.push({
          id: 'primary-fallback',
          name: t("external.screenName"),
          width: window.screen.width,
          height: window.screen.height,
          left: 0,
          top: 0,
          isPrimary: true,
          source: 'fallback'
        });
      }

      setAvailableScreens(screens);
      setIsScreensLoaded(true);
    };

    // Délai pour s'assurer que le composant est monté
    const timer = setTimeout(detectScreens, 100);
    return () => clearTimeout(timer);
  }, [isElectronApp, isTauriApp, getTauriDisplays, t]);




  // Configuration state - utilise maintenant le contexte global
  const { mainConfig: config, setMainConfig: setConfig, resetMainConfig } = useConfig()

  // État pour gérer le type de motif actif (personnalisé, couleur unie ou standard)
  const [activePatternType, setActivePatternType] = useState<'custom' | 'solid' | 'standard'>('custom')
  const [showGridOffset, setShowGridOffset] = useState(false)
  
  // État pour le mode Easy/Full (commence en mode Easy pour les nouveaux utilisateurs)
  const [isEasyMode, setIsEasyMode] = useState(true)
  const [isAnimatingSwitch, setIsAnimatingSwitch] = useState(false)

  // Charger l'état du mode Easy/Full depuis localStorage après le premier rendu
  useEffect(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      const saved = localStorage.getItem('isEasyMode');
      if (saved !== null) {
        setIsEasyMode(saved === 'true');
      }
    }
  }, [])

  // Sauvegarder l'état du mode Easy/Full dans localStorage quand il change
  useEffect(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem('isEasyMode', isEasyMode.toString());
    }
  }, [isEasyMode])

  // Animation automatique pour montrer les possibilités aux nouveaux utilisateurs
  useEffect(() => {
    if (typeof window === 'undefined' || !window.localStorage) return;
    
    const hasSeenAnimation = localStorage.getItem('hasSeenEasyFullAnimation');
    
    if (!hasSeenAnimation) {
      // Démarrer l'animation après 1 seconde
      const timer1 = setTimeout(() => {
        setIsAnimatingSwitch(true);
        setIsEasyMode(false); // Passer en mode Full
      }, 1000);
      
      // Revenir en mode Easy après 2 secondes
      const timer2 = setTimeout(() => {
        setIsEasyMode(true); // Revenir en mode Easy
        localStorage.setItem('hasSeenEasyFullAnimation', 'true');
      }, 3000);
      
      // Arrêter l'effet de brillance après l'animation
      const timer3 = setTimeout(() => {
        setIsAnimatingSwitch(false);
      }, 3500);
      
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
      };
    }
  }, [])

  // Forcer le mode "solid" quand isEasyMode est activé, et repasser sur "custom" quand désactivé
  const prevIsEasyModeRef = useRef(isEasyMode)
  useEffect(() => {
    const wasEasyMode = prevIsEasyModeRef.current;
    prevIsEasyModeRef.current = isEasyMode;

    if (isEasyMode && !wasEasyMode && activePatternType !== 'solid') {
      setActivePatternType('solid');
      // Forcer la couleur rouge par défaut en mode Easy
      updateConfig("backgroundColor", "#ff0000");
    } else if (!isEasyMode && wasEasyMode && activePatternType === 'solid') {
      setActivePatternType('custom');
      // Forcer le motif "Dégradé" quand on passe en mode Full
      updateConfig("patternType", "gradient");
    }
  }, [isEasyMode, activePatternType])

  // États d'animation (déclarés tôt pour éviter les erreurs de dépendance)
  const [isAnimating, setIsAnimating] = useState(false)

  // NOUVEAU : États pour les sessions partagées (après la déclaration de config et activePatternType)
  const {
    handleSessionStateChange,
    isSessionRemote,
    isSessionServer,
    isInSession,
    sendSyncMessage,
    setSendSyncMessage,
    endSession
  } = useSessionIntegration({ ...config, activePatternType })

  // NOUVEAU : Callback pour recevoir les configs de session
  const handleConfigUpdate = useCallback((receivedConfig: any) => {
    // DEBUG: Afficher la structure du message reçu
    // console.log('📥 [HANDLE_CONFIG_UPDATE] Message reçu - Type:', Object.keys(receivedConfig));
    // console.log('📥 [HANDLE_CONFIG_UPDATE] ANIMATION_COMMAND présent?', !!receivedConfig.ANIMATION_COMMAND);
    if (receivedConfig.ANIMATION_COMMAND) {
      // console.log('📥 [HANDLE_CONFIG_UPDATE] ANIMATION_COMMAND.action:', receivedConfig.ANIMATION_COMMAND.action);
    }

    // Traiter l'état d'animation reçu du host - clé directe isAnimating (quand c'est le seul message)
    if (receivedConfig.isAnimating !== undefined && Object.keys(receivedConfig).length === 1) {
      // console.log('📥 [SESSION] État d\'animation reçu du host (clé directe):', receivedConfig.isAnimating);
      setIsAnimating(receivedConfig.isAnimating);
      return;
    }

    // Traiter l'état d'animation reçu du host - format ANIMATION_STATE
    if (receivedConfig.ANIMATION_STATE) {
      // console.log('📥 [SESSION] État d\'animation reçu du host (format ANIMATION_STATE):', receivedConfig.ANIMATION_STATE.isAnimating);
      setIsAnimating(receivedConfig.ANIMATION_STATE.isAnimating);
      return;
    }

    // Traiter les mises à jour de configuration depuis le host
    if (receivedConfig.CONFIG_SYNC) {
      // console.log('📥 [SESSION] Config syncronisée reçue du host');
      receivedConfig = receivedConfig.CONFIG_SYNC;
    }

    // NOUVEAU : Traiter les fermetures de session
    if (receivedConfig.SESSION_CLOSE) {
      // console.log('🔚 [SESSION] La session a été fermée par le serveur');
      // Marquer la session comme inactive pour forcer la déconnexion
      handleSessionStateChange(false, false);

      // 👉 Rediriger vers la page de base après fermeture de session par le serveur
      setTimeout(() => {
        // Vérifier si on est sur la page remote (mobile)
        if (typeof window !== 'undefined' && window.location.pathname.includes('/remote')) {
          router.push('/grid/remote');
        } else {
          router.push('/grid');
        }
      }, 1500); // Délai pour afficher les toasts

      return;
    }

    // NOUVEAU : Traiter les commandes d'animation en priorité
    // Gère deux formats: { ANIMATION_COMMAND: { action } } et { action } (directement)
    let action: string | undefined;

    // console.log('🎬 [HANDLE_CONFIG_UPDATE] Vérification action:', {
    //  hasANIMATION_COMMAND: !!receivedConfig.ANIMATION_COMMAND,
    //  receivedConfigAction: receivedConfig.action,
    //  isString: typeof receivedConfig.action === 'string'
    // });

    if (receivedConfig.ANIMATION_COMMAND) {
      action = receivedConfig.ANIMATION_COMMAND.action;
    } else if (receivedConfig.action && typeof receivedConfig.action === 'string' && (receivedConfig.action === 'START' || receivedConfig.action === 'STOP')) {
      // Format direct depuis le mobile: { action: 'START' | 'STOP' }
      action = receivedConfig.action;
      // console.log('🎬 [HANDLE_CONFIG_UPDATE] Action extraite directement:', action);
    }

    // console.log('🎬 [HANDLE_CONFIG_UPDATE] Action finale:', { action, isAnimating });

    if (action) {
      // console.log('🎬 [HANDLE_CONFIG_UPDATE] Traitement action:', action);
      if (action === 'START') {
        // console.log('🎬 [HANDLE_CONFIG_UPDATE] Lancement animation (START reçu)');
        if (!isAnimating) {
          startTimeRef.current = Date.now();
          setIsAnimating(true);
        }

        // Synchroniser vers l'affichage externe si connecté
        if (broadcastChannelRef.current && isExternalDisplayConnected) {
          broadcastChannelRef.current.postMessage({
            type: 'ANIMATION_STATE_UPDATE',
            data: { isAnimating: true }
          });
        }
      } else if (action === 'STOP') {
        // console.log('🎬 [HANDLE_CONFIG_UPDATE] Arrêt animation (STOP reçu)');
        // ⚠️ IMPORTANT: Toujours arrêter l'animation, peu importe isAnimating
        // Car isAnimating peut ne pas être en sync avec l'état réel de l'animation
        // console.log('🎬 [HANDLE_CONFIG_UPDATE] Appel setIsAnimating(false)');
        setIsAnimating(false);
        if (animationRef.current) {
          // console.log('🎬 [HANDLE_CONFIG_UPDATE] Annulation du requestAnimationFrame');
          cancelAnimationFrame(animationRef.current);
          animationRef.current = null;
        }

        // Synchroniser vers l'affichage externe si connecté (même si déjà arrêté)
        if (broadcastChannelRef.current && isExternalDisplayConnected) {
          broadcastChannelRef.current.postMessage({
            type: 'ANIMATION_STATE_UPDATE',
            data: { isAnimating: false }
          });
        }
      }

      // Ne pas traiter le reste si c'est juste une commande d'animation
      return;
    }

    // NOUVEAU : Traiter les commandes Self-Test depuis mobile
    if (receivedConfig.SELFTEST_NAVIGATE) {
      const { direction } = receivedConfig.SELFTEST_NAVIGATE;

      // Exécuter la navigation localement (logique inline pour éviter les dépendances circulaires)
      if (selfTestMode.enabled && config.ledWallMode?.enabled) {
        const { columns, rows } = config.ledWallMode;
        const { selectedPanelX, selectedPanelY } = selfTestMode;

        let newX = selectedPanelX;
        let newY = selectedPanelY;

        switch (direction) {
          case 'up':
            newY = Math.max(1, selectedPanelY - 1);
            break;
          case 'down':
            newY = Math.min(rows, selectedPanelY + 1);
            break;
          case 'left':
            newX = Math.max(1, selectedPanelX - 1);
            break;
          case 'right':
            newX = Math.min(columns, selectedPanelX + 1);
            break;
        }


        if (newX !== selectedPanelX || newY !== selectedPanelY) {
          const panelKey = `${newX},${newY}`;

          let newActivePanels = selfTestMode.trailMode
            ? new Set(selfTestMode.activePanels)
            : new Set<string>();

          newActivePanels.add(panelKey);

          setSelfTestMode(prev => ({
            ...prev,
            selectedPanelX: newX,
            selectedPanelY: newY,
            activePanels: newActivePanels
          }));

          // Synchroniser vers l'externe
          if (broadcastChannelRef.current && isExternalDisplayConnected) {
            const serializableSelfTestMode = {
              enabled: true,
              selectedPanelX: newX,
              selectedPanelY: newY,
              trailMode: selfTestMode.trailMode,
              showBorders: selfTestMode.showBorders,
              activePanels: Array.from(newActivePanels)
            };

            broadcastChannelRef.current.postMessage({
              type: 'SELF_TEST_UPDATE',
              data: serializableSelfTestMode
            });
          } else {
          }

          // Forcer un redraw pour s'assurer que le changement est visible
          // Note: On ne peut pas utiliser drawPattern directement ici car il n'est pas encore défini
          // Le changement d'état de selfTestMode va déclencher un redraw automatiquement

        }
      }
      return;
    }

    if (receivedConfig.SELFTEST_TOGGLE_CYAN) {

      // Exécuter le toggle cyan localement
      const panelKey = `${selfTestMode.selectedPanelX},${selfTestMode.selectedPanelY}`;
      setCyanPanels(prev => {
        const newCyanPanels = new Set(prev);
        let isNowCyan = false;

        if (newCyanPanels.has(panelKey)) {
          newCyanPanels.delete(panelKey);

          // Si on retire le cyan, désactiver aussi le mode PCB
          setSubdivisionMode(false);
          setRedZones(prev => {
            const newRedZones = new Set(prev);
            // Retirer toutes les zones rouges de ce panneau
            for (const zoneKey of newRedZones) {
              if (zoneKey.startsWith(panelKey + ',')) {
                newRedZones.delete(zoneKey);
              }
            }
            return newRedZones;
          });
        } else {
          newCyanPanels.add(panelKey);
          isNowCyan = true;

          // NOUVEAU : Activer automatiquement le mode PCB quand on met un panneau en cyan
          setSubdivisionMode(true);
        }

        // Synchroniser vers l'externe
        if (broadcastChannelRef.current && isExternalDisplayConnected) {
          broadcastChannelRef.current.postMessage({
            type: 'CYAN_PANELS_UPDATE',
            data: Array.from(newCyanPanels)
          });

          // Synchroniser aussi le mode subdivision
          broadcastChannelRef.current.postMessage({
            type: 'SUBDIVISION_UPDATE',
            data: {
              subdivisionMode: isNowCyan,
              redZones: isNowCyan ? Array.from(redZones) : [],
              selectedQuadrant: selectedQuadrant
            }
          });
        }

        return newCyanPanels;
      });
      return;
    }

    if (receivedConfig.SELFTEST_TOGGLE_RED_ZONE) {

      // Vérifier que le panneau est cyan et qu'on est en mode subdivision
      const panelKey = `${selfTestMode.selectedPanelX},${selfTestMode.selectedPanelY}`;
      if (cyanPanels.has(panelKey)) {
        if (!subdivisionMode) {
          // Activer le mode subdivision automatiquement
          setSubdivisionMode(true);

          if (broadcastChannelRef.current && isExternalDisplayConnected) {
            broadcastChannelRef.current.postMessage({
              type: 'SUBDIVISION_UPDATE',
              data: {
                subdivisionMode: true,
                redZones: Array.from(redZones),
                selectedQuadrant
              }
            });
          }
        }

        // Marquer/démarquer la zone rouge
        const zoneKey = `${selfTestMode.selectedPanelX},${selfTestMode.selectedPanelY},${selectedQuadrant}`;
        setRedZones(prev => {
          const newRedZones = new Set(prev);
          if (newRedZones.has(zoneKey)) {
            newRedZones.delete(zoneKey);
          } else {
            newRedZones.add(zoneKey);
          }

          // Synchroniser vers l'externe
          if (broadcastChannelRef.current && isExternalDisplayConnected) {
            broadcastChannelRef.current.postMessage({
              type: 'SUBDIVISION_UPDATE',
              data: {
                subdivisionMode: true,
                redZones: Array.from(newRedZones),
                selectedQuadrant
              }
            });
          }

          return newRedZones;
        });
      }
      return;
    }

    if (receivedConfig.SELFTEST_NAVIGATE_QUADRANT) {
      const { quadrant } = receivedConfig.SELFTEST_NAVIGATE_QUADRANT;

      // Vérifier qu'on est en mode subdivision sur un panneau cyan
      const panelKey = `${selfTestMode.selectedPanelX},${selfTestMode.selectedPanelY}`;
      if (subdivisionMode && cyanPanels.has(panelKey)) {
        setSelectedQuadrant(quadrant);

        // Synchroniser vers l'externe
        if (broadcastChannelRef.current && isExternalDisplayConnected) {
          broadcastChannelRef.current.postMessage({
            type: 'SUBDIVISION_UPDATE',
            data: {
              subdivisionMode,
              redZones: Array.from(redZones),
              selectedQuadrant: quadrant
            }
          });
        }
      }
      return;
    }

    // NOUVEAU : Traiter l'activation/désactivation du mode Self-Test depuis mobile
    if (receivedConfig.SELFTEST_TOGGLE_MODE) {
      const { enabled } = receivedConfig.SELFTEST_TOGGLE_MODE;

      if (enabled) {
        // Activer le mode et sélectionner le panneau 1,1
        const panelKey = `1,1`;
        setSelfTestMode(prev => ({
          ...prev,
          enabled: true,
          selectedPanelX: 1,
          selectedPanelY: 1,
          showBorders: true,
          activePanels: new Set([panelKey])
        }));

        // Réinitialiser les autres états
        setCyanPanels(new Set());
        setSubdivisionMode(false);
        setRedZones(new Set());
        setSelectedQuadrant('tl');

      } else {
        // Désactiver le mode et nettoyer tous les états
        setSelfTestMode(prev => ({
          ...prev,
          enabled: false,
          showBorders: true,
          activePanels: new Set()
        }));

        setCyanPanels(new Set());
        setSubdivisionMode(false);
        setRedZones(new Set());
        setSelectedQuadrant('tl');

      }

      // La synchronisation n'est pas nécessaire ici car le mobile a déjà mis à jour son état local

      // Synchroniser vers l'externe
      if (broadcastChannelRef.current && isExternalDisplayConnected) {
        const serializableSelfTestMode = {
          enabled,
          selectedPanelX: enabled ? 1 : selfTestMode.selectedPanelX,
          selectedPanelY: enabled ? 1 : selfTestMode.selectedPanelY,
          trailMode: selfTestMode.trailMode,
          showBorders: true,
          activePanels: enabled ? ['1,1'] : []
        };

        broadcastChannelRef.current.postMessage({
          type: 'SELF_TEST_UPDATE',
          data: serializableSelfTestMode
        });

        broadcastChannelRef.current.postMessage({
          type: 'CYAN_PANELS_UPDATE',
          data: []
        });

        broadcastChannelRef.current.postMessage({
          type: 'SUBDIVISION_UPDATE',
          data: {
            subdivisionMode: false,
            redZones: [],
            selectedQuadrant: 'tl'
          }
        });
      }

      return;
    }

    // Mettre à jour la config locale
    // S'assurer que les propriétés essentielles ont des valeurs par défaut
    const mergedConfig = {
      ...receivedConfig,
      gridOffset: receivedConfig.gridOffset ?? { x: 0, y: 0 },
      animationSpeed: receivedConfig.animationSpeed ?? 1,
      sonarSpeed: receivedConfig.sonarSpeed ?? 0.2,
      patternSeed: receivedConfig.patternSeed ?? 0,
      rotationDeg: receivedConfig.rotationDeg ?? 0,
      patternType: receivedConfig.patternType ?? 'canvas',
      standardPatternType: receivedConfig.standardPatternType ?? 'none',
      backgroundColor: receivedConfig.backgroundColor ?? '#000000',
      rampDirection: receivedConfig.rampDirection ?? 'horizontal',
      width: receivedConfig.width ?? 1920,
      height: receivedConfig.height ?? 1152,
      gridSize: receivedConfig.gridSize ?? 192,
      ledWallMode: receivedConfig.ledWallMode ?? {
        enabled: false,
        columns: 10,
        rows: 6,
        panelWidth: 192,
        panelHeight: 192,
        borderWidth: 2
      },
      pixelMapMode: receivedConfig.pixelMapMode ?? config.pixelMapMode ?? DEFAULT_CONFIG.pixelMapMode
    };
    setConfig(mergedConfig);

    // IMPORTANT : Dériver activePatternType à partir de la config reçue
    let newActivePatternType: 'custom' | 'solid' | 'standard' = 'custom';

    // Si activePatternType est inclus dans la config reçue, l'utiliser
    if (receivedConfig.activePatternType) {
      newActivePatternType = receivedConfig.activePatternType;
    }
    // Sinon, le dériver de la logique métier
    else if (receivedConfig.standardPatternType && receivedConfig.standardPatternType !== 'none') {
      newActivePatternType = 'standard';
    }
    else if (receivedConfig.patternType === 'solid') {
      newActivePatternType = 'solid';
    }
    else {
      newActivePatternType = 'custom';
    }


    setActivePatternType(newActivePatternType);

    // NOUVEAU : Synchroniser les états Self-Test reçus via les sessions
    if (receivedConfig.selfTestMode) {
      setSelfTestMode({
        ...receivedConfig.selfTestMode,
        activePanels: new Set(receivedConfig.selfTestMode.activePanels || []) // Reconvertir Array en Set
      });
    }

    if (receivedConfig.cyanPanels) {
      setCyanPanels(new Set(receivedConfig.cyanPanels));
    }

    if (typeof receivedConfig.subdivisionMode === 'boolean') {
      setSubdivisionMode(receivedConfig.subdivisionMode);
    }

    if (receivedConfig.selectedQuadrant) {
      setSelectedQuadrant(receivedConfig.selectedQuadrant);
    }

    if (receivedConfig.redZones) {
      setRedZones(new Set(receivedConfig.redZones));
    }
  }, [setConfig, activePatternType, isAnimating, isExternalDisplayConnected, setSelfTestMode, setCyanPanels, setSubdivisionMode, setSelectedQuadrant, setRedZones, subdivisionMode, cyanPanels, redZones, selectedQuadrant, selfTestMode, broadcastChannelRef]);

  // Synchroniser les valeurs d'affichage avec la configuration
  useEffect(() => {
    if (config.ledWallMode && typeof config.ledWallMode.columns !== 'undefined' && typeof config.ledWallMode.rows !== 'undefined') {
      setColumnsDisplayValue(config.ledWallMode.columns.toString())
      setRowsDisplayValue(config.ledWallMode.rows.toString())
    }
  }, [config.ledWallMode?.columns, config.ledWallMode?.rows])

  // Vérification initiale de la capacité de rendu
  useEffect(() => {
    if (mounted) {
      checkRenderability(config)
    }
  }, [mounted, config])

  // Debug: tracer tous les changements de activePatternType
  useEffect(() => {
  }, [activePatternType, config.patternType, config.standardPatternType]);

  // Debug: tracer spécifiquement les changements de standardPatternType
  useEffect(() => {
  }, [config.standardPatternType, activePatternType]);

  // NOUVEAU : Callback pour recevoir l'état d'animation via session
  const handleAnimationStateUpdate = useCallback((isAnimating: boolean) => {
    setIsAnimating(isAnimating);
  }, []);

  // 📤 Envoyer l'état d'animation du host aux remotes connectées
  useEffect(() => {
    if (isInSession && isSessionServer && sendSyncMessage) {
      // console.log('📤 [ANIMATION_STATE] Conditions rencontrées:', {
      //   isInSession,
      //   isSessionServer,
      //   hasSendSyncMessage: !!sendSyncMessage,
      //   isAnimating
      // });
      //// console.log('📤 [ANIMATION_STATE] Envoi de l\'état d\'animation aux remotes:', isAnimating);
      sendSyncMessage('ANIMATION_STATE', { isAnimating });
    } else {
      // console.log('⚠️ [ANIMATION_STATE] Conditions NOT met:', {
      //   isInSession,
      //   isSessionServer,
      //   hasSendSyncMessage: !!sendSyncMessage
      // });
    }
  }, [isAnimating, isInSession, isSessionServer, sendSyncMessage]);

  const [isFullscreen, setIsFullscreen] = useState(false)
  const [textElements, setTextElements] = useState<TextElement[]>([])
  const [selectedTextId, setSelectedTextId] = useState<string | null>(null)
  const [showTextPanel, setShowTextPanel] = useState(false)
  const [logoElements, setLogoElements] = useState<LogoElement[]>([])
  const [selectedLogoId, setSelectedLogoId] = useState<string | null>(null)
  const [showLogoPanel, setShowLogoPanel] = useState(false)
  const [canvasScale, setCanvasScale] = useState({ scaleX: 1, scaleY: 1 })

  // Snake game state - Déclaré tôt pour éviter les erreurs de référence
  const [snakeGameState, setSnakeGameState] = useState<SnakeGameState | null>(null)
  const snakeLeaderboardRef = useRef<SnakeLeaderboardHandle>(null)

  // Fonction pour synchroniser tous les états vers l'affichage externe
  const syncAllToExternal = useCallback(() => {
    if (!broadcastChannelRef.current) {
      return;
    }


    // Synchroniser la configuration (activePatternType géré séparément)
    broadcastChannelRef.current.postMessage({
      type: 'CONFIG_UPDATE',
      data: config
    });

    // Message séparé pour s'assurer que activePatternType est bien synchronisé
    broadcastChannelRef.current.postMessage({
      type: 'ACTIVE_PATTERN_TYPE_UPDATE',
      data: { activePatternType }
    });

    // Synchroniser les éléments de texte
    broadcastChannelRef.current.postMessage({
      type: 'TEXT_ELEMENTS_UPDATE',
      data: textElements
    });

    // Synchroniser les éléments de logo
    // Convertir les images en data URLs pour la sérialisation
    const serializableLogoElements = logoElements.map(logo => {
      const imageData = logo.isGif 
        ? logo.imageSrc 
        : (logo.image.complete && logo.image.naturalWidth > 0
          ? logo.image.src
          : logo.image.src);

      return {
        ...logo,
        image: imageData, // Convertir HTMLImageElement en string
        imageWidth: logo.image.width || logo.image.naturalWidth,
        imageHeight: logo.image.height || logo.image.naturalHeight,
        isGif: logo.isGif,
        imageSrc: logo.imageSrc
      };
    });

    broadcastChannelRef.current.postMessage({
      type: 'LOGO_ELEMENTS_UPDATE',
      data: serializableLogoElements
    });

    // Synchroniser l'état d'animation
    broadcastChannelRef.current.postMessage({
      type: 'ANIMATION_STATE_UPDATE',
      data: { isAnimating }
    });

    // Synchroniser le module Self-Test
    const serializableSelfTestMode = {
      ...selfTestMode,
      activePanels: Array.from(selfTestMode.activePanels) // Convertir Set en Array
    };

    broadcastChannelRef.current.postMessage({
      type: 'SELF_TEST_UPDATE',
      data: serializableSelfTestMode
    });

    // Synchroniser les panneaux cyan
    broadcastChannelRef.current.postMessage({
      type: 'CYAN_PANELS_UPDATE',
      data: {
        cyanPanels: Array.from(cyanPanels)
      }
    });

    // Synchroniser le mode subdivision
    broadcastChannelRef.current.postMessage({
      type: 'SUBDIVISION_UPDATE',
      data: {
        subdivisionMode,
        redZones: Array.from(redZones),
        selectedQuadrant
      }
    });

    // Synchroniser le paramètre de désactivation de l'aperçu
    broadcastChannelRef.current.postMessage({
      type: 'PREVIEW_DISABLE_UPDATE',
      data: { disablePreviewWhenExternal }
    });

    // Synchroniser l'état du jeu Snake (toujours, pour maintenir la cohérence)
    if (snakeGameState) {

      // Convertir le Set visitedCells en Array pour la sérialisation
      const serializableSnakeState = {
        ...snakeGameState,
        visitedCells: Array.from(snakeGameState.visitedCells)
      };

      broadcastChannelRef.current.postMessage({
        type: 'SNAKE_STATE_UPDATE',
        data: serializableSnakeState
      });
    }
  }, [config, textElements, logoElements, isAnimating, activePatternType, selfTestMode, cyanPanels, subdivisionMode, redZones, selectedQuadrant, disablePreviewWhenExternal, snakeGameState]);

  // Fonction pour ouvrir l'affichage externe (hybride Tauri/Electron/Web)
  const openExternalDisplay = useCallback(async () => {
    try {
      const selectedScreen = availableScreens[selectedScreenIndex] || availableScreens[0];

      if (!selectedScreen) {
        toast.error(t("external.noScreen"));
        return;
      }

      let windowRef = null;

      // Méthode 1: Tauri natif - Priorité maximale
      if (isTauriApp && openTauriExternalDisplay) {
        try {
          // Vérifier s'il y a plusieurs écrans avant d'ouvrir en plein écran
          const tauriScreens = availableScreens.filter(s => s.source === 'tauri');
          const hasMultipleDisplays = tauriScreens.length > 1 || availableScreens.length > 1;
          
          if (!hasMultipleDisplays) {
            toast.error(t("external.singleDisplay"));
            return;
          }

          // Utiliser l'ID de l'écran sélectionné, ou le premier écran disponible si l'ID n'est pas valide
          let displayId = selectedScreen.id;
          
          // Si l'ID n'est pas au format Tauri (display_X), essayer de le convertir ou utiliser le premier écran
          if (!displayId || !displayId.startsWith('display_')) {
            // Chercher un écran Tauri dans la liste
            const tauriScreen = availableScreens.find(s => s.source === 'tauri');
            if (tauriScreen) {
              displayId = tauriScreen.id;
            } else if (availableScreens.length > 0) {
              // Utiliser le premier écran disponible
              displayId = availableScreens[0].id;
            } else {
              // Si aucun écran n'est détecté, utiliser l'écran principal par défaut (display_0)
              displayId = 'display_0';
            }
          }

          const result = await openTauriExternalDisplay(
            displayId,
            '/grid/?forceExternal=true',
            true // fullscreen
          );

          if (result.success && result.windowId) {
            windowRef = {
              id: result.windowId,
              isTauri: true,
              close: async () => {
                if (closeTauriExternalDisplay) {
                  await closeTauriExternalDisplay(result.windowId);
                }
              }
            };

            toast.success(t("external.electronSuccess")
              .replace("{screenName}", selectedScreen.name)
              .replace("{width}", selectedScreen.width.toString())
              .replace("{height}", selectedScreen.height.toString()));
          } else if (result.error) {
            // console.error('Erreur Tauri:', result.error);
            // Vérifier si l'erreur concerne le nombre d'écrans
            if (result.error.includes("Only one display detected") || result.error.includes("multiple displays")) {
              toast.error(t("external.singleDisplay"));
            } else {
              // Afficher l'erreur seulement si les autres méthodes ne sont pas disponibles
              if (!isElectronApp) {
                // Si l'erreur est déjà traduite ou est un message technique, l'afficher tel quel
                // Sinon utiliser le message générique traduit
                const errorMessage = result.error || t("external.popupBlocked");
                toast.error(errorMessage);
              }
            }
          }
        } catch (tauriError) {
          // console.error('Erreur lors de l\'ouverture Tauri:', tauriError);
          const errorMessage = tauriError instanceof Error ? tauriError.message : String(tauriError);
          // Afficher l'erreur seulement si les autres méthodes ne sont pas disponibles
          if (!isElectronApp) {
            toast.error(errorMessage || t("external.popupBlocked"));
          }
        }
      }

      // Méthode 2: Electron natif (Windows/macOS)
      if (!windowRef && isElectronApp && selectedScreen.source === 'electron' && (window as any).electronAPI) {
        try {

          const electronWindowId = await (window as any).electronAPI.openExternalDisplay({
            displayId: selectedScreen.id,
            url: '/grid/?forceExternal=true', // MODIFIÉ: Utiliser la même page avec paramètre
            options: {
              width: selectedScreen.width,
              height: selectedScreen.height,
              x: selectedScreen.left,
              y: selectedScreen.top,
              fullscreen: true,
              frame: false,
              show: true,
              webPreferences: {
                nodeIntegration: true,
                contextIsolation: false
              }
            }
          });

          if (electronWindowId) {
            windowRef = {
              id: electronWindowId,
              isElectron: true,
              close: () => (window as any).electronAPI.closeExternalDisplay(electronWindowId)
            };

            toast.success(t("external.electronSuccess")
              .replace("{screenName}", selectedScreen.name)
              .replace("{width}", selectedScreen.width.toString())
              .replace("{height}", selectedScreen.height.toString()));
          }

        } catch (electronError) {
        }
      }

      // Méthode 3: Fallback Web (si Tauri et Electron échouent ou ne sont pas disponibles)
      if (!windowRef) {

        const windowFeatures = [
          `width=${selectedScreen.width}`,
          `height=${selectedScreen.height}`,
          `left=${selectedScreen.left}`,
          `top=${selectedScreen.top}`,
          'menubar=no',
          'toolbar=no',
          'location=no',
          'status=no',
          'scrollbars=no',
          'resizable=yes'
        ].join(',');

        const webWindow = window.open('/grid/?forceExternal=true', 'visualGridExternalDisplay', windowFeatures); // MODIFIÉ: Utiliser la même page

        if (webWindow) {
          windowRef = webWindow;

          toast.success(t("toast.externalDisplayWeb", { name: selectedScreen.name, width: selectedScreen.width, height: selectedScreen.height }));
        }
      }

      // Configuration finale si une fenêtre a été ouverte
      if (windowRef) {
        setExternalDisplayWindow(windowRef);

        // Synchronisation initiale avec délai
        setTimeout(() => {
          syncAllToExternal();
        }, (windowRef && 'isElectron' in windowRef && windowRef.isElectron) ? 1500 : 2000);

      } else {
        toast.error(t("external.popupBlocked"));
      }

    } catch (error) {
      toast.error(t("external.generalError"));
    }
  }, [syncAllToExternal, availableScreens, selectedScreenIndex, isElectronApp, isTauriApp, openTauriExternalDisplay, closeTauriExternalDisplay, t]);

  // Fonction pour fermer l'affichage externe
  const closeExternalDisplay = useCallback(async () => {
    if (externalDisplayWindow) {
      // Si c'est une fenêtre Tauri, utiliser la méthode close spécifique
      if ('isTauri' in externalDisplayWindow && externalDisplayWindow.isTauri && closeTauriExternalDisplay) {
        try {
          await closeTauriExternalDisplay(externalDisplayWindow.id);
        } catch (error) {
          // console.error('Erreur lors de la fermeture Tauri:', error);
        }
      } else if ('isElectron' in externalDisplayWindow && externalDisplayWindow.isElectron && externalDisplayWindow.close) {
        // Si c'est Electron, utiliser la méthode close
        externalDisplayWindow.close();
      } else if (typeof externalDisplayWindow.close === 'function') {
        // Si c'est une fenêtre web standard
        externalDisplayWindow.close();
      } else if (externalDisplayWindow.close) {
        // Appel direct de la fonction close
        externalDisplayWindow.close();
      }
      
      setExternalDisplayWindow(null);
      setIsExternalDisplayConnected(false);
      setIsExternalDisplayFullscreen(false);
    }
  }, [externalDisplayWindow, closeTauriExternalDisplay]);

  // Auto-synchronisation des changements de config (SANS activePatternType)
  useEffect(() => {
    // Ne pas envoyer si on EST la page externe (pour éviter les boucles)
    if (broadcastChannelRef.current && isExternalDisplayConnected && !isExternalDisplay) {
      broadcastChannelRef.current.postMessage({
        type: 'CONFIG_UPDATE',
        data: config
      });
    }
  }, [config, isExternalDisplayConnected, isExternalDisplay]);

  // Auto-synchronisation des changements de texte
  useEffect(() => {
    if (broadcastChannelRef.current && isExternalDisplayConnected) {
      broadcastChannelRef.current.postMessage({
        type: 'TEXT_ELEMENTS_UPDATE',
        data: textElements
      });
    }
  }, [textElements, isExternalDisplayConnected]);

  // Auto-synchronisation des changements de logo
  useEffect(() => {
    if (broadcastChannelRef.current && isExternalDisplayConnected) {
      // Convertir les images en data URLs pour la sérialisation
      const serializableElements = logoElements.map(logo => {
        const imageData = logo.isGif 
          ? logo.imageSrc 
          : (logo.image.complete && logo.image.naturalWidth > 0
            ? logo.image.src
            : logo.image.src);

        return {
          ...logo,
          image: imageData, // Convertir HTMLImageElement en string
          imageWidth: logo.image.width || logo.image.naturalWidth,
          imageHeight: logo.image.height || logo.image.naturalHeight,
          isGif: logo.isGif,
          imageSrc: logo.imageSrc
        };
      });

      broadcastChannelRef.current.postMessage({
        type: 'LOGO_ELEMENTS_UPDATE',
        data: serializableElements
      });
    }
  }, [logoElements, isExternalDisplayConnected]);

  // Auto-synchronisation des changements d'animation
  useEffect(() => {
    if (broadcastChannelRef.current && isExternalDisplayConnected) {
      broadcastChannelRef.current.postMessage({
        type: 'ANIMATION_STATE_UPDATE',
        data: { isAnimating }
      });
    }
  }, [isAnimating, isExternalDisplayConnected]);

  // Auto-synchronisation du module Self-Test
  useEffect(() => {
    if (broadcastChannelRef.current && isExternalDisplayConnected && !isReceivingExternalUpdate.current) {

      // Sérialiser le Set pour BroadcastChannel
      const serializableSelfTestMode = {
        ...selfTestMode,
        activePanels: Array.from(selfTestMode.activePanels) // Convertir Set en Array
      };

      broadcastChannelRef.current.postMessage({
        type: 'SELF_TEST_UPDATE',
        data: serializableSelfTestMode
      });
    }
  }, [selfTestMode, isExternalDisplayConnected]);

  // Auto-synchronisation des panneaux cyan
  useEffect(() => {
    if (broadcastChannelRef.current && isExternalDisplayConnected && !isReceivingExternalUpdate.current) {
      broadcastChannelRef.current.postMessage({
        type: 'CYAN_PANELS_UPDATE',
        data: {
          cyanPanels: Array.from(cyanPanels)
        }
      });
    }
  }, [cyanPanels, isExternalDisplayConnected]);

  // Auto-synchronisation du mode subdivision et zones rouges
  useEffect(() => {
    if (broadcastChannelRef.current && isExternalDisplayConnected && !isReceivingExternalUpdate.current) {
      broadcastChannelRef.current.postMessage({
        type: 'SUBDIVISION_UPDATE',
        data: {
          subdivisionMode,
          redZones: Array.from(redZones),
          selectedQuadrant
        }
      });
    }
  }, [subdivisionMode, redZones, selectedQuadrant, isExternalDisplayConnected]);

  // Auto-synchronisation du paramètre de désactivation de l'aperçu
  useEffect(() => {
    if (broadcastChannelRef.current && isExternalDisplayConnected) {
      broadcastChannelRef.current.postMessage({
        type: 'PREVIEW_DISABLE_UPDATE',
        data: { disablePreviewWhenExternal }
      });
    }
  }, [disablePreviewWhenExternal, isExternalDisplayConnected]);

  // Auto-synchronisation de l'état Snake en temps réel
  useEffect(() => {
    if (broadcastChannelRef.current && isExternalDisplayConnected && snakeGameState && config.patternType === 'snake') {

      // Convertir le Set visitedCells en Array pour la sérialisation
      const serializableSnakeState = {
        ...snakeGameState,
        visitedCells: Array.from(snakeGameState.visitedCells)
      };

      // Synchronisation immédiate pour maintenir la même partie sur les deux écrans
      broadcastChannelRef.current.postMessage({
        type: 'SNAKE_STATE_UPDATE',
        data: serializableSnakeState
      });
    }
  }, [snakeGameState, isExternalDisplayConnected, config.patternType]);

  // Auto-synchronisation de activePatternType
  useEffect(() => {
    if (broadcastChannelRef.current && isExternalDisplayConnected) {

      broadcastChannelRef.current.postMessage({
        type: 'ACTIVE_PATTERN_TYPE_UPDATE',
        data: { activePatternType }
      });
    }
  }, [activePatternType, isExternalDisplayConnected]);

  // Système de heartbeat pour détecter la fermeture de la page principale
  useEffect(() => {
    if (isExternalDisplayConnected && broadcastChannelRef.current) {

      // Fréquence de heartbeat adaptée selon l'environnement
      const heartbeatInterval = isElectronApp ? 2000 : 3000; // Plus fréquent pour Electron

      const sendHeartbeat = () => {
        if (broadcastChannelRef.current && isExternalDisplayConnected) {
          broadcastChannelRef.current.postMessage({
            type: 'HEARTBEAT',
            data: { timestamp: Date.now(), isElectron: isElectronApp }
          });
        }
      };

      // Premier heartbeat immédiat
      sendHeartbeat();

      // Puis selon l'intervalle configuré
      heartbeatIntervalRef.current = setInterval(sendHeartbeat, heartbeatInterval);

      return () => {
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
          heartbeatIntervalRef.current = null;
        }
      };
    } else {
      // Nettoyer le heartbeat si pas connecté
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
    }
  }, [isExternalDisplayConnected]);

  // Note: Synchronisation Snake désactivée - chaque écran a son propre jeu indépendant

  // Drag & drop state
  const [isDragging, setIsDragging] = useState(false)
  const [cursorStyle, setCursorStyle] = useState<'default' | 'move' | 'pointer'>('default')
  const dragStateRef = useRef<{
    type: 'logo' | 'text'
    id: string
    startMouseX: number
    startMouseY: number
    startX: number
    startY: number
    hasStartedDragging: boolean
  } | null>(null)
  const justFinishedDraggingRef = useRef(false)

  // États pour la gestion des configurations
  const [configName, setConfigName] = useState<string>("")
  const [configDisplayName, setConfigDisplayName] = useState<string>("")
  const [makeDefaultConfig, setMakeDefaultConfig] = useState<boolean>(false)
  const [showSaveDialog, setShowSaveDialog] = useState<boolean>(false)
  const [showLoadDialog, setShowLoadDialog] = useState<boolean>(false)
  const [selectedPreset, setSelectedPreset] = useState<string>("HD")



  // Predefined resolutions
  const presetResolutions = [
    { name: "HD", width: 1920, height: 1080 },
    { name: "4K", width: 3840, height: 2160 },
    { name: "8K", width: 7680, height: 4320 },
    { name: "Custom", width: config.width, height: config.height },
  ]

  // Pattern types
  const patternTypes = [
    { value: "gradient", label: "Dégradé" },
    { value: "gradient4", label: "Dégradé 4 Couleurs" },
    { value: "solid", label: "Couleur Unie" },
    { value: "checkerboard", label: "Damier" },
    { value: "rainbow", label: "Arc-en-ciel" },
    { value: "colorBars", label: "Barres Colorées" },
    { value: "pulse", label: "Pulsation" },
    { value: "spiral", label: "Spirale" },
    { value: "orangeTeal", label: "Orange & Teal" },
    { value: "magentaSummer", label: "Magenta Summer" },
    { value: "summer", label: "Summer" },
    { value: "bluePurple", label: "Blue Purple" },
    { value: "snake", label: "🐍 Snake Game" },
  ]

  // Extended font families with web-safe and Google Fonts
  const fontFamilies = [
    // Web-safe fonts
    { name: "Arial", value: "Arial, sans-serif" },
    { name: "Helvetica", value: "Helvetica, Arial, sans-serif" },
    { name: "Times New Roman", value: "'Times New Roman', Times, serif" },
    { name: "Courier New", value: "'Courier New', Courier, monospace" },
    { name: "Verdana", value: "Verdana, Geneva, sans-serif" },
    { name: "Georgia", value: "Georgia, 'Times New Roman', serif" },
    { name: "Palatino", value: "'Palatino Linotype', 'Book Antiqua', Palatino, serif" },
    { name: "Tahoma", value: "Tahoma, Geneva, sans-serif" },
    { name: "Trebuchet MS", value: "'Trebuchet MS', Helvetica, sans-serif" },
    { name: "Lucida Console", value: "'Lucida Console', Monaco, monospace" },
    { name: "Impact", value: "Impact, Charcoal, sans-serif" },
    { name: "Comic Sans MS", value: "'Comic Sans MS', cursive" },
    // Google Fonts (commonly available)
    { name: "Roboto", value: "'Roboto', sans-serif" },
    { name: "Open Sans", value: "'Open Sans', sans-serif" },
    { name: "Lato", value: "'Lato', sans-serif" },
    { name: "Montserrat", value: "'Montserrat', sans-serif" },
    { name: "Oswald", value: "'Oswald', sans-serif" },
    { name: "Source Sans Pro", value: "'Source Sans Pro', sans-serif" },
    { name: "Raleway", value: "'Raleway', sans-serif" },
    { name: "PT Sans", value: "'PT Sans', sans-serif" },
    { name: "Ubuntu", value: "'Ubuntu', sans-serif" },
    { name: "Nunito", value: "'Nunito', sans-serif" },
  ]

  // Font styles
  const fontStyles = [
    { name: "Normal", value: "normal" },
    { name: "Italique", value: "italic" },
    { name: "Oblique", value: "oblique" },
  ]

  // Text alignment options
  const textAlignments = [
    { name: "Gauche", value: "left", icon: AlignLeft },
    { name: "Centre", value: "center", icon: AlignCenter },
    { name: "Droite", value: "right", icon: AlignRight },
  ]

  // Generate dynamic gradient colors based on time
  const generateGradientColors = useCallback((time: number) => {
    // Utiliser patternSeed pour décaler le point de départ de l'animation
    const seed = config.patternSeed * 100; // Multiplier pour avoir un effet plus visible

    // Fréquences harmoniques pour une vraie boucle en 10 secondes
    // Fréquence de base: 2π/10000 ≈ 0.0006283 (période = 10s)
    // Multiples entiers pour synchronisation parfaite à 10s
    const baseFreq = 0.0006283; // Boucle complète en 10 secondes

    // Couche 1: fréquence 1x (période 10s)
    const r1 = Math.sin(time * baseFreq + seed) * 127 + 128
    const g1 = Math.sin(time * baseFreq + seed + 2) * 127 + 128
    const b1 = Math.sin(time * baseFreq + seed + 4) * 127 + 128

    // Couche 2: fréquence 2x (période 5s)
    const r2 = Math.sin(time * baseFreq * 2 + seed + 1) * 127 + 128
    const g2 = Math.sin(time * baseFreq * 2 + seed + 3) * 127 + 128
    const b2 = Math.sin(time * baseFreq * 2 + seed + 5) * 127 + 128

    // Couche 3: fréquence 3x (période 3.33s)
    const r3 = Math.sin(time * baseFreq * 3 + seed + 2) * 127 + 128
    const g3 = Math.sin(time * baseFreq * 3 + seed + 4) * 127 + 128
    const b3 = Math.sin(time * baseFreq * 3 + seed + 6) * 127 + 128

    // Couche 4: fréquence 4x (période 2.5s)
    const r4 = Math.sin(time * baseFreq * 4 + seed + 3) * 127 + 128
    const g4 = Math.sin(time * baseFreq * 4 + seed + 5) * 127 + 128
    const b4 = Math.sin(time * baseFreq * 4 + seed + 1) * 127 + 128

    return [
      `rgb(${Math.floor(r1)}, ${Math.floor(g1)}, ${Math.floor(b1)})`,
      `rgb(${Math.floor(r2)}, ${Math.floor(g2)}, ${Math.floor(b2)})`,
      `rgb(${Math.floor(r3)}, ${Math.floor(g3)}, ${Math.floor(b3)})`,
      `rgb(${Math.floor(r4)}, ${Math.floor(g4)}, ${Math.floor(b4)})`,
    ]
  }, [config.patternSeed])

  // Logo management
  const handleLogoUpload = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      // Vérifier que les dimensions sont des nombres valides
      const width = typeof config.width === 'number' && isFinite(config.width) ? config.width : 1920
      const height = typeof config.height === 'number' && isFinite(config.height) ? config.height : 1080

      const file = event.target.files?.[0]
      if (!file) return

      // Détecter si c'est un GIF
      const isGif = file.type === 'image/gif' || file.name.toLowerCase().endsWith('.gif')

      const reader = new FileReader()
      reader.onload = (e) => {
        const dataUrl = e.target?.result as string
        
        if (isGif) {
          // Pour les GIF, créer un élément image mais ne pas attendre le chargement
          const img = new Image()
          img.src = dataUrl
          
          // Créer le logo avec les propriétés GIF
          const newLogo: LogoElement = {
            id: Date.now().toString(),
            image: img,
            x: width / 2,
            y: height / 2,
            width: 200, // Taille par défaut, sera ajustée après chargement
            height: 200,
            originalWidth: 200,
            originalHeight: 200,
            opacity: 1,
            isGif: true,
            imageSrc: dataUrl,
          }
          
          // Attendre que l'image soit chargée pour obtenir les dimensions réelles
          img.onload = () => {
            setLogoElements((prev) => {
              // Calculer les dimensions en préservant le ratio d'aspect
              const maxWidth = width / 4;
              const maxHeight = height / 4;
              const aspectRatio = img.width / img.height;
              
              let logoWidth = Math.min(img.width, maxWidth);
              let logoHeight = logoWidth / aspectRatio;
              
              // Si la hauteur dépasse la limite, ajuster à partir de la hauteur
              if (logoHeight > maxHeight) {
                logoHeight = maxHeight;
                logoWidth = logoHeight * aspectRatio;
              }
              
              const updatedLogo = {
                ...newLogo,
                width: logoWidth,
                height: logoHeight,
                originalWidth: img.width,
                originalHeight: img.height,
              }
              const newElements = prev.map(logo => 
                logo.id === newLogo.id ? updatedLogo : logo
              )

              // Synchroniser vers l'affichage externe
              if (broadcastChannelRef.current) {
                const serializableElements = newElements.map(logo => ({
                  ...logo,
                  image: logo.isGif ? logo.imageSrc : logo.image.src,
                  imageWidth: logo.image.width || logo.image.naturalWidth,
                  imageHeight: logo.image.height || logo.image.naturalHeight
                }));

                broadcastChannelRef.current.postMessage({
                  type: 'LOGO_ELEMENTS_UPDATE',
                  data: serializableElements
                });
              }

              return newElements;
            });
          }
          
          setLogoElements((prev) => {
            const newElements = [...prev, newLogo];

            // Synchroniser vers l'affichage externe - synchronisation instantanée
            if (broadcastChannelRef.current) {
              const serializableElements = newElements.map(logo => ({
                ...logo,
                image: logo.isGif ? logo.imageSrc : logo.image.src,
                imageWidth: logo.image.width || logo.image.naturalWidth,
                imageHeight: logo.image.height || logo.image.naturalHeight
              }));

              broadcastChannelRef.current.postMessage({
                type: 'LOGO_ELEMENTS_UPDATE',
                data: serializableElements
              });
            }

            return newElements;
          });
          setSelectedLogoId(newLogo.id)
          setShowLogoPanel(true)
        } else {
          // Pour les images normales, comportement existant
          const img = new Image()
          img.onload = () => {
            // Calculer les dimensions en préservant le ratio d'aspect
            const maxWidth = width / 4;
            const maxHeight = height / 4;
            const aspectRatio = img.width / img.height;
            
            let logoWidth = Math.min(img.width, maxWidth);
            let logoHeight = logoWidth / aspectRatio;
            
            // Si la hauteur dépasse la limite, ajuster à partir de la hauteur
            if (logoHeight > maxHeight) {
              logoHeight = maxHeight;
              logoWidth = logoHeight * aspectRatio;
            }
            
            const newLogo: LogoElement = {
              id: Date.now().toString(),
              image: img,
              x: width / 2,
              y: height / 2,
              width: logoWidth,
              height: logoHeight,
              originalWidth: img.width,
              originalHeight: img.height,
              opacity: 1,
            }
            setLogoElements((prev) => {
              const newElements = [...prev, newLogo];

              // Synchroniser vers l'affichage externe - synchronisation instantanée
              if (broadcastChannelRef.current) {
                // Convertir les images en data URLs pour la sérialisation
                const serializableElements = newElements.map(logo => ({
                  ...logo,
                  image: logo.isGif ? logo.imageSrc : logo.image.src, // Convertir HTMLImageElement en string
                  imageWidth: logo.image.width || logo.image.naturalWidth,
                  imageHeight: logo.image.height || logo.image.naturalHeight
                }));

                broadcastChannelRef.current.postMessage({
                  type: 'LOGO_ELEMENTS_UPDATE',
                  data: serializableElements
                });
              }

              return newElements;
            });
            setSelectedLogoId(newLogo.id)
            setShowLogoPanel(true)
          }
          img.src = dataUrl
        }
      }
      reader.readAsDataURL(file)
    },
    [config.width, config.height, isExternalDisplayConnected],
  )

  const updateLogoElement = useCallback((id: string, updates: Partial<LogoElement>) => {
    setLogoElements((prev) => {
      const newElements = prev.map((logo) => (logo.id === id ? { ...logo, ...updates } : logo));

      // Synchroniser vers l'affichage externe - synchronisation instantanée
      if (broadcastChannelRef.current) {
        const serializableElements = newElements.map(logo => ({
          ...logo,
          image: logo.image.src, // Convertir HTMLImageElement en string
          imageWidth: logo.image.width || logo.image.naturalWidth,
          imageHeight: logo.image.height || logo.image.naturalHeight
        }));

        broadcastChannelRef.current.postMessage({
          type: 'LOGO_ELEMENTS_UPDATE',
          data: serializableElements
        });
      }

      return newElements;
    });
  }, [])

  const deleteLogoElement = useCallback(
    (id: string) => {
      setLogoElements((prev) => {
        const newElements = prev.filter((logo) => logo.id !== id);

        // Synchroniser vers l'affichage externe - synchronisation instantanée
        if (broadcastChannelRef.current) {
          const serializableElements = newElements.map(logo => ({
            ...logo,
            image: logo.image.src, // Convertir HTMLImageElement en string
            imageWidth: logo.image.width || logo.image.naturalWidth,
            imageHeight: logo.image.height || logo.image.naturalHeight
          }));

          broadcastChannelRef.current.postMessage({
            type: 'LOGO_ELEMENTS_UPDATE',
            data: serializableElements
          });
        }

        return newElements;
      });

      if (selectedLogoId === id) {
        setSelectedLogoId(null)
      }
    },
    [selectedLogoId, isExternalDisplayConnected],
  )

  const alignLogo = useCallback(
    (alignment: string) => {
      if (!selectedLogoId) return

      const selectedLogo = logoElements.find((logo) => logo.id === selectedLogoId)
      if (!selectedLogo) return

      // Vérifier que les dimensions sont des nombres valides
      const width = typeof config.width === 'number' && isFinite(config.width) ? config.width : 1920

      const updates: Partial<LogoElement> = {}

      switch (alignment) {
        case "center":
          updates.x = width / 2 - selectedLogo.width / 2
          break
        case "left":
          updates.x = 50
          break
        case "right":
          updates.x = width - selectedLogo.width - 50
          break
      }

      updateLogoElement(selectedLogoId, updates)
    },
    [selectedLogoId, logoElements, config.width, updateLogoElement],
  )

  const alignLogoVertical = useCallback(
    (position: "top" | "middle" | "bottom") => {
      if (!selectedLogoId) return

      const selectedLogo = logoElements.find((logo) => logo.id === selectedLogoId)
      if (!selectedLogo) return

      // Vérifier que les dimensions sont des nombres valides
      const height = typeof config.height === 'number' && isFinite(config.height) ? config.height : 1080

      let y: number
      switch (position) {
        case "top":
          y = 50
          break
        case "middle":
          y = height / 2 - selectedLogo.height / 2
          break
        case "bottom":
          y = height - selectedLogo.height - 50
          break
      }

      updateLogoElement(selectedLogoId, { y })
    },
    [selectedLogoId, logoElements, config.height, updateLogoElement],
  )

  // Cache pour les cartes de distance (pulse pattern fallback)
  const [distanceMapCache, setDistanceMapCache] = useState<{
    width: number;
    height: number;
    map: Float32Array;
  } | null>(null);

  // Draw the test pattern
  const drawPattern = useCallback(
    (options?: { forExport?: boolean; exportTime?: number } | number, showSelection = true) => {
      let time: number | undefined;
      let isExporting = false;

      if (typeof options === 'number') {
        time = options;
      } else if (options && typeof options === 'object') {
        time = options.exportTime;
        isExporting = options.forExport === true;
      }

      if (disablePreviewWhenExternal && isExternalDisplayConnected) {
        return
      }

      const canvas = canvasRef.current
      if (!canvas) return

      const ctx = canvas.getContext("2d")
      if (!ctx) return

      const width = typeof config.width === 'number' && isFinite(config.width) ? config.width : 1920
      const height = typeof config.height === 'number' && isFinite(config.height) ? config.height : 1080
      const gridSize = typeof config.gridSize === 'number' && isFinite(config.gridSize) ? config.gridSize : 192

      const normalizeRotationDeg = (deg: number): 0 | 90 | 180 | -90 => {
        const d = ((Math.round(deg / 90) * 90) % 360 + 360) % 360
        if (d === 90) return 90
        if (d === 180) return 180
        if (d === 270) return -90
        return 0
      }

      const rotationDeg = normalizeRotationDeg(config.rotationDeg ?? 0)
      const needsSwap = rotationDeg === 90 || rotationDeg === -90
      const canvasWidth = needsSwap ? height : width
      const canvasHeight = needsSwap ? width : height

      canvas.width = canvasWidth
      canvas.height = canvasHeight

      ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.clearRect(0, 0, canvasWidth, canvasHeight)

      if (rotationDeg !== 0) {
        const rad = (rotationDeg * Math.PI) / 180
        if (rotationDeg === 90) {
          ctx.translate(height, 0)
          ctx.rotate(rad)
        } else if (rotationDeg === -90) {
          ctx.translate(0, width)
          ctx.rotate(rad)
        } else {
          ctx.translate(width, height)
          ctx.rotate(rad)
        }
      }

      const currentSelfTestMode = tempSelfTestModeRef.current || selfTestMode;

      const elapsedTime = time !== undefined
        ? time
        : (isAnimating ? (Date.now() - startTimeRef.current) : 0);

      const patternCtx = { ctx, width, height, time: elapsedTime, gridSize, animationSpeed: config.animationSpeed, colors: [] as string[] }

      // Mode Self-Test : fond noir
      if (currentSelfTestMode.enabled || isNavigatingRef.current) {
        ctx.fillStyle = '#000000'
        ctx.fillRect(0, 0, width, height)
      }
      // Mode standard patterns
      else if (activePatternType === 'standard' && config.standardPatternType !== "none") {
        drawStandardPattern(ctx, width, height, time, config.standardPatternType, {
          direction: config.rampDirection as "horizontal" | "vertical" | "diagonal",
          gridSize, animationSpeed: config.animationSpeed
        });
      }
      // Mode couleur unie
      else if (activePatternType === 'solid') {
        if (config.backgroundColor !== 'transparent') {
          ctx.fillStyle = config.backgroundColor;
          ctx.fillRect(0, 0, width, height);
        }
      }
      // Motifs personnalisés
      else {
        const colors = generateGradientColors(elapsedTime * config.animationSpeed)
        patternCtx.colors = colors

        const patternRenderers: Record<string, () => void> = {
          gradient: () => renderGradient(patternCtx),
          gradient4: () => renderGradient4(patternCtx),
          solid: () => renderSolid(patternCtx),
          checkerboard: () => renderCheckerboard(patternCtx),
          rainbow: () => renderRainbow(patternCtx),
          colorBars: () => renderColorBars(patternCtx),
          pulse: () => renderPulse(patternCtx, distanceMapCache, setDistanceMapCache),
          spiral: () => renderSpiral(patternCtx),
          orangeTeal: () => renderOrangeTeal(patternCtx),
          magentaSummer: () => renderMagentaSummer(patternCtx),
          summer: () => renderSummer(patternCtx),
          bluePurple: () => renderBluePurple(patternCtx),
          snake: () => renderSnake(patternCtx, snakeGameState),
        }

        const renderer = patternRenderers[config.patternType]
        if (renderer) renderer()
      }

      // === OVERLAYS SANS ANAMORPHOSE (GRILLE ET PANNEAUX) ===

      // Stocker l'image du canvas avant la grille pour le sonar reveal
      if ((time > 0 || isExporting) && config.sonarRevealMode && config.showSonar && !currentSelfTestMode.enabled) {
        if (!sonarBackgroundCanvasRef.current ||
            sonarLastDimensionsRef.current?.width !== canvasWidth ||
            sonarLastDimensionsRef.current?.height !== canvasHeight) {
          sonarBackgroundCanvasRef.current = document.createElement('canvas');
          sonarBackgroundCanvasRef.current.width = canvasWidth;
          sonarBackgroundCanvasRef.current.height = canvasHeight;
          sonarBackgroundCtxRef.current = sonarBackgroundCanvasRef.current.getContext('2d')!;
          sonarOffscreenCanvasRef.current = null;
          sonarLastDimensionsRef.current = { width: canvasWidth, height: canvasHeight };
        }
        sonarBackgroundCtxRef.current!.drawImage(canvasRef.current!, 0, 0);
      }

      // Grille (taille normale)
      renderGrid(ctx, width, height, { showGrid: config.showGrid, showLEDNumbers: config.showLEDNumbers, gridSize, gridOffset: config.gridOffset, ledWallMode: config.ledWallMode, gridEvenOpacity: config.gridEvenOpacity, gridOddOpacity: config.gridOddOpacity }, currentSelfTestMode, cyanPanels)

      // Sonar reveal effect (taille normale)
      if (config.showGrid && config.sonarRevealMode && config.showSonar && !currentSelfTestMode.enabled && (elapsedTime > 0 || isExporting)) {
        const effectiveElapsedTime = isExporting && elapsedTime === 0 ? 1 : elapsedTime
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        renderSonarRevealEffect(ctx, canvasWidth, canvasHeight, config, effectiveElapsedTime);
        ctx.restore();
      }

      // Panneaux cyan et subdivisions (taille normale)
      renderCyanPanels(ctx, currentSelfTestMode, config.ledWallMode || { enabled: false, columns: 0, rows: 0, panelWidth: 0, panelHeight: 0, borderWidth: 0 }, config.gridOffset, cyanPanels, subdivisionMode, redZones, selectedQuadrant)

      // Sonar classique (taille normale)
      renderClassicSonar(ctx, canvasWidth, canvasHeight, { showSonar: config.showSonar, sonarRevealMode: config.sonarRevealMode, sonarSpeed: config.sonarSpeed, gridOffset: config.gridOffset, sonarThickness: config.sonarThickness }, elapsedTime, isExporting, currentSelfTestMode.enabled)

      // Numeros LED Self-Test
      renderSelfTestLEDNumbers(ctx, currentSelfTestMode, config.ledWallMode || { enabled: false, columns: 0, rows: 0, panelWidth: 0, panelHeight: 0, borderWidth: 0 }, config.gridOffset, cyanPanels, subdivisionMode, redZones, selectedQuadrant, isAnimating, startTimeRef.current)

      // Numeros LED standard
      renderStandardLEDNumbers(ctx, width, height, { showLEDNumbers: config.showLEDNumbers, gridSize, gridOffset: config.gridOffset, ledWallMode: config.ledWallMode, patternType: config.patternType }, currentSelfTestMode.enabled, config.enableAnamorphosis)

      // Numeros LED personnalisés Nova Screen Config
      if (config.novaScreenConfigMode) {
        const dragPath = isNovaDraggingRef.current ? novaDragPathRef.current : undefined
        renderNovaCustomNumbers(ctx, width, height, { gridSize, gridOffset: config.gridOffset, ledWallMode: config.ledWallMode }, novaPanelConnections, dragPath, config.novaShowArrows, config.novaShowLines, config.novaMinimalMode)
      }

      // Logos
      renderLogoElements(ctx, logoElements, selectedLogoId, isFullscreen, isDragging, showSelection, currentSelfTestMode.enabled, config.enableAnamorphosis)

      // Textes
      renderTextElements(ctx, textElements, selectedTextId, isFullscreen, isDragging, showSelection, currentSelfTestMode.enabled, config.enableAnamorphosis)

      // Resolution
      renderResolution(ctx, width, height, { showResolution: config.showResolution, resolutionFontSize: config.resolutionFontSize, ledWallMode: config.ledWallMode, standardPatternType: config.standardPatternType }, activePatternType, gridSize, config.enableAnamorphosis)

      // Logo par defaut
      renderDefaultLogo(ctx, width, height, { showDefaultLogo: config.showDefaultLogo, gridOffset: config.gridOffset, pixelMapMode: config.pixelMapMode }, defaultLogoRef.current, logoElements.length, currentSelfTestMode.enabled, config.enableAnamorphosis)

      // Icon light
      renderIconLight(ctx, width, height, { showDefaultLogo: config.showDefaultLogo, gridOffset: config.gridOffset }, iconLightRef.current, logoElements.length, isPro, currentSelfTestMode.enabled, config.enableAnamorphosis)

      // Pixel Map Mode
      if (config.pixelMapMode) {
        renderPixelMapMode(ctx, width, height, config.pixelMapMode, isPro, currentSelfTestMode.enabled, config.enableAnamorphosis)
      }

    },
    [config, generateGradientColors, textElements, selectedTextId, logoElements, selectedLogoId, isFullscreen, snakeGameState, selfTestMode, tempSelfTestModeRef, isNavigatingRef, isAnimating, startTimeRef, cyanPanels, subdivisionMode, redZones, selectedQuadrant, disablePreviewWhenExternal, isExternalDisplayConnected, isDragging, isPro, distanceMapCache, novaPanelConnections, ndi, isTauriApp],
  )

  // Toujours appeler la dernière version de drawPattern dans la boucle d'animation
  const drawPatternRef = useRef(drawPattern)
  useEffect(() => {
    drawPatternRef.current = drawPattern
  }, [drawPattern])

  // Redessiner automatiquement quand les connexions Nova changent ou quand le port sélectionné change
  useEffect(() => {
    if (config.novaScreenConfigMode) {
      drawPatternRef.current()
    }
  }, [novaPanelConnections, selectedNovaPort, config.novaScreenConfigMode])

  // Hook pour l'export vidéo WebCodecs GPU-native
  // Utilise la vraie drawFunction pour capturer exactement ce que l'utilisateur voit
  const {
    isExporting: isWebCodecsExporting,
    progress: webCodecsProgress,
    currentFrame: webCodecsCurrentFrame,
    totalFrames: webCodecsTotalFrames,
    error: webCodecsError,
    exportSpeed: webCodecsExportSpeed,
    estimatedTimeRemaining: webCodecsTimeRemaining,
    isSupported: isWebCodecsSupported,
    statusMessage: webCodecsStatusMessage,
    phase: webCodecsPhase,
    selectedFormat: webCodecsSelectedFormat,
    startExport: startWebCodecsExport,
    cancelExport: cancelWebCodecsExport,
    closeExport: closeWebCodecsExport,
  } = useWebCodecsVideoExport(
    canvasRef,
    (time, showSelection) => {
      // Utilise la vraie fonction de dessin qui inclut TOUS les paramètres utilisateur
      // Passer les options comme multigrid pour gérer correctement le sonar
      drawPatternRef.current({ forExport: true, exportTime: time }, showSelection);
    }
  );

  // Redessiner le canvas quand effectiveIsMobile change (mobile ↔ desktop)
  useEffect(() => {
    if (!mounted) return
    // Petit délai pour laisser le canvas se monter
    const timer = setTimeout(() => {
      drawPattern()
    }, 100)
    return () => clearTimeout(timer)
  }, [effectiveIsMobile, mounted, drawPattern])

  // Synchroniser pixelMapMode avec la config actuelle quand les paramètres de la mire changent
  useEffect(() => {
    if (!config.pixelMapMode?.enabled) return
    setConfig(prev => ({
      ...prev,
      pixelMapMode: {
        ...prev.pixelMapMode,
        resW: prev.width,
        resH: prev.height,
        panelW: prev.ledWallMode?.panelWidth || prev.gridSize || 192,
        panelH: prev.ledWallMode?.panelHeight || prev.gridSize || 192,
      }
    }))
  }, [config.width, config.height, config.gridSize, config.ledWallMode?.panelWidth, config.ledWallMode?.panelHeight, config.pixelMapMode?.enabled])

  // ============= OPTIMISATIONS ADVANCED SONAR MODE =============
  // Pool COMPLET : obtenir ou créer les canvases hors-écran réutilisables (ZERO allocation par frame!)
  const getSonarCanvases = useCallback((width: number, height: number) => {
    // Vérifier si les dimensions correspondent
    if (sonarLastDimensionsRef.current?.width === width &&
        sonarLastDimensionsRef.current?.height === height &&
        sonarOffscreenCanvasRef.current &&
        sonarBackgroundCanvasRef.current) {
      return {
        offscreenCanvas: sonarOffscreenCanvasRef.current,
        offscreenCtx: sonarOffscreenCtxRef.current!,
        backgroundCanvas: sonarBackgroundCanvasRef.current,
        backgroundCtx: sonarBackgroundCtxRef.current!
      };
    }

    // Créer les canvases si dimensions changent
    const offscreenCanvas = sonarOffscreenCanvasRef.current || document.createElement('canvas');
    offscreenCanvas.width = width;
    offscreenCanvas.height = height;
    const offscreenCtx = offscreenCanvas.getContext('2d')!;

    const backgroundCanvas = sonarBackgroundCanvasRef.current || document.createElement('canvas');
    backgroundCanvas.width = width;
    backgroundCanvas.height = height;
    const backgroundCtx = backgroundCanvas.getContext('2d')!;

    // Stocker en cache pour réutilisation
    sonarOffscreenCanvasRef.current = offscreenCanvas;
    sonarOffscreenCtxRef.current = offscreenCtx;
    sonarBackgroundCanvasRef.current = backgroundCanvas;
    sonarBackgroundCtxRef.current = backgroundCtx;
    sonarLastDimensionsRef.current = { width, height };

    return { offscreenCanvas, offscreenCtx, backgroundCanvas, backgroundCtx };
  }, []);

  // ULTRA-OPTIMISÉ: Révélation sonar SANS getImageData/putImageData!
  const renderSonarRevealEffect = useCallback((
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    config: any,
    elapsedTime: number
  ) => {
    if (!canvasRef.current || !sonarBackgroundCanvasRef.current) return;

    const centerX = width / 2 + config.gridOffset.x;
    const centerY = height / 2 + config.gridOffset.y;
    const maxRadius = Math.max(
      Math.sqrt(centerX * centerX + centerY * centerY),
      Math.sqrt((width - centerX) * (width - centerX) + centerY * centerY),
      Math.sqrt(centerX * centerX + (height - centerY) * (height - centerY)),
      Math.sqrt((width - centerX) * (width - centerX) + (height - centerY) * (height - centerY))
    );
    const sonarInterval = 4000; // Augmenté de 2000 à 4000 pour ralentir le sonar
    // Utiliser elapsedTime directement (géré par drawPattern)
    const sonarTime = (elapsedTime * config.sonarSpeed) % sonarInterval;
    const sonarProgress = sonarTime / sonarInterval;

    // Utiliser l'épaisseur configurée ou une valeur par défaut
    const baseThickness = config.sonarThickness ?? 4;

    // Calculer rapidement les anneaux (pas d'allocation inutile)
    const sonarRings: Array<{ radius: number; opacity: number; thickness: number }> = [];
    for (let i = 0; i < 3; i++) {
      const ringProgress = (sonarProgress + i * 0.33) % 1;
      if (ringProgress < 0.8) {
        sonarRings.push({
          radius: ringProgress * maxRadius * 1.2,
          opacity: Math.max(0, 1 - ringProgress * 1.25),
          thickness: baseThickness // Utiliser l'épaisseur configurée
        });
      }
    }

    if (sonarRings.length === 0) return;

    // Obtenir les canvases réutilisables (AUCUNE création de nouveau canvas!)
    const { offscreenCanvas, offscreenCtx, backgroundCanvas } = getSonarCanvases(width, height);

    // ÉTAPE 1: Nettoyer et préparer le canvas hors-écran
    offscreenCtx.clearRect(0, 0, width, height);

    // ÉTAPE 2: Copier l'image AVEC grille
    offscreenCtx.drawImage(canvasRef.current, 0, 0);

    // ÉTAPE 3: Appliquer les masques des anneaux DIRECTEMENT
    offscreenCtx.save();
    sonarRings.forEach(ring => {
      offscreenCtx.beginPath();
      offscreenCtx.arc(centerX, centerY, ring.radius, 0, 2 * Math.PI);
      offscreenCtx.lineWidth = ring.thickness;
      offscreenCtx.strokeStyle = `rgba(255, 255, 255, ${ring.opacity})`;
      offscreenCtx.globalCompositeOperation = "destination-out";
      offscreenCtx.stroke();
    });

    // ÉTAPE 4: Peindre l'image du background directement (sans putImageData!)
    // Le background canvas contient déjà l'image avant la grille
    offscreenCtx.globalCompositeOperation = "destination-atop";
    offscreenCtx.drawImage(backgroundCanvas, 0, 0);

    offscreenCtx.restore();

    // ÉTAPE 5: Copier le résultat au canvas principal
    ctx.drawImage(offscreenCanvas, 0, 0);
  }, [getSonarCanvases]);

  // Animation loop
  const animateLoop = useCallback(() => {
    if (!isAnimating) return
    const currentTime = Date.now()
    const elapsedTime = currentTime - startTimeRef.current
    drawPatternRef.current(elapsedTime, true)
    animationRef.current = requestAnimationFrame(animateLoop)
  }, [isAnimating])

  // Fullscreen functionality
  const handleToggleFullscreen = () => {
    // Uniformiser le comportement Web/Electron: ne mettre en plein écran que le conteneur du canvas
    if (document.fullscreenElement) {
      document.exitFullscreen()
    } else if (fullscreenContainerRef.current) {
      fullscreenContainerRef.current.requestFullscreen()
    }

    // Compter immédiatement lors du passage en plein écran (action utilisateur délibérée)
    gridCounter.forceCount();
  }

  // Effet pour gérer l'échelle du canvas en mode plein écran
  useEffect(() => {
    const adjustCanvasScale = () => {
      if (isFullscreen && canvasRef.current) {
        const canvas = canvasRef.current;

        // Réinitialiser toute transformation précédente
        canvas.style.transform = 'none';

        // Laisser le canvas à sa taille d'origine (échelle 1:1 des pixels)
        // Aucune mise à l'échelle n'est appliquée pour préserver la netteté des pixels
      } else if (canvasRef.current) {
        // Réinitialiser la transformation quand on quitte le mode plein écran
        canvasRef.current.style.transform = 'none';
      }
    };

    adjustCanvasScale();

    // Ajuster si la fenêtre est redimensionnée en mode plein écran
    window.addEventListener('resize', adjustCanvasScale);
    return () => window.removeEventListener('resize', adjustCanvasScale);
  }, [isFullscreen]);

  // Fonction pour sauvegarder la configuration actuelle
  const handleSaveConfig = async () => {
    if (showSaveDialog) {
      // Si le dialogue est déjà ouvert, ne rien faire
      return;
    }

    // Ouvrir le dialogue de sauvegarde
    setConfigName("");
    setConfigDisplayName("");
    setMakeDefaultConfig(false);
    setShowSaveDialog(true);
  }

  // Fonction pour effectuer la sauvegarde
  const handleSaveConfigConfirm = async () => {
    try {
      // Valider les noms
      const name = configName.trim() || `config_${new Date().getTime()}`;
      const displayName = configDisplayName.trim() || `Configuration du ${new Date().toLocaleDateString()}`;

      // Préparer l'objet de configuration complète
      const fullConfig = {
        version: "1.0.0", // Pour la compatibilité future
        timestamp: new Date().toISOString(),
        canvas: config,
        textElements,
        logoElements: logoElements.map(logo => ({
          id: logo.id,
          x: logo.x,
          y: logo.y,
          width: logo.width,
          height: logo.height,
          opacity: logo.opacity,
          // Convertir l'image en data URL
          dataUrl: logo.isGif ? logo.imageSrc : logo.image.src,
          isGif: logo.isGif,
          imageSrc: logo.imageSrc
        })),
        // ✅ NOUVEAUX PARAMÈTRES SAUVEGARDÉS
        activePatternType,
        selfTestMode: {
          ...selfTestMode,
          activePanels: Array.from(selfTestMode.activePanels) // Convertir Set en Array
        },
        cyanPanels: Array.from(cyanPanels), // Convertir Set en Array
        subdivisionMode,
        redZones: Array.from(redZones), // Convertir Set en Array
        selectedQuadrant,
        disablePreviewWhenExternal,
        isAnimating,
        // États d'affichage des valeurs
        columnsDisplayValue,
        rowsDisplayValue
      }

      const result = await saveConfig(fullConfig, {
        name,
        displayName,
        makeDefault: makeDefaultConfig
      });

      if (result.success) {
        toast.success(t('toast.configSaved').replace('{name}', name));
        setShowSaveDialog(false);
        
        // ✅ PostHog: Track config save
        captureEvent('grid:save_config', {
          resolution: `${config.width}x${config.height}`,
          pattern_type: config.patternType,
          standard_pattern_type: config.standardPatternType,
          led_wall_enabled: config.ledWallMode?.enabled,
          led_wall_columns: config.ledWallMode?.columns,
          led_wall_rows: config.ledWallMode?.rows,
          led_wall_panel_width: config.ledWallMode?.panelWidth,
          led_wall_panel_height: config.ledWallMode?.panelHeight,
          led_wall_border_width: config.ledWallMode?.borderWidth,
          is_animating: isAnimating,
          text_elements_count: textElements.length,
          logo_elements_count: logoElements.length,
          self_test_enabled: selfTestMode.enabled,
          cyan_panels_count: cyanPanels.size,
          subdivision_mode: subdivisionMode,
          red_zones_count: redZones.size,
          make_default: makeDefaultConfig,
        });

        // Recharger la liste des configurations
        await loadConfigList();

        // Compter immédiatement lors de la sauvegarde (action utilisateur délibérée)
        gridCounter.forceCount();
      } else {
        toast.error(t("toast.configSaveError", { error: result.error }));
      }
    } catch (error) {
      toast.error(t('toast.saveError'));
    }
  }

  // Fonction pour charger une configuration
  const handleLoadConfig = async () => {
    if (showLoadDialog) {
      // Si le dialogue est déjà ouvert, ne rien faire
      return;
    }

    // Recharger la liste des configurations avant d'ouvrir le dialogue
    await loadConfigList();

    // Ouvrir le dialogue de chargement
    setShowLoadDialog(true);
  }

  // Restaure un snapshot de configuration dans l'éditeur
  // (config sauvegardée classique ou mire rechargée depuis Synoptique)
  const applyConfigSnapshot = useCallback((configToLoad: GridConfigSnapshot) => {
    // Restaurer la configuration de base
    if (configToLoad.canvas) {
      const completeConfig = ensureCompleteClassicConfig(configToLoad.canvas)
      setConfig(completeConfig)
    }

    // ✅ RESTAURER LES ÉLÉMENTS DE TEXTE
    if (configToLoad.textElements) {
      setTextElements(configToLoad.textElements)
    }

    // ✅ RESTAURER LES ÉLÉMENTS DE LOGO
    if (configToLoad.logoElements && Array.isArray(configToLoad.logoElements)) {
      const reconstructedLogos = configToLoad.logoElements.map((logoData: SerializedLogoData) => {
        const img = new Image()
        const imageSrc = logoData.dataUrl || logoData.image || logoData.imageSrc || ''
        img.src = imageSrc
        img.width = logoData.width || 100
        img.height = logoData.height || 100

        return {
          id: logoData.id,
          x: logoData.x,
          y: logoData.y,
          width: logoData.width,
          height: logoData.height,
          opacity: logoData.opacity || 1,
          image: img,
          originalWidth: logoData.originalWidth || logoData.width || 100,
          originalHeight: logoData.originalHeight || logoData.height || 100,
          isGif: logoData.isGif || false,
          imageSrc: logoData.isGif ? imageSrc : undefined
        }
      })
      setLogoElements(reconstructedLogos)
    }

    // ✅ RESTAURER LE TYPE DE MOTIF ACTIF
    if (configToLoad.activePatternType) {
      setActivePatternType(configToLoad.activePatternType)
    }

    // ✅ RESTAURER LE MODE SELF-TEST
    if (configToLoad.selfTestMode) {
      setSelfTestMode({
        ...configToLoad.selfTestMode,
        activePanels: new Set(configToLoad.selfTestMode.activePanels || [])
      })
    }

    // ✅ RESTAURER LES PANNEAUX CYAN
    if (configToLoad.cyanPanels) {
      setCyanPanels(new Set(configToLoad.cyanPanels))
    }

    // ✅ RESTAURER LE MODE SUBDIVISION
    if (typeof configToLoad.subdivisionMode === 'boolean') {
      setSubdivisionMode(configToLoad.subdivisionMode)
    }

    // ✅ RESTAURER LES ZONES ROUGES
    if (configToLoad.redZones) {
      setRedZones(new Set(configToLoad.redZones))
    }

    // ✅ RESTAURER LE QUADRANT SÉLECTIONNÉ
    if (configToLoad.selectedQuadrant) {
      setSelectedQuadrant(configToLoad.selectedQuadrant)
    }

    // ✅ RESTAURER LA DÉSACTIVATION APERÇU EXTERNE
    if (typeof configToLoad.disablePreviewWhenExternal === 'boolean') {
      setDisablePreviewWhenExternal(configToLoad.disablePreviewWhenExternal)
    }

    // ✅ RESTAURER L'ÉTAT D'ANIMATION
    if (typeof configToLoad.isAnimating === 'boolean') {
      setIsAnimating(configToLoad.isAnimating)
    }

    // ✅ RESTAURER LES VALEURS D'AFFICHAGE
    if (configToLoad.columnsDisplayValue) {
      setColumnsDisplayValue(configToLoad.columnsDisplayValue)
    }
    if (configToLoad.rowsDisplayValue) {
      setRowsDisplayValue(configToLoad.rowsDisplayValue)
    }
  }, [setConfig, setTextElements, setLogoElements, setActivePatternType, setSelfTestMode, setCyanPanels, setSubdivisionMode, setRedZones, setSelectedQuadrant, setDisablePreviewWhenExternal, setIsAnimating, setColumnsDisplayValue, setRowsDisplayValue])

  // Fonction pour charger une configuration spécifique
  const handleLoadSpecificConfig = async (name: string) => {
    try {
      const result = await loadConfig(name)
      if (result && result.success && result.config) {
        applyConfigSnapshot(result.config)

        setShowLoadDialog(false)
        toast.success(t("toast.configLoadedComplete", { name }))
        // Compter immédiatement lors du chargement d'une configuration (action utilisateur délibérée)
        gridCounter.forceCount();
      } else {
        toast.error(t('toast.loadError'))
      }
    } catch (error) {
      toast.error(t('toast.loadError'))
    }
  }

  // Fonction pour supprimer une configuration
  const handleDeleteConfig = async (name: string) => {
    if (window.confirm(t("confirm.deleteConfig"))) {
      try {
        const result = await deleteConfig(name);

        if (result.success) {
          toast.success(t("toast.configDeleted"));
          await loadConfigList();
        } else {
          toast.error(t("toast.configDeleteError", { error: result.error }));
        }
      } catch (error) {
        toast.error(t("toast.configDeleteGenericError"));
      }
    }
  }

  // Fonction pour exporter le canvas
  // Envoyer la configuration LED au parent (Synoptique) via postMessage
  const sendLEDConfigToSynoptic = useCallback(() => {
    if (!isEmbeddedInSynoptic) return

    const ledConfig = {
      type: 'LED_CONFIG_UPDATE',
      timestamp: Date.now(),
      width: config.width,
      height: config.height,
      gridSize: config.gridSize,
      ledWallMode: {
        enabled: config.ledWallMode?.enabled || false,
        columns: config.ledWallMode?.columns || 1,
        rows: config.ledWallMode?.rows || 1,
        panelWidth: config.ledWallMode?.panelWidth || 192,
        panelHeight: config.ledWallMode?.panelHeight || 192,
        borderWidth: config.ledWallMode?.borderWidth || 0,
      },
      // ✅ AJOUTER LES ÉLÉMENTS DE TEXTE ET LOGO
      textElements: textElements,
      logoElements: logoElements.map(logo => ({
        id: logo.id,
        x: logo.x,
        y: logo.y,
        width: logo.width,
        height: logo.height,
        opacity: logo.opacity,
        dataUrl: logo.isGif ? logo.imageSrc : logo.image.src,
        isGif: logo.isGif,
        imageSrc: logo.imageSrc,
        originalWidth: logo.originalWidth,
        originalHeight: logo.originalHeight
      })),
      // ✅ Snapshot complet de l'éditeur — permet de recharger la mire pour modification
      editorState: {
        canvas: config,
        activePatternType,
        selfTestMode: {
          ...selfTestMode,
          activePanels: Array.from(selfTestMode.activePanels)
        },
        cyanPanels: Array.from(cyanPanels),
        subdivisionMode,
        redZones: Array.from(redZones),
        selectedQuadrant,
        disablePreviewWhenExternal,
        isAnimating,
        columnsDisplayValue,
        rowsDisplayValue
      }
    }

    // Envoyer au parent via postMessage
    window.parent.postMessage(ledConfig, window.location.origin)

    // Aussi sauvegarder dans localStorage pour persistance
    localStorage.setItem('visualgrid_led_config', JSON.stringify(ledConfig))

  }, [config, textElements, logoElements, isEmbeddedInSynoptic, activePatternType, selfTestMode, cyanPanels, subdivisionMode, redZones, selectedQuadrant, disablePreviewWhenExternal, isAnimating, columnsDisplayValue, rowsDisplayValue])

  // Flag : après chargement d'une mire depuis Synoptique, renvoyer la config au parent
  // (l'envoi auto debounced ne couvre que certains champs et exige ledWallMode.enabled)
  const pendingMireNotifyRef = useRef(false)

  // Écouter les demandes de capture d'image et de chargement de mire depuis le parent (Synoptique)
  useEffect(() => {
    if (!isEmbeddedInSynoptic) return

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return

      // ✅ Recharger une mire sauvegardée dans l'éditeur pour modification
      if (event.data && event.data.type === 'LOAD_MIRE_CONFIG' && event.data.mire) {
        const mire = event.data.mire as LEDConfig

        const snapshot: GridConfigSnapshot = mire.editorState
          ? {
              ...mire.editorState,
              textElements: mire.textElements,
              logoElements: mire.logoElements
            }
          : {
              // Ancienne mire sans snapshot complet : restaurer la structure disponible
              canvas: {
                width: mire.width,
                height: mire.height,
                gridSize: mire.gridSize,
                ledWallMode: mire.ledWallMode
              },
              textElements: mire.textElements,
              logoElements: mire.logoElements
            }

        applyConfigSnapshot(snapshot)
        pendingMireNotifyRef.current = true
        // Accusé de réception au parent pour annuler le timeout de l'éditeur
        window.parent?.postMessage({ type: 'MIRE_CONFIG_LOADED' }, event.origin)
      }

      if (event.data && event.data.type === 'CAPTURE_MIRE_IMAGE') {

        if (canvasRef.current) {
          try {
            // Redessiner sans indicateurs de sélection pour la capture
            drawPatternRef.current(undefined, false)
            const dataUrl = canvasRef.current.toDataURL("image/png")
            // Restaurer l'affichage avec les indicateurs de sélection
            drawPatternRef.current(undefined, true)

            // Envoyer l'image au parent avec le requestId pour éviter les race conditions
            window.parent.postMessage({
              type: 'MIRE_IMAGE_CAPTURE',
              imageData: dataUrl,
              requestId: event.data.requestId // Renvoyer le requestId reçu
            }, window.location.origin)

          } catch (error) {
            console.error('Erreur lors de la capture de l\'image:', error)
          }
        }
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [isEmbeddedInSynoptic, applyConfigSnapshot])

  // Après le chargement d'une mire, renvoyer la config au parent (Synoptique)
  // pour qu'il affiche la mire en attente — même si ledWallMode est désactivé
  useEffect(() => {
    if (!pendingMireNotifyRef.current || !isEmbeddedInSynoptic) return
    pendingMireNotifyRef.current = false
    const timeoutId = setTimeout(() => sendLEDConfigToSynoptic(), 400)
    return () => clearTimeout(timeoutId)
  }, [config, isEmbeddedInSynoptic, sendLEDConfigToSynoptic])

  // Tracker si c'est le premier render pour éviter l'envoi automatique au chargement
  const isFirstRender = useRef(true)

  // Envoyer automatiquement la config au chargement de l'iframe
  useEffect(() => {
    if (isEmbeddedInSynoptic && config.ledWallMode?.enabled) {
      // Envoyer la config par défaut immédiatement au chargement
      const timeoutId = setTimeout(() => {
        sendLEDConfigToSynoptic()
      }, 500) // Petit délai pour s'assurer que l'iframe est prête

      return () => clearTimeout(timeoutId)
    }
  }, [isEmbeddedInSynoptic, sendLEDConfigToSynoptic, config.ledWallMode?.enabled])

  // Envoyer automatiquement la config quand elle change (mode debounced)
  useEffect(() => {
    if (!isEmbeddedInSynoptic || !config.ledWallMode?.enabled) return

    // Ne pas envoyer au premier render (évite la notification automatique au chargement)
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }

    // Attendre 1 seconde après le dernier changement avant d'envoyer
    const timeoutId = setTimeout(() => {
      sendLEDConfigToSynoptic()
    }, 1000)

    return () => clearTimeout(timeoutId)
  }, [
    config.ledWallMode?.columns,
    config.ledWallMode?.rows,
    config.ledWallMode?.panelWidth,
    config.ledWallMode?.panelHeight,
    config.width,
    config.height,
    sendLEDConfigToSynoptic,
    isEmbeddedInSynoptic,
    config.ledWallMode?.enabled
  ])

  const handleExportCanvas = async () => {
    if (!canvasRef.current) return

    try {
      // Redessiner sans indicateurs de sélection pour l'export
      drawPatternRef.current(undefined, false)
      const dataUrl = canvasRef.current.toDataURL("image/png")
      // Restaurer l'affichage avec les indicateurs de sélection
      drawPatternRef.current(undefined, true)
      
      // Nomenclature: {Résolution}_{Res panneau}_VisualGrid.png
      const panelW = config.ledWallMode?.enabled ? config.ledWallMode.panelWidth : config.gridSize;
      const panelH = config.ledWallMode?.enabled ? config.ledWallMode.panelHeight : config.gridSize;
      const filename = `${config.width}x${config.height}_${panelW}x${panelH}_VisualGrid.png`
      
      const result = await exportCanvas(dataUrl, filename)
      
      if (result.success) {
        toast.success(t('toast.imageExported'))
        
        // ✅ Upload vers Supabase Storage
        const supabaseUpload = await uploadTestPatternToSupabase(dataUrl, filename);
        if (supabaseUpload.success) {
          console.log('✅ Mire uploadée vers Supabase:', supabaseUpload.publicUrl);
        } else {
          console.warn('⚠️ Erreur upload Supabase (non bloquant):', supabaseUpload.error);
        }
        
        // ✅ PostHog: Track PNG export
        captureEvent('grid:export_png', {
          resolution: `${config.width}x${config.height}`,
          panel_resolution: `${panelW}x${panelH}`,
          pattern_type: config.patternType,
          standard_pattern_type: config.standardPatternType,
          led_wall_enabled: config.ledWallMode?.enabled,
          led_wall_columns: config.ledWallMode?.columns,
          led_wall_rows: config.ledWallMode?.rows,
          is_animating: isAnimating,
          text_elements_count: textElements.length,
          logo_elements_count: logoElements.length,
          is_embedded_in_synoptic: isEmbeddedInSynoptic,
          supabase_upload_success: supabaseUpload.success,
        });
      } else if (result.error) {
        toast.error(result.error)
        return
      }

      // NOUVEAU: Envoyer la config au Synoptique si on est dans l'iframe
      if (isEmbeddedInSynoptic && config.ledWallMode?.enabled) {
        sendLEDConfigToSynoptic()
        toast.success(t('toast.configSentToSynoptic'), {
          description: t('toast.configSentToSynopticDesc').replace('{columns}', String(config.ledWallMode?.columns || 1)).replace('{rows}', String(config.ledWallMode?.rows || 1))
        })
      }

      // Compter immédiatement lors de l'exportation (action utilisateur délibérée)
      gridCounter.forceCount();
    } catch (error) {
      toast.error(t('toast.configExportError'))
    }
  }

  // Text management
  const addTextElement = useCallback(() => {
    // Vérifier que les dimensions sont des nombres valides
    const width = typeof config.width === 'number' && isFinite(config.width) ? config.width : 1920
    const height = typeof config.height === 'number' && isFinite(config.height) ? config.height : 1080

    const newText: TextElement = {
      id: Date.now().toString(),
      text: "Nouveau texte",
      x: width / 2,
      y: height / 2,
      fontSize: 48,
      fontFamily: "Arial, sans-serif",
      fontWeight: 400,
      fontStyle: "normal",
      textAlign: "center",
      color: "#ffffff",
      strokeWidth: 0,
    }
    setTextElements((prev) => {
      const newElements = [...prev, newText];

      // Synchroniser vers l'affichage externe - synchronisation instantanée
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.postMessage({
          type: 'TEXT_ELEMENTS_UPDATE',
          data: newElements
        });
      }

      return newElements;
    });
    setSelectedTextId(newText.id)
    setShowTextPanel(true)
  }, [config.width, config.height, isExternalDisplayConnected])

  const updateTextElement = useCallback((id: string, updates: Partial<TextElement>) => {
    setTextElements((prev) => {
      const newElements = prev.map((text) => (text.id === id ? { ...text, ...updates } : text));

      // Synchroniser vers l'affichage externe - synchronisation instantanée
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.postMessage({
          type: 'TEXT_ELEMENTS_UPDATE',
          data: newElements
        });
      }

      return newElements;
    });
  }, [])

  const deleteTextElement = useCallback(
    (id: string) => {
      setTextElements((prev) => {
        const newElements = prev.filter((text) => text.id !== id);

        // Synchroniser vers l'affichage externe - synchronisation instantanée
        if (broadcastChannelRef.current) {
          broadcastChannelRef.current.postMessage({
            type: 'TEXT_ELEMENTS_UPDATE',
            data: newElements
          });
        }

        return newElements;
      });

      if (selectedTextId === id) {
        setSelectedTextId(null)
      }
    },
    [selectedTextId, isExternalDisplayConnected],
  )

  // Quick alignment functions
  const alignText = useCallback(
    (alignment: string) => {
      if (!selectedTextId) return

      const selectedText = textElements.find((text) => text.id === selectedTextId)
      if (!selectedText) return

      // Vérifier que les dimensions sont des nombres valides
      const width = typeof config.width === 'number' && isFinite(config.width) ? config.width : 1920

      const updates: Partial<TextElement> = { textAlign: alignment }

      // Toujours utiliser textAlign "center" pour que le point d'ancrage soit le centre du texte
      // Puis positionner ce centre selon l'alignement demandé
      updates.textAlign = "center"

      switch (alignment) {
        case "center":
          updates.x = width / 2
          break
        case "left":
          // Positionner le centre du texte près du bord gauche
          updates.x = 50
          break
        case "right":
          // Positionner le centre du texte près du bord droit
          updates.x = width - 50
          break
      }

      updateTextElement(selectedTextId, updates)
    },
    [selectedTextId, textElements, config.width, updateTextElement],
  )

  const alignVertical = useCallback(
    (position: "top" | "middle" | "bottom") => {
      if (!selectedTextId) return

      const selectedText = textElements.find((text) => text.id === selectedTextId)
      if (!selectedText) return

      // Vérifier que les dimensions sont des nombres valides
      const height = typeof config.height === 'number' && isFinite(config.height) ? config.height : 1080

      // La hauteur du texte est approximativement égale à fontSize
      // Note: textBaseline est "top", donc y représente le haut du texte
      // Pour centrer verticalement, on doit décaler de fontSize/2
      const textHeight = selectedText.fontSize

      let y: number
      switch (position) {
        case "top":
          // Positionner le centre du texte près du haut
          // y = 50 représente le haut du texte, donc le centre est à 50 + textHeight/2
          // Pour que le centre soit à 50, on met y à 50 - textHeight/2
          y = 50
          break
        case "middle":
          // Centrer verticalement : positionner le haut du texte de sorte que son centre soit au milieu
          // Centre du texte = y + textHeight/2, donc pour center = height/2
          // y = height/2 - textHeight/2
          y = height / 2 - textHeight / 2
          break
        case "bottom":
          // Positionner le centre du texte près du bas
          // Pour que le centre soit à (height - 50), on met y à (height - 50 - textHeight/2)
          y = height - 50 - textHeight
          break
      }

      updateTextElement(selectedTextId, { y })
    },
    [selectedTextId, textElements, config.height, updateTextElement],
  )

  // Keyboard navigation for text and logos
  const moveElementWithArrows = useCallback(
    (direction: "up" | "down" | "left" | "right", step = 1) => {
      // Vérifier que les dimensions sont des nombres valides
      const width = typeof config.width === 'number' && isFinite(config.width) ? config.width : 1920
      const height = typeof config.height === 'number' && isFinite(config.height) ? config.height : 1080

      if (selectedTextId) {
        const selectedText = textElements.find((text) => text.id === selectedTextId)
        if (!selectedText) return

        const updates: Partial<TextElement> = {}

        switch (direction) {
          case "up":
            updates.y = Math.max(0, selectedText.y - step)
            break
          case "down":
            updates.y = Math.min(height - selectedText.fontSize, selectedText.y + step)
            break
          case "left":
            updates.x = Math.max(0, selectedText.x - step)
            break
          case "right":
            updates.x = Math.min(width, selectedText.x + step)
            break
        }

        updateTextElement(selectedTextId, updates)
      } else if (selectedLogoId) {
        const selectedLogo = logoElements.find((logo) => logo.id === selectedLogoId)
        if (!selectedLogo) return

        const updates: Partial<LogoElement> = {}

        switch (direction) {
          case "up":
            updates.y = Math.max(0, selectedLogo.y - step)
            break
          case "down":
            updates.y = Math.min(height - selectedLogo.height, selectedLogo.y + step)
            break
          case "left":
            updates.x = Math.max(0, selectedLogo.x - step)
            break
          case "right":
            updates.x = Math.min(width - selectedLogo.width, selectedLogo.x + step)
            break
        }

        updateLogoElement(selectedLogoId, updates)
      }
    },
    [
      selectedTextId,
      selectedLogoId,
      textElements,
      logoElements,
      config.width,
      config.height,
      updateTextElement,
      updateLogoElement,
    ],
  )

  // Canvas click handler for text and logo positioning
  const handleCanvasClick = useCallback(
    (event: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current
      if (!canvas) return

      // Bloquer toute interaction pendant l'export vidéo
      if (isWebCodecsExporting) {
        return
      }

      // Ignorer le click si on vient de finir un drag (évite le repositionnement au relâchement)
      if (justFinishedDraggingRef.current) {
        return
      }

      // Logique pour le mode Nova Screen Config - câblage des panneaux
      if (config.novaScreenConfigMode && config.ledWallMode?.enabled && selectedNovaPortRef.current !== null) {
        const rect = canvas.getBoundingClientRect();
        const scaleX = config.width / rect.width;
        const scaleY = config.height / rect.height;
        const clickX = (event.clientX - rect.left) * scaleX;
        const clickY = (event.clientY - rect.top) * scaleY;

        const { columns, rows, panelWidth, panelHeight } = config.ledWallMode;
        const offsetX = config.gridOffset.x;
        const offsetY = config.gridOffset.y;

        // Calculer quel panneau a été cliqué
        const panelCol = Math.floor((clickX - offsetX) / panelWidth);
        const panelRow = Math.floor((clickY - offsetY) / panelHeight);

        // Vérifier que le clic est dans la grille
        if (panelCol >= 0 && panelCol < columns && panelRow >= 0 && panelRow < rows) {
          const panelKey = `${panelCol + 1}.${panelRow + 1}`;

          setNovaPanelConnections(prev => {
            const existingConnections = prev.filter(c => c.panelId === panelKey);

            if (existingConnections.length > 0) {
              const preferredConnection = selectedNovaPortRef.current !== null
                ? existingConnections.find(c => c.port === selectedNovaPortRef.current)
                : undefined;
              const clickedPort = preferredConnection?.port ?? existingConnections[0]!.port;
              const clickedOrder = Math.min(
                ...existingConnections
                  .filter(c => c.port === clickedPort)
                  .map(c => c.order)
              );
              const filtered = prev.filter(c => 
                !(c.port === clickedPort && c.order >= clickedOrder)
              );

              const portConns = filtered.filter(c => c.port === clickedPort);
              const otherConns = filtered.filter(c => c.port !== clickedPort);
              const recalculatedPortConns = portConns.map((conn, index) => ({
                ...conn,
                order: index + 1
              }));
              const final = [...otherConns, ...recalculatedPortConns];
              novaPanelConnectionsRef.current = final;
              return final;
            }

            const baseConnections = prev.filter(c => c.panelId !== panelKey);
            const portConnections = baseConnections.filter(c => c.port === selectedNovaPortRef.current);
            const newConnection = {
              panelId: panelKey,
              port: selectedNovaPortRef.current!,
              order: portConnections.length + 1
            };
            const updated = [...baseConnections, newConnection];
            const portConns = updated.filter(c => c.port === selectedNovaPortRef.current);
            const otherConns = updated.filter(c => c.port !== selectedNovaPortRef.current);
            const recalculatedPortConns = portConns.map((conn, index) => ({
              ...conn,
              order: index + 1
            }));
            const final = [...otherConns, ...recalculatedPortConns];
            novaPanelConnectionsRef.current = final;
            return final;
          });
          return;
        }
      }

      // Priorité au Self-Test si activé
      if (selfTestMode.enabled && config.ledWallMode?.enabled) {
        const rect = canvas.getBoundingClientRect();
        const scaleX = config.width / rect.width;
        const scaleY = config.height / rect.height;
        const clickX = (event.clientX - rect.left) * scaleX;
        const clickY = (event.clientY - rect.top) * scaleY;

        const { columns, rows, panelWidth, panelHeight } = config.ledWallMode;
        const offsetX = config.gridOffset.x;
        const offsetY = config.gridOffset.y;

        // Calculer quel panneau a été cliqué
        const panelCol = Math.floor((clickX - offsetX) / panelWidth);
        const panelRow = Math.floor((clickY - offsetY) / panelHeight);

        // Vérifier que le clic est dans la grille
        if (panelCol >= 0 && panelCol < columns && panelRow >= 0 && panelRow < rows) {
          const newX = panelCol + 1;
          const newY = panelRow + 1;
          const panelKey = `${newX},${newY}`;

          let newActivePanels = selfTestMode.trailMode
            ? new Set(selfTestMode.activePanels)
            : new Set<string>();

          newActivePanels.add(panelKey);

          // Stocker immédiatement les nouvelles valeurs avec enabled: true forcé
          tempSelfTestModeRef.current = {
            ...selfTestMode,
            enabled: true, // S'assurer que le mode reste activé
            selectedPanelX: newX,
            selectedPanelY: newY,
            activePanels: newActivePanels,
            showBorders: selfTestMode.showBorders // Préserver l'état des bordures
          };

          // Redessiner IMMÉDIATEMENT
          drawPattern();

          // Mettre à jour l'état React
          setSelfTestMode(prev => ({
            ...prev,
            selectedPanelX: newX,
            selectedPanelY: newY,
            activePanels: newActivePanels
          }));

          // Nettoyer le ref temporaire après un court délai
          keyPressTimeoutRef.current = window.setTimeout(() => {
            tempSelfTestModeRef.current = null;
            isNavigatingRef.current = false;
            keyPressTimeoutRef.current = null;
          }, 150);

          // Maintenir le focus sur le canvas pour permettre la navigation clavier
          canvas.focus();
          return; // Sortir ici pour éviter la logique texte/logo
        }
      }

      // Logique existante pour textes et logos
      // Vérifier que les dimensions sont des nombres valides
      const width = typeof config.width === 'number' && isFinite(config.width) ? config.width : 1920
      const height = typeof config.height === 'number' && isFinite(config.height) ? config.height : 1080

      const rect = canvas.getBoundingClientRect()
      const scaleX = width / rect.width
      const scaleY = height / rect.height

      const x = (event.clientX - rect.left) * scaleX
      const y = (event.clientY - rect.top) * scaleY

      if (selectedTextId) {
        updateTextElement(selectedTextId, { x, y })
      } else if (selectedLogoId) {
        const selectedLogo = logoElements.find((logo) => logo.id === selectedLogoId)
        if (selectedLogo) {
          updateLogoElement(selectedLogoId, {
            x: x - selectedLogo.width / 2,
            y: y - selectedLogo.height / 2,
          })
        }
      }
    },
    [selectedTextId, selectedLogoId, logoElements, config.width, config.height, updateTextElement, updateLogoElement, selfTestMode, config.ledWallMode, drawPattern, tempSelfTestModeRef, keyPressTimeoutRef, isNavigatingRef],
  )

  // Gestion de la navigation des panneaux Self-Test
  const navigatePanel = useCallback((direction: 'up' | 'down' | 'left' | 'right') => {
    if (!selfTestMode.enabled || !config.ledWallMode?.enabled) return false;

    const { columns, rows } = config.ledWallMode;
    const { selectedPanelX, selectedPanelY } = selfTestMode;

    let newX = selectedPanelX;
    let newY = selectedPanelY;

    switch (direction) {
      case 'up':
        newY = Math.max(1, selectedPanelY - 1);
        break;
      case 'down':
        newY = Math.min(rows, selectedPanelY + 1);
        break;
      case 'left':
        newX = Math.max(1, selectedPanelX - 1);
        break;
      case 'right':
        newX = Math.min(columns, selectedPanelX + 1);
        break;
    }

    if (newX !== selectedPanelX || newY !== selectedPanelY) {
      const panelKey = `${newX},${newY}`;

      let newActivePanels = selfTestMode.trailMode
        ? new Set(selfTestMode.activePanels)
        : new Set<string>();

      newActivePanels.add(panelKey);

      setSelfTestMode(prev => ({
        ...prev,
        selectedPanelX: newX,
        selectedPanelY: newY,
        activePanels: newActivePanels
      }));

      // Marquer la navigation pour éviter l'affichage de la mire
      isNavigatingRef.current = true;

      // Forcer un redessinage immédiat avec les nouvelles valeurs
      tempSelfTestModeRef.current = {
        ...selfTestMode,
        enabled: true, // S'assurer que le mode reste activé
        selectedPanelX: newX,
        selectedPanelY: newY,
        activePanels: newActivePanels,
        showBorders: selfTestMode.showBorders // Préserver l'état des bordures
      };

      // Redessiner immédiatement
      drawPattern();

      // Nettoyer le ref temporaire après un court délai
      keyPressTimeoutRef.current = window.setTimeout(() => {
        tempSelfTestModeRef.current = null;
        isNavigatingRef.current = false;
        keyPressTimeoutRef.current = null;
      }, 150);
    }

    return true;
  }, [selfTestMode, config.ledWallMode, drawPattern]);

  // Fonction de gestion des touches pour la navigation des panneaux
  const handleSelfTestKeyDown = useCallback((event: KeyboardEvent) => {
    // Empêcher les touches multiples simultanées
    if (isNavigatingRef.current && event.key.startsWith('Arrow')) {
      event.preventDefault();
      return true;
    }

    // Gestion de la touche Espace pour mettre en cyan
    if (event.key === ' ' || event.key === 'Space') {
      event.preventDefault();
      if (config.ledWallMode?.enabled && selfTestMode.enabled) {
        const panelKey = `${selfTestMode.selectedPanelX},${selfTestMode.selectedPanelY}`;

        setCyanPanels(prev => {
          const newCyanPanels = new Set(prev);
          const wasPresent = newCyanPanels.has(panelKey);

          if (wasPresent) {
            newCyanPanels.delete(panelKey);

            // Supprimer aussi toutes les zones rouges de ce panneau
            setRedZones(prevRedZones => {
              const newRedZones = new Set(prevRedZones);
              // Supprimer toutes les zones rouges de ce panneau (tous quadrants)
              ['tl', 'tr', 'bl', 'br'].forEach(quadrant => {
                newRedZones.delete(`${panelKey},${quadrant}`);
              });
              return newRedZones;
            });
          } else {
            newCyanPanels.add(panelKey);
          }

          // Mettre à jour les panneaux actifs selon l'état cyan
          setSelfTestMode(prevSelfTest => ({
            ...prevSelfTest,
            activePanels: wasPresent
              ? new Set([...prevSelfTest.activePanels].filter(p => p !== panelKey)) // Retirer si on supprime cyan
              : new Set([...prevSelfTest.activePanels, panelKey]) // Ajouter si on ajoute cyan
          }));

          // Synchroniser vers l'externe
          if (broadcastChannelRef.current) {
            broadcastChannelRef.current.postMessage({
              type: 'CYAN_PANELS_UPDATE',
              data: {
                cyanPanels: Array.from(newCyanPanels)
              }
            });

            // Synchroniser aussi les panneaux actifs
            const updatedActivePanels = wasPresent
              ? [...selfTestMode.activePanels].filter(p => p !== panelKey)
              : [...selfTestMode.activePanels, panelKey];

            broadcastChannelRef.current.postMessage({
              type: 'SELF_TEST_UPDATE',
              data: {
                ...selfTestMode,
                activePanels: updatedActivePanels
              }
            });

            // Si on supprime un panneau cyan, synchroniser aussi la suppression des zones rouges
            if (wasPresent) {
              setTimeout(() => {
                if (broadcastChannelRef.current) {
                  broadcastChannelRef.current.postMessage({
                    type: 'SUBDIVISION_UPDATE',
                    data: {
                      subdivisionMode,
                      redZones: Array.from(redZones).filter(zoneKey => !zoneKey.startsWith(panelKey + ',')),
                      selectedQuadrant
                    }
                  });
                }
              }, 10);
            }
          }

          return newCyanPanels;
        });

        return true;
      }
    }

    if (!selfTestMode.enabled || !config.ledWallMode?.enabled) return false;

    // Gestion de la touche Entrée pour mode PCB et zones rouges
    if (event.key === 'Enter') {
      event.preventDefault();

      if (!subdivisionMode) {
        // Si le mode PCB n'est pas activé, l'activer automatiquement
        setSubdivisionMode(true);

        // Synchroniser vers l'externe
        if (broadcastChannelRef.current) {
          broadcastChannelRef.current.postMessage({
            type: 'SUBDIVISION_UPDATE',
            data: {
              subdivisionMode: true,
              redZones: Array.from(redZones),
              selectedQuadrant
            }
          });
        }
      } else {
        // Mode subdivision déjà activé : gérer les zones rouges
        const panelKey = `${selfTestMode.selectedPanelX},${selfTestMode.selectedPanelY}`;

        // Vérifier que le panneau est cyan avant d'autoriser la zone rouge
        if (cyanPanels.has(panelKey)) {
          const zoneKey = `${selfTestMode.selectedPanelX},${selfTestMode.selectedPanelY},${selectedQuadrant}`;

          setRedZones(prev => {
            const newRedZones = new Set(prev);
            if (newRedZones.has(zoneKey)) {
              newRedZones.delete(zoneKey);
            } else {
              newRedZones.add(zoneKey);
            }

            // Synchroniser vers l'externe
            if (broadcastChannelRef.current) {
              broadcastChannelRef.current.postMessage({
                type: 'SUBDIVISION_UPDATE',
                data: {
                  subdivisionMode,
                  redZones: Array.from(newRedZones),
                  selectedQuadrant
                }
              });
            }

            return newRedZones;
          });
        }
      }

      return true;
    }

    // Navigation améliorée dans les quadrants en mode subdivision (touches Z, Q, S, D)
    if (subdivisionMode && cyanPanels.has(`${selfTestMode.selectedPanelX},${selfTestMode.selectedPanelY}`)) {
      let newQuadrant: 'tl' | 'tr' | 'bl' | 'br' | null = null;

      switch (event.key.toLowerCase()) {
        case 'q': // Quadrant top-left (haut-gauche)
          newQuadrant = 'tl';
          break;
        case 'z': // Quadrant top-right (haut-droite) - Z est à droite de Q sur QWERTY/AZERTY
          newQuadrant = 'tr';
          break;
        case 's': // Quadrant bottom-left (bas-gauche) - S est sous Q
          newQuadrant = 'bl';
          break;
        case 'd': // Quadrant bottom-right (bas-droite) - D est sous Z
          newQuadrant = 'br';
          break;
      }

      if (newQuadrant && newQuadrant !== selectedQuadrant) {
        event.preventDefault();
        setSelectedQuadrant(newQuadrant);


        // Synchroniser vers l'externe
        if (broadcastChannelRef.current) {
          broadcastChannelRef.current.postMessage({
            type: 'SUBDIVISION_UPDATE',
            data: {
              subdivisionMode,
              redZones: Array.from(redZones),
              selectedQuadrant: newQuadrant
            }
          });
        }

        // Forcer un redraw immédiat pour voir le changement de quadrant
        drawPattern();

        return true;
      }

      // Si on appuie sur la même touche, on l'affiche quand même pour le feedback visuel
      if (newQuadrant === selectedQuadrant) {
        event.preventDefault();
        drawPattern(); // Redraw pour montrer la sélection actuelle
        return true;
      }
    }

    let navigationOccurred = false;
    switch (event.key) {
      case 'ArrowUp':
        navigationOccurred = navigatePanel('up');
        break;
      case 'ArrowDown':
        navigationOccurred = navigatePanel('down');
        break;
      case 'ArrowLeft':
        navigationOccurred = navigatePanel('left');
        break;
      case 'ArrowRight':
        navigationOccurred = navigatePanel('right');
        break;
    }

    if (navigationOccurred) {
      event.preventDefault();
      return true;
    }

    return false;
  }, [selfTestMode, config.ledWallMode, navigatePanel]);

  // Keyboard event handler
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ignorer les événements si l'utilisateur tape dans un champ de saisie
      const target = event.target as HTMLElement
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return
      }

      // Priorité absolue au Self-Test (fonctionne même en plein écran)
      if (handleSelfTestKeyDown(event)) return;

      // En plein écran, on autorise seulement les touches utiles (Snake est géré par useSnakeGame)
      if (isFullscreen) {
        // En mode plein écran, pas de gestion des flèches pour les éléments sélectionnés
        // Snake et Self-Test sont gérés ailleurs
        return;
      }

      // Touche ECHAP pour désélectionner
      if (event.key === "Escape") {
        if (selectedTextId || selectedLogoId) {
          event.preventDefault()
          setSelectedTextId(null)
          setSelectedLogoId(null)
          setShowTextPanel(false)
          setShowLogoPanel(false)
          setIsDragging(false)
          dragStateRef.current = null
          setCursorStyle('default')
          return
        }
      }

      // Mode normal : gestion des éléments sélectionnés
      if (!selectedTextId && !selectedLogoId) return;

      const step = event.shiftKey ? 10 : 1

      switch (event.key) {
        case "ArrowUp":
          event.preventDefault()
          moveElementWithArrows("up", step)
          break
        case "ArrowDown":
          event.preventDefault()
          moveElementWithArrows("down", step)
          break
        case "ArrowLeft":
          event.preventDefault()
          moveElementWithArrows("left", step)
          break
        case "ArrowRight":
          event.preventDefault()
          moveElementWithArrows("right", step)
          break
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [selectedTextId, selectedLogoId, isFullscreen, moveElementWithArrows, handleSelfTestKeyDown]);

  // Other functions remain the same...
  const toggleAnimation = useCallback(() => {

    // Si on est dans une session active et qu'on est Remote (télécommande)
    if (isInSession && isSessionRemote) {
      // Envoyer la commande au Server
      const action = isAnimating ? 'STOP' : 'START';

      // console.log('🎬 [TOGGLE_ANIMATION_REMOTE] Remote envoie ANIMATION_COMMAND:', {
      //  action,
      //  isAnimating,
      //  isSessionRemote,
      //  hasSendSyncMessage: !!sendSyncMessage
      // });
      sendSyncMessage('ANIMATION_COMMAND', { action });
      // console.log('🎬 [TOGGLE_ANIMATION_REMOTE] Message envoyé');
      return;
    }

    // Mode normal (pas en session ou en mode Master)
    if (!isAnimating) {
      startTimeRef.current = Date.now()
      setIsAnimating(true)

      // Synchroniser vers l'affichage externe
      if (broadcastChannelRef.current && isExternalDisplayConnected) {
        broadcastChannelRef.current.postMessage({
          type: 'ANIMATION_STATE_UPDATE',
          data: { isAnimating: true }
        });
      } else if (!isExternalDisplayConnected) {
      }
    } else {
      setIsAnimating(false)
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }

      // Synchroniser vers l'affichage externe
      if (broadcastChannelRef.current && isExternalDisplayConnected) {
        broadcastChannelRef.current.postMessage({
          type: 'ANIMATION_STATE_UPDATE',
          data: { isAnimating: false }
        });
      }
    }
  }, [isAnimating, isExternalDisplayConnected, config.patternType, activePatternType, isSessionRemote, isInSession, isSessionServer, sendSyncMessage])

  // Fonction pour réinitialiser l'animation
  const resetAnimation = useCallback(async () => {
    // Afficher une confirmation avant de réinitialiser
    let shouldReset = false;
    
    if (isTauriApp) {
      // En mode Tauri, utiliser la boîte de dialogue native Tauri
      try {
        const { ask } = await import('@tauri-apps/plugin-dialog');
        shouldReset = await ask(t("confirm.resetAll"), {
          title: t("confirm.title") || "Confirmation",
          kind: 'warning'
        });
      } catch (error) {
        // Fallback sur window.confirm si l'API Tauri échoue
        shouldReset = window.confirm(t("confirm.resetAll"));
      }
    } else {
      // Web: utiliser window.confirm
      shouldReset = window.confirm(t("confirm.resetAll"));
    }
    
    if (!shouldReset) {
      return;
    }

    setIsAnimating(false);
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    resetMainConfig();

    // Réinitialiser le switch Easy/Full en mode Full
    setIsEasyMode(false);

    // Réinitialiser les textes et logos
    setTextElements([]);
    setLogoElements([]);
    setSelectedTextId(null);
    setSelectedLogoId(null);
    setShowTextPanel(false);
    setShowLogoPanel(false);

    // Réinitialiser le mode Self-Test
    resetSelfTest();

    // Réinitialiser les connexions Nova
    setNovaPanelConnections([]);
    setSelectedNovaPort(null);
    novaPanelConnectionsRef.current = [];

    // Réinitialiser les états d'animation
    setIsAnimating(false);
    startTimeRef.current = Date.now();

    drawPattern(0, true);

    // Synchroniser la réinitialisation vers l'écran externe
    if (broadcastChannelRef.current) {
      broadcastChannelRef.current.postMessage({
        type: 'CONFIG_UPDATE',
        data: config
      });
    }
  }, [t, drawPattern, resetSelfTest, isTauriApp, resetMainConfig, config]);

  const downloadPattern = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    // Redraw without selection indicators
    drawPattern(0, false)

    // Nouvelle nomenclature: {Résolution}_{Res panneau}_VisualGrid_{JJ-MM-YYYY}_{HH-MM-SS}.png
    const now = new Date();
    const dateStr = `${String(now.getDate()).padStart(2, '0')}-${String(now.getMonth() + 1).padStart(2, '0')}-${now.getFullYear()}`;
    const timeStr = `${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(now.getSeconds()).padStart(2, '0')}`;
    const panelW = config.ledWallMode?.enabled ? config.ledWallMode.panelWidth : config.gridSize;
    const panelH = config.ledWallMode?.enabled ? config.ledWallMode.panelHeight : config.gridSize;

    const link = document.createElement("a")
    link.download = `${config.width}x${config.height}_${panelW}x${panelH}_VisualGrid_${dateStr}_${timeStr}.png`
    link.href = canvas.toDataURL()
    link.click()

    // Redraw with selection indicators
    drawPattern(0, true)
  }, [config, drawPattern])

  const updateConfig = useCallback((key: string, value: any) => {

    setConfig((prev) => {
      const newConfig = { ...prev, [key]: value };

      // Synchroniser vers l'affichage externe
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.postMessage({
          type: 'CONFIG_UPDATE',
          data: { ...newConfig, activePatternType }
        });
      } else {
      }

      return newConfig;
    });
  }, [activePatternType])

  const applyPreset = useCallback((preset: any) => {
    if (preset.name !== "Custom") {
      setConfig((prev) => {
        const newConfig = { ...prev, width: preset.width, height: preset.height };

        // Synchroniser vers l'affichage externe
        if (broadcastChannelRef.current) {
          broadcastChannelRef.current.postMessage({
            type: 'CONFIG_UPDATE',
            data: { ...newConfig, activePatternType }
          });
        }

        return newConfig;
      });
    }
    setSelectedPreset(preset.name);
  }, [activePatternType])

  // Handler pour ouvrir le dialogue d'export
  const handleExportLoopVideo = useCallback(() => {
    // TODO: Re-enable Tauri/Web check after debugging
    setShowExportDialog(true);
  }, []);

  // Handler pour l'export WebCodecs avec settings personnalisés (depuis le dialogue)
  const handleWebCodecsExportWithSettings = useCallback(async (options: {
    fps: number;
    duration: number;
    outputFormat: 'MP4' | 'MOV' | 'ProRes' | 'WebM';
  }) => {
    if (!isWebCodecsSupported) {
      toast.error(t('toast.webCodecsNotSupported'), {
        description: t('toast.webCodecsNotSupportedDesc')
      });
      return;
    }

    // For Gradient pattern, apply optimal settings for perfect loop
    const isGradient = config.patternType?.toLowerCase() === 'gradient';
    
    if (isGradient) {
      const durationInSeconds = options.duration / 1000;
      const optimalSpeed = 10 / durationInSeconds;

      // Calculate sonarSpeed to ensure EXACT integer cycles (no frame drift)
      const sonarInterval = 4000; // milliseconds
      const numSonarCycles = Math.max(1, Math.floor(options.duration / sonarInterval));
      const baseSonarSpeed = (numSonarCycles * sonarInterval) / options.duration;

      // Pour 10s : ralentir en divisant la vitesse par 2 (sonar 2x plus lent)
      // Pour 3s et 5s : vitesse normale
      const optimalSonarSpeed = durationInSeconds === 10 ? baseSonarSpeed / 2 : baseSonarSpeed;

      updateConfig('animationSpeed', optimalSpeed);
      updateConfig('sonarSpeed', optimalSonarSpeed);

      // Wait for config to be applied before starting export
      await new Promise(resolve => setTimeout(resolve, 150));
    }

    const result = await startWebCodecsExport({
      duration: options.duration,
      fps: options.fps,
      outputFormat: options.outputFormat,
      loopDuration: options.loopDuration,
      fpsForFilename: options.fpsForFilename,
    });

    if (result.success) {
      // ✅ PostHog: Track video export
      captureEvent('grid:export_video', {
        resolution: `${config.width}x${config.height}`,
        pattern_type: config.patternType,
        standard_pattern_type: config.standardPatternType,
        led_wall_enabled: config.ledWallMode?.enabled,
        led_wall_columns: config.ledWallMode?.columns,
        led_wall_rows: config.ledWallMode?.rows,
        is_animating: isAnimating,
        fps: options.fps,
        duration: options.duration,
        output_format: options.outputFormat,
        text_elements_count: textElements.length,
        logo_elements_count: logoElements.length,
      });
    } else {
      toast.error(t('toast.exportError').replace('{error}', result.error || ''));
    }
  }, [isWebCodecsSupported, startWebCodecsExport, config.patternType, updateConfig]);

  // Effects
  useEffect(() => {
    if (isAnimating) {
      // Ne pas réinitialiser startTimeRef ici pour éviter de redémarrer l'animation
      animationRef.current = requestAnimationFrame(animateLoop)
    } else if (animationRef.current) {
      cancelAnimationFrame(animationRef.current)
    }
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [isAnimating, animateLoop])

  useEffect(() => {
    if (!isAnimating) {
      // Délai pour éviter les conflits avec les changements de config des mires standards
      const delay = activePatternType === 'standard' ? 50 : 0;

      setTimeout(() => {
        drawPattern(0, true)
      }, delay);
    }
  }, [config, drawPattern, isAnimating, textElements, logoElements, snakeGameState, activePatternType])

  // Calculer les facteurs d'échelle du canvas pour positionner les GIF
  useEffect(() => {
    const updateCanvasScale = () => {
      const canvas = canvasRef.current
      if (!canvas) return
      
      const canvasRect = canvas.getBoundingClientRect()
      const scaleX = canvasRect.width / canvas.width
      const scaleY = canvasRect.height / canvas.height
      setCanvasScale({ scaleX, scaleY })
    }

    updateCanvasScale()
    
    // Mettre à jour lors du redimensionnement
    window.addEventListener('resize', updateCanvasScale)
    const interval = setInterval(updateCanvasScale, 100) // Mise à jour périodique pour gérer les changements de taille
    
    return () => {
      window.removeEventListener('resize', updateCanvasScale)
      clearInterval(interval)
    }
  }, [config.width, config.height, isFullscreen, config.stretchFullscreen])



  useEffect(() => {
    drawPattern(0, true) // Show selection on initial draw
  }, [])

  // Arrêter l'animation automatiquement uniquement pour les patterns qui n'en ont pas besoin
  useEffect(() => {
    // Liste des patterns standards qui nécessitent une animation
    const animatedStandardPatterns = [
      'liveVHScroll',
      'liveVScroll',
      'lineHScroll',
      'movingBall'
    ];

    // Arrêter l'animation seulement pour les couleurs unies ou les patterns standard NON animés
    // MAIS garder l'animation si le sonar est activé
    if ((activePatternType === 'solid' && !config.showSonar) ||
      (activePatternType === 'standard' &&
        config.standardPatternType !== 'none' &&
        !animatedStandardPatterns.includes(config.standardPatternType))) {
      if (isAnimating) {
        setIsAnimating(false);
      }
    }
    // Démarrer l'animation automatiquement pour les mires standard animées
    else if (activePatternType === 'standard' &&
      config.standardPatternType !== 'none' &&
      animatedStandardPatterns.includes(config.standardPatternType)) {
      if (!isAnimating) {
        setIsAnimating(true);
        startTimeRef.current = Date.now();
      }
    }
  }, [activePatternType, isAnimating, config.standardPatternType])

  // Gestion automatique de l'affichage UI pour les mires standards
  useEffect(() => {
    const isStandardPatternActive = activePatternType === 'standard' && config.standardPatternType !== 'none';


    if (isStandardPatternActive) {
      // Passer en mode mires standards : sauvegarder et désactiver
      if (previousUIStates === null) {

        // Sauvegarder les états actuels
        const currentUIStates = {
          showTechnicalDetails: config.showTechnicalDetails,
          showGrid: config.showGrid,
          showLogo: config.showLogo,
          showLEDNumbers: config.showLEDNumbers,
          showResolution: config.showResolution,
          showSonar: config.showSonar
        };
        setPreviousUIStates(currentUIStates);

        // Batch update pour éviter les conflits (désactiver détails techniques sauf résolution)
        setConfig(prev => ({
          ...prev,
          showTechnicalDetails: false,
          showGrid: false,
          showLogo: false,
          showLEDNumbers: false,
          showResolution: true, // Garder résolution activée pour les mires
          showSonar: false
        }));

        // Synchroniser manuellement car on n'utilise pas updateConfig
        if (broadcastChannelRef.current) {
          broadcastChannelRef.current.postMessage({
            type: 'CONFIG_UPDATE',
            data: {
              ...config,
              activePatternType,
              showTechnicalDetails: false,
              showGrid: false,
              showLogo: false,
              showLEDNumbers: false,
              showResolution: true,
              showSonar: false
            }
          });
        }
      }
    } else {
      // Sortir du mode mires standards : restaurer les valeurs précédentes
      if (previousUIStates !== null) {

        // Batch update pour éviter les conflits
        setConfig(prev => ({
          ...prev,
          showTechnicalDetails: previousUIStates.showTechnicalDetails,
          showGrid: previousUIStates.showGrid,
          showLogo: previousUIStates.showLogo,
          showLEDNumbers: previousUIStates.showLEDNumbers,
          showResolution: previousUIStates.showResolution,
          showSonar: previousUIStates.showSonar
        }));

        // Synchroniser manuellement
        if (broadcastChannelRef.current) {
          broadcastChannelRef.current.postMessage({
            type: 'CONFIG_UPDATE',
            data: {
              ...config,
              activePatternType,
              showTechnicalDetails: previousUIStates.showTechnicalDetails,
              showGrid: previousUIStates.showGrid,
              showLogo: previousUIStates.showLogo,
              showLEDNumbers: previousUIStates.showLEDNumbers,
              showResolution: previousUIStates.showResolution,
              showSonar: previousUIStates.showSonar
            }
          });
        }

        // Effacer la sauvegarde
        setPreviousUIStates(null);
      }
    }
  }, [activePatternType, config.standardPatternType]);

  // Initialize Snake Game hook
  const width = typeof config.width === 'number' && isFinite(config.width) ? config.width : 1920;
  const height = typeof config.height === 'number' && isFinite(config.height) ? config.height : 1080;
  const gridSize = typeof config.gridSize === 'number' && isFinite(config.gridSize) ? config.gridSize : 192;

  const snakeGameControls = useSnakeGame({
    gridWidth: width,
    gridHeight: height,
    gridSize,
    onGameStateChange: setSnakeGameState,
    gameSpeed: Math.max(150, 500 - config.animationSpeed * 200) // Vitesse raisonnable et contrôlable
  });

  // Force redraw when snake game state changes
  useEffect(() => {
    if (config.patternType === "snake" && snakeGameState) {
      drawPattern(0, true);
    }
  }, [snakeGameState, config.patternType, drawPattern]);

  // Auto-disable grid, logo, and sonar when Snake mode is selected
  useEffect(() => {
    if (config.patternType === "snake") {
      setConfig(prev => ({
        ...prev,
        showTechnicalDetails: false,
        showGrid: false,
        showLEDNumbers: false,
        showResolution: false,
        showLogo: false,
        showSonar: false,
        showDefaultLogo: false
      }));
      // Auto-start animation when Snake mode is selected
      if (!isAnimating) {
        setIsAnimating(true);
        startTimeRef.current = Date.now();
      }
    }
  }, [config.patternType]);

  // Fullscreen event listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }

    document.addEventListener("fullscreenchange", handleFullscreenChange)
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange)
  }, [])

  // Charger le logo par défaut et l'icône light
  const loadDefaultLogo = () => {
    const logo = new Image();
    logo.onload = () => {
      defaultLogoRef.current = logo;
      drawPattern(0, true);
    };

    // Déterminer le chemin correct selon l'environnement
    let logoPath;
    if (typeof window !== 'undefined' && window.location.protocol === 'file:') {
      logoPath = 'icon_full_res.png';
    } else {
      logoPath = '/icon_full_res.png';
    }

    logo.src = logoPath;
  };

  const loadIconLight = () => {
    const icon = new Image();
    icon.onload = () => {
      iconLightRef.current = icon;
      drawPattern(0, true);
    };

    // Déterminer le chemin correct selon l'environnement
    let iconPath;
    if (typeof window !== 'undefined' && window.location.protocol === 'file:') {
      iconPath = 'icon_full_res_light.png';
    } else {
      iconPath = '/icon_full_res_light.png';
    }

    icon.src = iconPath;
  };

  // Charger le logo et l'icône au montage du composant
  useEffect(() => {
    loadDefaultLogo();
    loadIconLight();
  }, []);

  const selectedText = textElements.find((text) => text.id === selectedTextId)
  const selectedLogo = logoElements.find((logo) => logo.id === selectedLogoId)

  // Effet pour empêcher le défilement en mode plein écran
  useEffect(() => {
    if (isFullscreen && !config.allowFullscreenScroll) {
      // Désactiver le défilement du corps de la page seulement si l'option est désactivée
      document.body.style.overflow = 'hidden';
      // Réinitialiser la position de défilement
      window.scrollTo(0, 0);
    } else {
      // Réactiver le défilement normal
      document.body.style.overflow = '';
    }

    return () => {
      // Réactiver le défilement normal lorsque le composant est démonté
      document.body.style.overflow = '';
    };
  }, [isFullscreen, config.allowFullscreenScroll]);

  // Fonction pour exporter la configuration actuelle sous forme de fichier .visualgrid
  const handleExportConfigFile = async () => {
    try {
      // Importer les utilitaires de compatibilité
      const { convertClassicToUnified } = await import('@/lib/config-compatibility');

      // Préparer la configuration classique avec TOUS les paramètres
      const classicConfig = {
        version: "2.0.0",
        timestamp: new Date().toISOString(),
        canvas: config,
        textElements,
        logoElements: logoElements.map(logo => ({
          id: logo.id,
          x: logo.x,
          y: logo.y,
          width: logo.width,
          height: logo.height,
          opacity: logo.opacity,
          // Convertir l'image en data URL
          dataUrl: logo.isGif ? logo.imageSrc : logo.image.src,
          isGif: logo.isGif,
          imageSrc: logo.imageSrc
        })),
        // Nouveaux paramètres Self-Test
        selfTestMode: {
          ...selfTestMode,
          activePanels: Array.from(selfTestMode.activePanels) // Convertir Set en Array
        },
        cyanPanels: Array.from(cyanPanels), // Convertir Set en Array
        subdivisionMode,
        redZones: Array.from(redZones), // Convertir Set en Array
        selectedQuadrant,
        // États d'animation et d'interface
        isAnimating,
        showTextPanel,
        showLogoPanel
      };

      // Convertir vers le format unifié v3.0.0
      const fullConfig = convertClassicToUnified(classicConfig);

      // Générer un nom de fichier basé sur la date et la résolution
      const filename = `visual-grid-${config.width}x${config.height}-${new Date().toISOString().slice(0, 10)}.visualgrid`;

      const result = await exportConfigFile(fullConfig, filename);

      if (result.success) {
        toast.success(t('toast.configExported'));

        // Compter immédiatement lors de l'exportation de la configuration (action utilisateur délibérée)
        gridCounter.forceCount();
      } else {
        toast.error(t("toast.exportError", { error: result.error }));
      }
    } catch (error) {
      toast.error(t('toast.configExportError'));
    }
  }

  // Fonction pour importer une configuration depuis un fichier .visualgrid uniquement
  const handleImportConfigFile = async () => {
    try {
      const result = await importConfigFile('visualgrid')
      if (result && result.success && result.config) {
        const importedConfig = result.config;

        // Importer les utilitaires de compatibilité
        const { detectSaveFormat, convertToUnified, convertUnifiedToClassic, validateUnifiedConfig, ensureCompleteClassicConfig } = await import('@/lib/config-compatibility');

        // Détecter le format de la configuration
        const format = detectSaveFormat(importedConfig);

        try {
          // Convertir vers le format unifié
          const unifiedConfig = convertToUnified(importedConfig);

          // Valider la configuration unifiée
          if (!validateUnifiedConfig(unifiedConfig)) {
            throw new Error("Configuration unifiée invalide");
          }

          // Convertir vers le format classique
          const classicConfig = convertUnifiedToClassic(unifiedConfig);

          // S'assurer que la configuration a une structure complète
          const configToImport = {
            ...classicConfig,
            canvas: ensureCompleteClassicConfig(classicConfig.canvas)
          };

          // Vérifier le format de la configuration (logique existante conservée)
          if (configToImport.version && configToImport.version.startsWith('2.')) {
            // Nouveau format (v2.0.0+) avec tous les paramètres

            // Importer la configuration canvas principale
            if (configToImport.canvas) {
              setConfig(configToImport.canvas);
            }

            // Importer les éléments de texte
            if (configToImport.textElements) {
              setTextElements(configToImport.textElements);
            }

            // Importer les éléments de logo avec reconstruction des images
            if (configToImport.logoElements && Array.isArray(configToImport.logoElements)) {
              const reconstructedLogos = configToImport.logoElements.map((logoData: any) => {
                const img = new Image();
                const imageSrc = logoData.dataUrl || logoData.image || logoData.imageSrc; // Compatibilité ancien/nouveau format
                img.src = imageSrc;
                img.width = logoData.width || 100;
                img.height = logoData.height || 100;

                return {
                  id: logoData.id,
                  x: logoData.x,
                  y: logoData.y,
                  width: logoData.width,
                  height: logoData.height,
                  opacity: logoData.opacity || 1,
                  image: img,
                  originalWidth: logoData.originalWidth || logoData.width || 100,
                  originalHeight: logoData.originalHeight || logoData.height || 100,
                  isGif: logoData.isGif || false,
                  imageSrc: logoData.isGif ? imageSrc : undefined
                };
              });
              setLogoElements(reconstructedLogos);
            }

            // Importer le mode Self-Test
            if (configToImport.selfTestMode) {
              setSelfTestMode({
                ...configToImport.selfTestMode,
                activePanels: new Set(configToImport.selfTestMode.activePanels || [])
              });
            }

            // Importer les panneaux cyan
            if (configToImport.cyanPanels) {
              setCyanPanels(new Set(configToImport.cyanPanels));
            }

            // Importer le mode subdivision
            if (typeof configToImport.subdivisionMode === 'boolean') {
              setSubdivisionMode(configToImport.subdivisionMode);
            }

            // Importer les zones rouges
            if (configToImport.redZones) {
              setRedZones(new Set(configToImport.redZones));
            }

            // Importer le quadrant sélectionné
            if (configToImport.selectedQuadrant) {
              setSelectedQuadrant(configToImport.selectedQuadrant);
            }

            // Importer les états d'interface
            if (typeof configToImport.isAnimating === 'boolean') {
              setIsAnimating(configToImport.isAnimating);
            }
            if (typeof configToImport.showTextPanel === 'boolean') {
              setShowTextPanel(configToImport.showTextPanel);
            }
            if (typeof configToImport.showLogoPanel === 'boolean') {
              setShowLogoPanel(configToImport.showLogoPanel);
            }

          } else {
            // Ancien format (v1.x) - compatibilité rétroactive

            if (configToImport.canvas) {
              setConfig(configToImport.canvas);
            } else {
              // Très ancien format - la config était directement dans result.config
              setConfig(configToImport);
            }

            // Importer les anciens éléments si disponibles
            if (configToImport.textElements) {
              setTextElements(configToImport.textElements);
            }
            if (configToImport.logoElements) {
              const reconstructedLogos = configToImport.logoElements.map((logoData: any) => {
                const img = new Image();
                const imageSrc = logoData.dataUrl || logoData.image || logoData.imageSrc;
                img.src = imageSrc;
                return { 
                  ...logoData, 
                  image: img,
                  originalWidth: logoData.originalWidth || logoData.width || 100,
                  originalHeight: logoData.originalHeight || logoData.height || 100,
                  isGif: logoData.isGif || false,
                  imageSrc: logoData.isGif ? imageSrc : undefined
                };
              });
              setLogoElements(reconstructedLogos);
            }

            // Réinitialiser les nouveaux paramètres pour éviter les conflits
            setSelfTestMode({
              enabled: false,
              selectedPanelX: 1,
              selectedPanelY: 1,
              trailMode: false,
              showBorders: true,
              activePanels: new Set<string>()
            });
            setCyanPanels(new Set<string>());
            setSubdivisionMode(false);
            setRedZones(new Set<string>());
            setSelectedQuadrant('tl');
          }

          // Synchroniser vers l'écran externe si connecté
          if (broadcastChannelRef.current && isExternalDisplayConnected) {
            setTimeout(() => {
              syncAllToExternal();
            }, 500);
          }

          // Message de succès avec information sur l'origine
          const sourceInfo = unifiedConfig.sourceMode === "multigrid"
            ? "depuis une configuration multigrid"
            : "depuis une configuration classique";

          toast.success(t('toast.configImportedFrom').replace('{source}', sourceInfo));

          // Compter immédiatement lors de l'importation d'une configuration (action utilisateur délibérée)
          gridCounter.forceCount();

        } catch (conversionError) {
          toast.error(t('toast.configImportFormatError').replace('{format}', format));
        }
      } else if (result && !result.success) {
        toast.error(result.error || t('toast.configImportError'))
      }
    } catch (error) {
      toast.error(t('toast.configImportError'))
    }
  }

  // Cache déplacé avant drawPattern

  // Variables pour WebGL
  const [pulseGLContext, setPulseGLContext] = useState<WebGLRenderingContext | null>(null);
  const [pulseGLProgram, setPulseGLProgram] = useState<{
    program: WebGLProgram;
    attribLocations: {
      position: number;
      texCoord: number;
    };
    uniformLocations: {
      resolution: WebGLUniformLocation | null;
      color: WebGLUniformLocation | null;
      center: WebGLUniformLocation | null;
      time: WebGLUniformLocation | null;
    };
    buffers: {
      position: WebGLBuffer;
      texCoord: WebGLBuffer;
      indices: WebGLBuffer;
    };
  } | null>(null);

  // Fonctions utilitaires pour WebGL
  const createShader = useCallback((gl: WebGLRenderingContext, type: number, source: string): WebGLShader | null => {
    const shader = gl.createShader(type);
    if (!shader) return null;

    gl.shaderSource(shader, source);
    gl.compileShader(shader);

    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      gl.deleteShader(shader);
      return null;
    }

    return shader;
  }, []);

  const createProgram = useCallback((gl: WebGLRenderingContext, vertexShader: WebGLShader | null, fragmentShader: WebGLShader | null): WebGLProgram | null => {
    if (!vertexShader || !fragmentShader) return null;

    const program = gl.createProgram();
    if (!program) return null;

    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      gl.deleteProgram(program);
      return null;
    }

    return program;
  }, []);

  const initBuffers = useCallback((gl: WebGLRenderingContext) => {
    // Créer un buffer pour les positions des sommets
    const positionBuffer = gl.createBuffer();
    if (!positionBuffer) return null;
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);

    // Positions pour un rectangle qui couvre tout le canvas
    const positions = [
      0, 0,
      gl.canvas.width, 0,
      gl.canvas.width, gl.canvas.height,
      0, gl.canvas.height,
    ];
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(positions), gl.STATIC_DRAW);

    // Créer un buffer pour les coordonnées de texture
    const texCoordBuffer = gl.createBuffer();
    if (!texCoordBuffer) return null;
    gl.bindBuffer(gl.ARRAY_BUFFER, texCoordBuffer);

    // Coordonnées de texture pour le rectangle
    const texCoords = [
      0.0, 0.0,
      1.0, 0.0,
      1.0, 1.0,
      0.0, 1.0,
    ];
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(texCoords), gl.STATIC_DRAW);

    // Créer un buffer pour les indices
    const indexBuffer = gl.createBuffer();
    if (!indexBuffer) return null;
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, indexBuffer);

    // Indices pour dessiner le rectangle avec deux triangles
    const indices = [
      0, 1, 2,
      0, 2, 3,
    ];
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(indices), gl.STATIC_DRAW);

    return {
      position: positionBuffer,
      texCoord: texCoordBuffer,
      indices: indexBuffer
    };
  }, []);

  // Fonction pour vérifier si le rendu est possible avec la configuration actuelle
  const checkRenderability = (configToCheck: typeof config) => {
    const { ledWallMode } = configToCheck;

    // Si ledWallMode n'existe pas ou n'a pas les propriétés requises, ignorer la vérification
    if (!ledWallMode || !ledWallMode.columns || !ledWallMode.rows || !ledWallMode.panelWidth || !ledWallMode.panelHeight) {
      setRenderError(null);
      return;
    }

    const totalPixels = ledWallMode.columns * ledWallMode.rows * ledWallMode.panelWidth * ledWallMode.panelHeight;

    if (totalPixels > MAX_PIXELS) {
      setRenderError(t("config.renderError", { maxPixels: (MAX_PIXELS / 1000000).toFixed(0) }));
    } else if (ledWallMode.columns < MIN_COLUMNS_ROWS || ledWallMode.rows < MIN_COLUMNS_ROWS) {
      setRenderError(t("config.minDimensionsError"));
    } else {
      setRenderError(null);
    }
  };

  useEffect(() => {
    if (canvasRef.current && config.patternType !== "solid" && config.patternType !== "standard") {
      // Supprimer l'incrémentation automatique au chargement
      return () => {
        if (animationRef.current !== null) {
          cancelAnimationFrame(animationRef.current)
        }
      }
    }
  }, [config.patternType, mounted])

  // Ajouter un effet pour détecter les modifications de la configuration
  const isFirstConfigChange = useRef(true);

  useEffect(() => {
    // Si c'est le premier rendu, démarrer le timer de comptage
    if (isFirstConfigChange.current) {
      isFirstConfigChange.current = false;
      gridCounter.onActivity(true); // true = génération initiale
      return;
    }

    // Si ce n'est pas le premier rendu, c'est une modification
    gridCounter.onActivity(false); // false = modification
  }, [
    config.width,
    config.height,
    config.gridSize,
    config.patternType,
    config.showGrid,
    config.showLogo,
    config.showSonar,
    config.standardPatternType,
    gridCounter
  ]);

  // Helpers pour coordonnées souris -> canvas
  const getCanvasCoordinates = useCallback((event: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (!canvas) return { x: 0, y: 0 }
    const width = typeof config.width === 'number' && isFinite(config.width) ? config.width : 1920
    const height = typeof config.height === 'number' && isFinite(config.height) ? config.height : 1080
    const rect = canvas.getBoundingClientRect()
    const scaleX = width / rect.width
    const scaleY = height / rect.height
    const x = (event.clientX - rect.left) * scaleX
    const y = (event.clientY - rect.top) * scaleY
    return { x, y }
  }, [config.width, config.height])

  // Hit test texte
  const hitTestText = useCallback((x: number, y: number): string | null => {
    const canvas = canvasRef.current
    if (!canvas) return null
    const ctx = canvas.getContext('2d')
    if (!ctx) return null
    // Parcourir du haut vers le bas (dernier ajouté en dernier dessiné)
    for (let i = textElements.length - 1; i >= 0; i--) {
      const t = textElements[i]
      // Ignorer les textes vides
      if (!t.text || t.text.trim() === '') continue

      ctx.font = `${t.fontStyle} ${t.fontWeight} ${t.fontSize}px ${t.fontFamily}`
      const metrics = ctx.measureText(t.text)
      const textWidth = metrics.width
      const textHeight = t.fontSize

      // Calculer la zone de hit selon l'alignement
      let rectX = t.x
      if (t.textAlign === 'center') {
        rectX = t.x - textWidth / 2
      } else if (t.textAlign === 'right') {
        rectX = t.x - textWidth
      }

      const rectY = t.y
      const padding = 5 // Zone de padding pour faciliter la sélection

      // Test de collision avec padding
      if (x >= rectX - padding &&
        x <= rectX + textWidth + padding &&
        y >= rectY - padding &&
        y <= rectY + textHeight + padding) {
        return t.id
      }
    }
    return null
  }, [textElements])

  // Hit test logo
  const hitTestLogo = useCallback((x: number, y: number): string | null => {
    const padding = 5 // Zone de padding pour faciliter la sélection
    for (let i = logoElements.length - 1; i >= 0; i--) {
      const lg = logoElements[i]
      // Test de collision avec padding
      if (x >= lg.x - padding &&
        x <= lg.x + lg.width + padding &&
        y >= lg.y - padding &&
        y <= lg.y + lg.height + padding) {
        return lg.id
      }
    }
    return null
  }, [logoElements])

  // Mouse handlers pour sélection/drag & drop
  const handleCanvasMouseDown = useCallback((event: React.MouseEvent<HTMLCanvasElement>) => {
    // Bloquer toute interaction pendant l'export vidéo
    if (isWebCodecsExporting) {
      return
    }

    const { x, y } = getCanvasCoordinates(event)
    // Priorité au texte (dessiné au-dessus des logos)
    const textId = hitTestText(x, y)
    if (textId) {
      // Si le texte est déjà sélectionné, on initialise le drag uniquement
      // Sinon, on le sélectionne sans initialiser le drag
      const wasAlreadySelected = selectedTextId === textId

      setSelectedTextId(textId)
      setSelectedLogoId(null)
      setShowTextPanel(true)
      setShowLogoPanel(false)

      // Initialiser le drag seulement si l'élément était déjà sélectionné
      if (wasAlreadySelected) {
        const t = textElements.find(tt => tt.id === textId)!

        dragStateRef.current = {
          type: 'text',
          id: textId,
          startMouseX: x,
          startMouseY: y,
          startX: t.x,
          startY: t.y,
          hasStartedDragging: false,
        }
      } else {
        dragStateRef.current = null
      }
      // Ne pas démarrer le drag immédiatement, attendre un mouvement significatif
      return
    }
    const logoId = hitTestLogo(x, y)
    if (logoId) {
      // Si le logo est déjà sélectionné, on initialise le drag uniquement
      // Sinon, on le sélectionne sans initialiser le drag
      const wasAlreadySelected = selectedLogoId === logoId

      setSelectedLogoId(logoId)
      setSelectedTextId(null)
      setShowLogoPanel(true)
      setShowTextPanel(false)

      // Initialiser le drag seulement si l'élément était déjà sélectionné
      if (wasAlreadySelected) {
        const lg = logoElements.find(l => l.id === logoId)!

        dragStateRef.current = {
          type: 'logo',
          id: logoId,
          startMouseX: x,
          startMouseY: y,
          startX: lg.x,
          startY: lg.y,
          hasStartedDragging: false,
        }
      } else {
        dragStateRef.current = null
      }
      // Ne pas démarrer le drag immédiatement, attendre un mouvement significatif
      return
    }
    // Clic à côté: désélection
    setSelectedTextId(null)
    setSelectedLogoId(null)
    setShowTextPanel(false)
    setShowLogoPanel(false)
    dragStateRef.current = null
    
    // Mode Nova Screen Config - drag-to-select
    if (config.novaScreenConfigMode && config.ledWallMode?.enabled && selectedNovaPortRef.current !== null) {
      const canvas = canvasRef.current
      if (!canvas) return
      
      const rect = canvas.getBoundingClientRect();
      const scaleX = config.width / rect.width;
      const scaleY = config.height / rect.height;
      const clickX = (event.clientX - rect.left) * scaleX;
      const clickY = (event.clientY - rect.top) * scaleY;
      
      const { columns, rows, panelWidth, panelHeight } = config.ledWallMode;
      const offsetX = config.gridOffset.x;
      const offsetY = config.gridOffset.y;
      
      // Calculer quel panneau a été cliqué
      const panelCol = Math.floor((clickX - offsetX) / panelWidth);
      const panelRow = Math.floor((clickY - offsetY) / panelHeight);
      
      // Vérifier que le clic est dans la grille
      if (panelCol >= 0 && panelCol < columns && panelRow >= 0 && panelRow < rows) {
        // Commencer le drag
        novaDragStartRef.current = { col: panelCol, row: panelRow }
        novaDragCurrentRef.current = { col: panelCol, row: panelRow }
        isNovaDraggingRef.current = true
        novaDragPathRef.current = [{ col: panelCol, row: panelRow }] // Initialiser le chemin
      }
    }
  }, [getCanvasCoordinates, hitTestText, hitTestLogo, textElements, logoElements, selectedTextId, selectedLogoId, config.novaScreenConfigMode, config.ledWallMode])

  const handleCanvasMouseMove = useCallback((event: React.MouseEvent<HTMLCanvasElement>) => {
    const { x, y } = getCanvasCoordinates(event)

    // Mode Nova Screen Config - drag-to-select
    if (isNovaDraggingRef.current && novaDragStartRef.current && config.ledWallMode?.enabled) {
      const canvas = canvasRef.current
      if (!canvas) return
      
      const rect = canvas.getBoundingClientRect();
      const scaleX = config.width / rect.width;
      const scaleY = config.height / rect.height;
      const mouseX = (event.clientX - rect.left) * scaleX;
      const mouseY = (event.clientY - rect.top) * scaleY;
      
      const { columns, rows, panelWidth, panelHeight } = config.ledWallMode;
      const offsetX = config.gridOffset.x;
      const offsetY = config.gridOffset.y;
      
      // Calculer quel panneau est survolé
      const panelCol = Math.floor((mouseX - offsetX) / panelWidth);
      const panelRow = Math.floor((mouseY - offsetY) / panelHeight);
      
      // Vérifier que le survol est dans la grille
      if (panelCol >= 0 && panelCol < columns && panelRow >= 0 && panelRow < rows) {
        novaDragCurrentRef.current = { col: panelCol, row: panelRow }
        
        // Ajouter le panneau au chemin s'il n'y est pas déjà
        const isAlreadyInPath = novaDragPathRef.current.some(p => p.col === panelCol && p.row === panelRow)
        
        if (!isAlreadyInPath) {
          novaDragPathRef.current.push({ col: panelCol, row: panelRow })
        }
        
        // Redessiner pour afficher la sélection en cours
        requestAnimationFrame(() => {
          drawPatternRef.current()
        })
      }
      return
    }

    // Mode Nova Screen Config - ajouter le dernier panneau au mouseup
    if (isNovaDraggingRef.current && novaDragCurrentRef.current && config.ledWallMode?.enabled) {
      const { col, row } = novaDragCurrentRef.current
      const isAlreadyInPath = novaDragPathRef.current.some(p => p.col === col && p.row === row)
      if (!isAlreadyInPath) {
        novaDragPathRef.current.push({ col, row })
      }
    }

    // Gérer le curseur au survol
    if (!isDragging && !dragStateRef.current) {
      const textId = hitTestText(x, y)
      const logoId = hitTestLogo(x, y)
      if (textId || logoId) {
        setCursorStyle('move')
      } else {
        setCursorStyle('default')
      }
    }

    // Si on a un dragState mais qu'on n'a pas encore commencé le drag
    if (dragStateRef.current && !dragStateRef.current.hasStartedDragging) {
      const dx = Math.abs(x - dragStateRef.current.startMouseX)
      const dy = Math.abs(y - dragStateRef.current.startMouseY)
      const distance = Math.sqrt(dx * dx + dy * dy)

      // Seuil de mouvement pour commencer le drag (5 pixels)
      if (distance > 5) {
        dragStateRef.current.hasStartedDragging = true
        setIsDragging(true)
      } else {
        return // Attendre un mouvement plus significatif
      }
    }

    // Si on est en train de dragger
    if (isDragging && dragStateRef.current && dragStateRef.current.hasStartedDragging) {
      const ds = dragStateRef.current

      // Position simple : le texte suit exactement la souris
      let newX = x
      let newY = y

      // Appliquer un snap (accrochage) sur une grille de 10 pixels (activé avec Shift)
      const snapSize = 10

      // Snap aux positions de grille activé uniquement avec Shift
      if (event.shiftKey) {
        newX = Math.round(newX / snapSize) * snapSize
        newY = Math.round(newY / snapSize) * snapSize
      }

      // Limiter aux bornes du canvas
      const width = typeof config.width === 'number' && isFinite(config.width) ? config.width : 1920
      const height = typeof config.height === 'number' && isFinite(config.height) ? config.height : 1080

      if (ds.type === 'text') {
        // Position directe de la souris
        updateTextElement(ds.id, { x: newX, y: newY })
      } else {
        // Pour le logo, calculer le décalage par rapport à la position initiale
        const lg = logoElements.find(l => l.id === ds.id)!

        // Calculer le décalage de la souris depuis le début du drag
        const deltaX = newX - ds.startMouseX
        const deltaY = newY - ds.startMouseY

        // Appliquer ce décalage à la position initiale du logo
        let logoX = ds.startX + deltaX
        let logoY = ds.startY + deltaY

        // Limiter aux bornes du canvas
        logoX = Math.max(0, Math.min(width - lg.width, logoX))
        logoY = Math.max(0, Math.min(height - lg.height, logoY))

        updateLogoElement(ds.id, { x: logoX, y: logoY })
      }

      setCursorStyle('move')
    }
  }, [isDragging, getCanvasCoordinates, updateTextElement, updateLogoElement, hitTestText, hitTestLogo, textElements, logoElements, config.width, config.height])

  const endDrag = useCallback(() => {
    // Mode Nova Screen Config - finaliser le drag-to-select
    if (isNovaDraggingRef.current) {
      const panelsToAdd = novaDragPathRef.current
      const isRealNovaDrag = panelsToAdd.length > 1
      
      // Ajouter les panneaux aux connexions
      if (isRealNovaDrag && selectedNovaPortRef.current !== null) {
        setNovaPanelConnections(prev => {
          const existingPanelIds = new Set(panelsToAdd.map(panel => `${panel.col + 1}.${panel.row + 1}`))
          const baseConnections = prev.filter(c => !existingPanelIds.has(c.panelId))
          const portConnections = baseConnections.filter(c => c.port === selectedNovaPortRef.current)
          const newOrder = portConnections.length + 1
          
          const newConnections = panelsToAdd.map((panel, index) => ({
            panelId: `${panel.col + 1}.${panel.row + 1}`,
            port: selectedNovaPortRef.current!,
            order: newOrder + index
          }))
          
          const updated = [...baseConnections, ...newConnections]
          
          // Recalculer les ordres pour ce port
          const portConns = updated.filter(c => c.port === selectedNovaPortRef.current)
          const otherConns = updated.filter(c => c.port !== selectedNovaPortRef.current)
          const recalculatedPortConns = portConns.map((conn, index) => ({
            ...conn,
            order: index + 1
          }))
          
          const final = [...otherConns, ...recalculatedPortConns]
          novaPanelConnectionsRef.current = final
          
          return final
        })
      }
      
      // Reset les refs de drag
      novaDragStartRef.current = null
      novaDragCurrentRef.current = null
      isNovaDraggingRef.current = false
      novaDragPathRef.current = [] // Reset le chemin
      if (isRealNovaDrag) {
        justFinishedDraggingRef.current = true
        setTimeout(() => {
          justFinishedDraggingRef.current = false
        }, 50)
      }
    }
    
    // Si on avait un dragState et qu'on a commencé le drag, marquer qu'on vient de finir
    if (dragStateRef.current && dragStateRef.current.hasStartedDragging) {
      justFinishedDraggingRef.current = true
      // Reset après un court délai pour permettre au click de l'ignorer
      setTimeout(() => {
        justFinishedDraggingRef.current = false
      }, 50)
    }
    setIsDragging(false)
    dragStateRef.current = null
    setCursorStyle('default')
  }, [])

  const handleCanvasMouseUp = useCallback((event: React.MouseEvent<HTMLCanvasElement>) => {
    // Mode Nova Screen Config - capturer le dernier panneau au relâchement
    if (isNovaDraggingRef.current && config.ledWallMode?.enabled) {
      const canvas = canvasRef.current
      if (!canvas) {
        endDrag()
        return
      }
      
      const rect = canvas.getBoundingClientRect();
      const scaleX = config.width / rect.width;
      const scaleY = config.height / rect.height;
      const mouseX = (event.clientX - rect.left) * scaleX;
      const mouseY = (event.clientY - rect.top) * scaleY;
      
      const { columns, rows, panelWidth, panelHeight } = config.ledWallMode;
      const offsetX = config.gridOffset.x;
      const offsetY = config.gridOffset.y;
      
      // Calculer quel panneau est survolé au moment du relâchement
      const panelCol = Math.floor((mouseX - offsetX) / panelWidth);
      const panelRow = Math.floor((mouseY - offsetY) / panelHeight);
      
      // Vérifier que le survol est dans la grille
      if (panelCol >= 0 && panelCol < columns && panelRow >= 0 && panelRow < rows) {
        // Ajouter le panneau au chemin s'il n'y est pas déjà
        const isAlreadyInPath = novaDragPathRef.current.some(p => p.col === panelCol && p.row === panelRow)
        if (!isAlreadyInPath) {
          novaDragPathRef.current.push({ col: panelCol, row: panelRow })
        }
      }
    }
    
    endDrag()
  }, [endDrag, config.ledWallMode])

  // ===== HANDLERS POUR CONTRÔLES MOBILES SELF-TEST =====

  // Handler pour navigation depuis mobile
  const handleMobileSelfTestNavigate = useCallback((direction: 'up' | 'down' | 'left' | 'right') => {
    if (!isInSession || !sendSyncMessage) return;


    // NOUVEAU : Mettre à jour l'état local du mobile également pour feedback immédiat
    if (selfTestMode.enabled && config.ledWallMode?.enabled) {
      const { columns, rows } = config.ledWallMode;
      const { selectedPanelX, selectedPanelY } = selfTestMode;

      let newX = selectedPanelX;
      let newY = selectedPanelY;

      switch (direction) {
        case 'up':
          newY = Math.max(1, selectedPanelY - 1);
          break;
        case 'down':
          newY = Math.min(rows, selectedPanelY + 1);
          break;
        case 'left':
          newX = Math.max(1, selectedPanelX - 1);
          break;
        case 'right':
          newX = Math.min(columns, selectedPanelX + 1);
          break;
      }

      if (newX !== selectedPanelX || newY !== selectedPanelY) {
        const panelKey = `${newX},${newY}`;

        let newActivePanels = selfTestMode.trailMode
          ? new Set(selfTestMode.activePanels)
          : new Set<string>();

        newActivePanels.add(panelKey);

        setSelfTestMode(prev => ({
          ...prev,
          selectedPanelX: newX,
          selectedPanelY: newY,
          activePanels: newActivePanels
        }));

      } else {
      }
    }

    // Envoyer la commande de navigation via les sessions
    sendSyncMessage('SELFTEST_NAVIGATE', { direction });
  }, [isInSession, sendSyncMessage, selfTestMode, config.ledWallMode]);

  // Handler pour toggle panneau cyan depuis mobile
  const handleMobileSelfTestToggleCyan = useCallback(() => {
    if (!isInSession || !sendSyncMessage) return;

    const panelKey = `${selfTestMode.selectedPanelX},${selfTestMode.selectedPanelY}`;

    // NOUVEAU : Mettre à jour l'état local immédiatement pour feedback instantané
    setCyanPanels(prev => {
      const newCyanPanels = new Set(prev);
      let isNowCyan = false;

      if (newCyanPanels.has(panelKey)) {
        newCyanPanels.delete(panelKey);

        // Si on retire le cyan, désactiver aussi le mode PCB pour ce panneau
        setSubdivisionMode(false);
        setRedZones(prev => {
          const newRedZones = new Set(prev);
          // Retirer toutes les zones rouges de ce panneau
          for (const zoneKey of newRedZones) {
            if (zoneKey.startsWith(panelKey + ',')) {
              newRedZones.delete(zoneKey);
            }
          }
          return newRedZones;
        });
      } else {
        newCyanPanels.add(panelKey);
        isNowCyan = true;
        // NOUVEAU : Activer automatiquement le mode PCB quand on met un panneau en cyan
        setSubdivisionMode(true);
      }

      return newCyanPanels;
    });

    // Envoyer la commande toggle cyan via les sessions
    sendSyncMessage('SELFTEST_TOGGLE_CYAN', {});
  }, [isInSession, sendSyncMessage, selfTestMode.selectedPanelX, selfTestMode.selectedPanelY]);

  // Handler pour toggle zone rouge PCB depuis mobile
  const handleMobileSelfTestToggleRedZone = useCallback(() => {
    if (!isInSession || !sendSyncMessage) return;

    const panelKey = `${selfTestMode.selectedPanelX},${selfTestMode.selectedPanelY}`;
    const zoneKey = `${selfTestMode.selectedPanelX},${selfTestMode.selectedPanelY},${selectedQuadrant}`;


    // Vérifier que le panneau est cyan et qu'on est en mode subdivision
    if (!cyanPanels.has(panelKey)) {
      return;
    }

    if (!subdivisionMode) {
      return;
    }

    // NOUVEAU : Mettre à jour l'état local immédiatement pour feedback instantané
    setRedZones(prev => {
      const newRedZones = new Set(prev);

      if (newRedZones.has(zoneKey)) {
        newRedZones.delete(zoneKey);
      } else {
        newRedZones.add(zoneKey);
      }

      return newRedZones;
    });

    // Envoyer la commande toggle zone rouge via les sessions
    sendSyncMessage('SELFTEST_TOGGLE_RED_ZONE', {});
  }, [isInSession, sendSyncMessage, selfTestMode.selectedPanelX, selfTestMode.selectedPanelY, selectedQuadrant, cyanPanels, subdivisionMode]);

  // Handler pour navigation quadrant depuis mobile
  const handleMobileSelfTestNavigateQuadrant = useCallback((quadrant: 'tl' | 'tr' | 'bl' | 'br') => {
    if (!isInSession || !sendSyncMessage) return;

    // NOUVEAU : Mettre à jour l'état local immédiatement pour feedback instantané
    setSelectedQuadrant(quadrant);

    // Envoyer la commande navigation quadrant via les sessions
    sendSyncMessage('SELFTEST_NAVIGATE_QUADRANT', { quadrant });
  }, [isInSession, sendSyncMessage]);

  return (
    <div className="min-h-screen bg-background text-foreground p-4 flex flex-col">
      <DynamicTitle width={config.width} height={config.height} isAnimating={isAnimating} />

      {/* GPU auto-détecté silencieusement via powerPreference */}

      <div className="max-w-7xl mx-auto w-full">
        <div className="mb-6 md:mb-8 flex flex-row justify-between items-start md:items-center gap-4">
          {!isEmbeddedInSynoptic && (
            <div className="flex-shrink min-w-0">
              <div className="flex items-center mb-2">
                <AppLogo />
                <h1 className="text-2xl md:text-3xl font-bold truncate">{t("app.title")}</h1>
              </div>
              <p className="text-sm md:text-base text-muted-foreground hidden sm:block">{t("app.description")}</p>
            </div>
          )}
          {!isEmbeddedInSynoptic && (
            <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
              {/* MultiGrid Button - Affiché seulement sur VRAI desktop (pas mobile forçant desktop) */}
              {!isMobile && (
                <Button
                  variant="outline"
                  className="flex items-center gap-1 md:gap-2 relative overflow-hidden bg-gradient-to-r from-blue-500/10 to-purple-500/10 border-blue-400 dark:border-blue-600 animate-pulse hover:animate-none shadow-[0_0_15px_rgba(59,130,246,0.5)] dark:shadow-[0_0_15px_rgba(147,197,253,0.5)] text-xs md:text-sm px-2 md:px-4"
                  asChild
                >
                  <SmartLink href="/multigrid/" className="relative z-10 flex items-center gap-1 md:gap-2">
                    <div className="pointer-events-none absolute inset-y-0 -left-1/2 w-1/2 skew-x-12 bg-gradient-to-r from-transparent via-white/80 to-transparent dark:via-white/40 mix-blend-screen opacity-90 animate-wipe filter drop-shadow-[0_0_12px_rgba(255,255,255,0.6)]"></div>
                    <Grid className="h-4 w-4 text-blue-500 dark:text-blue-400 flex-shrink-0" />
                    <span className="font-bold text-blue-600 dark:text-blue-400 hidden sm:inline">{t("Multi-Grid") || "Multi-Grid"}</span>
                  </SmartLink>
                </Button>
              )}

              {/* Synoptique Button - Affiché seulement sur VRAI desktop */}
              {!isMobile && (
                <Button
                  variant="outline"
                  className="flex items-center gap-1 md:gap-2 relative overflow-hidden bg-gradient-to-r from-green-500/10 to-teal-500/10 border-green-400 dark:border-green-600 animate-pulse hover:animate-none shadow-[0_0_15px_rgba(34,197,94,0.5)] dark:shadow-[0_0_15px_rgba(74,222,128,0.5)] text-xs md:text-sm px-2 md:px-4"
                  asChild
                >
                  <SmartLink href="/synoptique/" className="relative z-10 flex items-center gap-1 md:gap-2">
                    <div className="pointer-events-none absolute inset-y-0 -left-1/2 w-1/2 skew-x-12 bg-gradient-to-r from-transparent via-white/80 to-transparent dark:via-white/40 mix-blend-screen opacity-90 animate-wipe filter drop-shadow-[0_0_12px_rgba(255,255,255,0.6)]"></div>
                    <Grid3X3 className="h-4 w-4 text-green-500 dark:text-green-400 flex-shrink-0" />
                    <span className="font-bold text-green-600 dark:text-green-400 hidden sm:inline">{t("landing.header.synoptic")}</span>
                  </SmartLink>
                </Button>
              )}

              {/* Desktop Navigation - Affiché seulement sur VRAI desktop */}
              {!isMobile && (
                <>
                  {!isTauriApp && (
                    <Button variant="outline" className="flex items-center gap-2" asChild>
                      <SmartLink href="/downloads/">
                        <Download className="h-4 w-4" />
                        {t("nav.downloads")}
                      </SmartLink>
                    </Button>
                  )}
                  <LanguageSwitcher />
                  <NdiControl ndi={ndi} width={config.width} height={config.height} />
                  <Button variant="outline" size="icon" onClick={() => setTheme(theme === "dark" ? "light" : "dark")}>
                    {mounted && (theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />)}
                  </Button>
                  <AuthButton variant="outline" size="sm" />
                </>
              )}

              {/* Mobile - Affiche AuthButton + Burger Menu */}
              {isMobile && (
                <>
                  <AuthButton variant="outline" size="sm" />
                  <BurgerMenu showDownloads={!isTauriApp} />
                </>
              )}
            </div>
          )}
        </div>

        {/* Bandeau mobile/desktop switcher - Affiché seulement sur mobile détecté */}
        {showMobileBanner && !isEmbeddedInSynoptic && (
          <div className="mb-4">
            <div className="bg-primary/10 border-2 border-primary/20 rounded-lg p-3">
              <div>
                <p className="text-sm font-medium mb-2">
                  {displayMode === 'mobile' ? t('mobile.banner.mobileMode') : displayMode === 'desktop' ? t('mobile.banner.desktopMode') : t('mobile.banner.chooseDisplay')}
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant={displayMode === 'mobile' ? 'default' : 'outline'}
                    onClick={() => router.push("/grid?forceMobile=true")}
                    className="flex-1"
                  >
                    {t('mobile.banner.versionMobile')}
                  </Button>
                  <Button
                    size="sm"
                    variant={displayMode === 'desktop' ? 'default' : 'outline'}
                    onClick={() => router.push("/grid?forceDesktop=true")}
                    className="flex-1"
                  >
                    {t('mobile.banner.versionDesktop')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className={`grid gap-6 ${effectiveIsMobile ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-4'}`}>

          {/* Session Integration - Mobile uniquement (ordre 2, après canvas) */}
          {!isEmbeddedInSynoptic && isMobile && (
            <div className="order-2">
              <SessionIntegration
                currentConfig={{
                  ...config,
                  activePatternType,
                  selfTestMode: {
                    ...selfTestMode,
                    activePanels: Array.from(selfTestMode.activePanels)
                  },
                  cyanPanels: Array.from(cyanPanels),
                  subdivisionMode,
                  selectedQuadrant,
                  redZones: Array.from(redZones)
                }}
                onConfigUpdate={handleConfigUpdate}
                onAnimationStateUpdate={handleAnimationStateUpdate}
                onSessionStateChange={handleSessionStateChange}
                setSendSyncMessage={setSendSyncMessage}
                hideCreate={effectiveIsMobile}
                hideJoin={!effectiveIsMobile}
              />
            </div>
          )}

          {/* Configuration Panel - masqué en mode externe */}
          {!isExternalDisplay && (
            <div className={`space-y-4 ${isMobile ? 'order-3' : 'lg:col-span-1'}`}>

              {/* Session Integration - Desktop uniquement */}
              {!isEmbeddedInSynoptic && !isMobile && isPro && (
                <SessionIntegration
                  currentConfig={{
                    ...config,
                    activePatternType,
                    selfTestMode: {
                      ...selfTestMode,
                      activePanels: Array.from(selfTestMode.activePanels)
                    },
                    cyanPanels: Array.from(cyanPanels),
                    subdivisionMode,
                    selectedQuadrant,
                    redZones: Array.from(redZones)
                  }}
                  onConfigUpdate={handleConfigUpdate}
                  onAnimationStateUpdate={handleAnimationStateUpdate}
                  onSessionStateChange={handleSessionStateChange}
                  setSendSyncMessage={setSendSyncMessage}
                  hideCreate={effectiveIsMobile}
                />
              )}

              {/* NOUVEAU : Sur mobile, afficher les modules seulement si une session est active */}
              {(!effectiveIsMobile || isInSession) && (
                <>

                  <Card className="relative">
                    {/* Overlay pendant l'export pour bloquer les interactions */}
                    {isWebCodecsExporting && (
                      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-10 rounded-lg flex items-center justify-center">
                        <div className="text-center space-y-2">
                          <div className="text-sm font-medium">Export in progress...</div>
                          <div className="text-xs text-muted-foreground">Controls are disabled</div>
                        </div>
                      </div>
                    )}
                    <CardHeader>
                      <div>
                        <CardTitle>{t("config.title")}</CardTitle>
                        <CardDescription>{t("config.description")}</CardDescription>
                      </div>
                      <div className={`mt-4 inline-flex items-center gap-2 bg-muted/50 px-2 py-2 rounded-lg border transition-all duration-300 w-fit ${isAnimatingSwitch ? 'ring-1 ring-primary ring-offset-1 shadow-md shadow-primary/40 scale-105' : ''}`}>
                          <span className={`text-sm font-medium ${isEasyMode ? 'text-primary' : 'text-muted-foreground'}`}>{t("config.easyMode")}</span>
                          <Switch
                            checked={!isEasyMode}
                            onCheckedChange={(checked) => setIsEasyMode(!checked)}
                            className="data-[state=checked]:bg-primary"
                          />
                          <span className={`text-sm font-medium ${!isEasyMode ? 'text-primary' : 'text-muted-foreground'}`}>{t("config.fullMode")}</span>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* ===== SECTION GRILLE & RÉSOLUTION ===== */}
                      <div className="flex items-center gap-2 pb-2 border-b">
                        <MonitorCog className="w-4 h-4 text-muted-foreground" />
                        <h3 className="font-semibold text-sm">Grille & Résolution</h3>
                      </div>

                      {/* LED Wall Mode */}
                      <div className="space-y-4">
                        {/* Masqué en mode Easy */}
                        {!isEasyMode && (
                          <div className="flex items-center justify-between">
                            <Label htmlFor="ledWallEnabled">{t("config.ledwall")}</Label>
                            <Switch
                              id="ledWallEnabled"
                              checked={config.ledWallMode?.enabled}
                              onCheckedChange={(checked) => {
                                const updatedConfig = {
                                  ...config,
                                  ledWallMode: {
                                    ...config.ledWallMode,
                                    enabled: checked
                                  }
                                };

                                // Si activé, mettre à jour la taille de la grille automatiquement
                                if (checked) {
                                  const { columns, rows, panelWidth, panelHeight } = updatedConfig.ledWallMode;
                                  updatedConfig.width = columns * panelWidth;
                                  updatedConfig.height = rows * panelHeight;
                                  updatedConfig.gridSize = panelWidth;
                                  // Activer les détails techniques (grille, numéros LED, résolution)
                                  updatedConfig.showTechnicalDetails = true;
                                  updatedConfig.showGrid = true;
                                  updatedConfig.showLEDNumbers = true;
                                  updatedConfig.showResolution = true;
                                } else {
                                  // Conserver la résolution mais réinitialiser la taille de la grille à une valeur plus adaptée
                                  const minDimension = Math.min(updatedConfig.width, updatedConfig.height);
                                  updatedConfig.gridSize = Math.max(Math.floor(minDimension / 10), 192); // Valeur par défaut ou 1/10 de la plus petite dimension
                                }

                                setConfig(updatedConfig);

                                // ✅ PostHog: Track LED Wall Mode toggle
                                captureEvent('grid:toggle_led_wall_mode', {
                                  enabled: checked,
                                  columns: updatedConfig.ledWallMode?.columns,
                                  rows: updatedConfig.ledWallMode?.rows,
                                  panel_width: updatedConfig.ledWallMode?.panelWidth,
                                  panel_height: updatedConfig.ledWallMode?.panelHeight,
                                });
                              }}
                            />
                          </div>
                        )}

                        {/* Resolution Presets */}
                        {!config.ledWallMode?.enabled && (
                          <div className="space-y-2">
                            <Label>{t("config.preset")}</Label>
                            <Select
                              value={selectedPreset}
                              onValueChange={(value) => {
                                const preset = presetResolutions.find((p) => p.name === value)
                                if (preset) applyPreset(preset)
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder={t("config.choose")} />
                              </SelectTrigger>
                              <SelectContent>
                                {presetResolutions.map((preset) => (
                                  <SelectItem key={preset.name} value={preset.name}>
                                    {preset.name} ({preset.width}×{preset.height})
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}

                        {config.ledWallMode?.enabled && (
                          <>
                            <div className="grid grid-cols-2 gap-2" data-onboarding="grid-dimensions">
                              <div className="space-y-2">
                                <Label htmlFor="ledColumns">{t("config.columns")}</Label>
                                <MathInput
                                  id="ledColumns"
                                  inputMode="numeric"
                                  className={columnsError ? "border-red-500" : ""}
                                  value={config.ledWallMode?.columns ?? 10}
                                  onValueChange={(value) => {
                                    // Garder la valeur persistée synchronisée
                                    setColumnsDisplayValue(value.toString());

                                    // Validation pour les valeurs < 1
                                    if (value < MIN_COLUMNS_ROWS) {
                                      setColumnsError(t("config.minColumnsError"));
                                      return; // Ne pas mettre à jour la config pour une valeur invalide
                                    }

                                    // Vérifier si le nombre total de pixels dépasse la limite
                                    const totalPixels = value * (config.ledWallMode?.rows || 1) * (config.ledWallMode?.panelWidth || 192) * (config.ledWallMode?.panelHeight || 192);

                                    if (totalPixels > MAX_PIXELS) {
                                      setColumnsError(t("config.maxPixelsError"));
                                      return; // Ne pas mettre à jour la config si limite dépassée
                                    } else {
                                      setColumnsError(null);
                                    }

                                    // Calculer la nouvelle largeur de dalle pour maintenir la résolution totale
                                    const newPanelWidth = config.ledWallMode?.panelWidth || 192;

                                    const updatedConfig = {
                                      ...config,
                                      ledWallMode: {
                                        ...config.ledWallMode,
                                        columns: value
                                      },
                                      width: value * newPanelWidth
                                    };
                                    setConfig(updatedConfig);

                                    // Vérifier si le rendu est possible
                                    checkRenderability(updatedConfig);
                                  }}
                                />
                                {columnsError && (
                                  <p className="text-xs text-red-500">{columnsError}</p>
                                )}
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="ledRows">{t("config.rows")}</Label>
                                <MathInput
                                  id="ledRows"
                                  inputMode="numeric"
                                  className={rowsError ? "border-red-500" : ""}
                                  value={config.ledWallMode?.rows ?? 6}
                                  onValueChange={(value) => {
                                    // Garder la valeur persistée synchronisée
                                    setRowsDisplayValue(value.toString());

                                    // Validation pour les valeurs < 1
                                    if (value < MIN_COLUMNS_ROWS) {
                                      setRowsError(t("config.minRowsError"));
                                      return; // Ne pas mettre à jour la config pour une valeur invalide
                                    }

                                    // Vérifier si le nombre total de pixels dépasse la limite
                                    const totalPixels = (config.ledWallMode?.columns || 1) * value * (config.ledWallMode?.panelWidth || 192) * (config.ledWallMode?.panelHeight || 192);

                                    if (totalPixels > MAX_PIXELS) {
                                      setRowsError(t("config.maxPixelsError"));
                                      return; // Ne pas mettre à jour la config si limite dépassée
                                    } else {
                                      setRowsError(null);
                                    }

                                    // Calculer la nouvelle hauteur de dalle pour maintenir la résolution totale
                                    const newPanelHeight = config.ledWallMode?.panelHeight || 192;

                                    const updatedConfig = {
                                      ...config,
                                      ledWallMode: {
                                        ...config.ledWallMode,
                                        rows: value
                                      },
                                      height: value * newPanelHeight
                                    };
                                    setConfig(updatedConfig);

                                    // Vérifier si le rendu est possible
                                    checkRenderability(updatedConfig);
                                  }}
                                />
                                {rowsError && (
                                  <p className="text-xs text-red-500">{rowsError}</p>
                                )}
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-2" data-onboarding="grid-customization">
                              <div className="space-y-2">
                                <Label htmlFor="panelWidth">{t("config.panelWidth")}</Label>
                                <MathInput
                                  id="panelWidth"
                                  inputMode="numeric"
                                  value={config.ledWallMode?.panelWidth || 192}
                                  onValueChange={(rawValue) => {
                                    const value = Math.max(1, rawValue);
                                    const updatedConfig = {
                                      ...config,
                                      ledWallMode: {
                                        ...config.ledWallMode,
                                        panelWidth: value
                                      },
                                      width: (config.ledWallMode?.columns || 1) * value
                                    };

                                    // Mettre à jour également la taille de la grille pour qu'elle corresponde à la taille des dalles
                                    updatedConfig.gridSize = value;

                                    setConfig(updatedConfig);
                                  }}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="panelHeight">{t("config.panelHeight")}</Label>
                                <MathInput
                                  id="panelHeight"
                                  inputMode="numeric"
                                  value={config.ledWallMode?.panelHeight || 192}
                                  onValueChange={(rawValue) => {
                                    const value = Math.max(1, rawValue);
                                    const updatedConfig = {
                                      ...config,
                                      ledWallMode: {
                                        ...config.ledWallMode,
                                        panelHeight: value
                                      },
                                      height: (config.ledWallMode?.rows || 1) * value
                                    };
                                    setConfig(updatedConfig);
                                  }}
                                />
                              </div>
                            </div>

                            {/* LED Panel Numbering Toggle - Masqué en mode Easy */}
                            {!isEasyMode && (
                              <div className="flex items-center justify-between">
                                <Label htmlFor="showLEDNumbers">{t("config.showLEDNumbers")}</Label>
                                <Switch
                                  id="showLEDNumbers"
                                  checked={config.showLEDNumbers}
                                  onCheckedChange={(checked) => {
                                    setConfig(prev => ({
                                      ...prev,
                                      showLEDNumbers: checked,
                                      showTechnicalDetails: prev.showGrid && prev.showResolution && checked,
                                      novaScreenConfigMode: false // Désactiver Nova Screen Config quand LED Numbering est activé
                                    }));

                                    // Broadcaster la mise à jour si en mode multi-écran
                                    if (broadcastChannelRef.current) {
                                      broadcastChannelRef.current.postMessage({
                                        type: 'CONFIG_UPDATE',
                                        data: { showLEDNumbers: checked, novaScreenConfigMode: false }
                                      });
                                    }

                                    // Redessiner immédiatement la mire
                                    requestAnimationFrame(() => {
                                      drawPattern();
                                    });
                                  }}
                                />
                              </div>
                            )}

                            {/* Épaisseur des bordures - masquée sur mobile et en mode Easy */}
                            {!isMobile && !isEasyMode && (
                              <div className="space-y-2">
                                <Label htmlFor="borderWidth">{t("config.borderWidth")}</Label>
                                <div className="flex items-center gap-2">
                                  <Slider
                                    id="borderWidth"
                                    value={[config.ledWallMode?.borderWidth ?? 2]}
                                    onValueChange={(value) => {
                                      const updatedConfig = {
                                        ...config,
                                        ledWallMode: {
                                          ...config.ledWallMode,
                                          borderWidth: value[0]
                                        }
                                      };
                                      setConfig(updatedConfig);
                                    }}
                                    max={5}
                                    min={1}
                                    step={1}
                                    className="flex-1"
                                  />
                                  <div className="w-10 text-center">{config.ledWallMode?.borderWidth ?? 2}px</div>
                                </div>
                              </div>
                            )}

                            {/* Anamorphose Toggle - Masqué en mode Easy */}
                            {!isEasyMode && (
                              <div className="flex items-center justify-between border-t pt-4">
                                <div className="flex flex-col">
                                  <Label htmlFor="enable_anamorphosis" className="text-white font-semibold">
                                    {t("config.anamorphose") || "Anamorphose"}
                                  </Label>
                                  <span className="text-xs text-gray-400">
                                    {t("config.anamorphoseDesc") || "Écrase verticalement pour compensation"}
                                  </span>
                                </div>
                                <Switch
                                  id="enable_anamorphosis"
                                  checked={config.enableAnamorphosis}
                                  onCheckedChange={(checked) => {
                                    setConfig(prev => ({
                                      ...prev,
                                      enableAnamorphosis: checked
                                    }));

                                    // Broadcaster la mise à jour si en mode multi-écran
                                    if (broadcastChannelRef.current) {
                                      broadcastChannelRef.current.postMessage({
                                        type: 'CONFIG_UPDATE',
                                        data: { enableAnamorphosis: checked }
                                      });
                                    }
                                  }}
                                />
                              </div>
                            )}

                            {/* Descriptif masqué sur mobile et en mode Easy */}
                            {!isMobile && !isEasyMode && (
                              <div className="text-xs text-muted-foreground space-y-1">
                                {t("config.ledWallInfo")
                                  .replace("{columns}", (config.ledWallMode?.columns || 1).toString())
                                  .replace("{rows}", (config.ledWallMode?.rows || 1).toString())
                                  .replace("{width}", (config.ledWallMode?.panelWidth || 192).toString())
                                  .replace("{height}", (config.ledWallMode?.panelHeight || 192).toString())
                                  .replace("{totalWidth}", config.width?.toString() ?? '1920')
                                  .replace("{totalHeight}", config.height?.toString() ?? '1152')
                                  .replace("{border}", (config.ledWallMode?.borderWidth || 0).toString())
                                  .split("\n").map((line, i) => (
                                    <p key={i}>{line}</p>
                                  ))
                                }
                              </div>
                            )}

                            {/* Bouton pour envoyer manuellement la config au Synoptique */}
                          </>
                        )}
                      </div>

                      {/* Custom Resolution - masquée en mode LED Wall et en mode Easy */}
                      {!config.ledWallMode?.enabled && !isEasyMode && (
                        <div className="grid grid-cols-2 gap-2">
                          <div className="space-y-2">
                            <Label htmlFor="width">{t("config.width")}</Label>
                            <MathInput
                              id="width"
                              inputMode="numeric"
                              value={typeof config.width === 'number' ? config.width : 1920}
                              onValueChange={(value) => {
                                updateConfig("width", value);
                                setSelectedPreset("Custom");
                              }}
                              placeholder="Ex: 1920 ou 1920+100"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="height">{t("config.height")}</Label>
                            <MathInput
                              id="height"
                              inputMode="numeric"
                              value={typeof config.height === 'number' ? config.height : 1080}
                              onValueChange={(value) => {
                                updateConfig("height", value);
                                setSelectedPreset("Custom");
                              }}
                              placeholder="Ex: 1080 ou 1080/2"
                            />
                          </div>
                        </div>
                      )}

                      {/* Grid Size */}
                      {!config.ledWallMode?.enabled && (
                        <div className="space-y-2">
                          <Label htmlFor="gridSize">{t('config.gridSizeLabel')}</Label>
                          <MathInput
                            id="gridSize"
                            inputMode="numeric"
                            value={typeof config.gridSize === 'number' ? config.gridSize : 192}
                            onValueChange={(value) => {
                              if (value >= 50) {
                                updateConfig("gridSize", value);
                              } else {
                                updateConfig("gridSize", 192);
                                toast.error(t('toast.invalidGridSize'));
                              }
                            }}
                            placeholder="Ex: 192 ou 1920/10"
                          />
                        </div>
                      )}

                      {/* Épaisseur des bordures - masquée sur mobile */}
                      {!config.ledWallMode?.enabled && !isMobile && (
                        <div className="space-y-2">
                          <Label htmlFor="borderWidth">{t("config.borderWidth")}</Label>
                          <div className="flex items-center gap-2">
                            <Slider
                              id="borderWidth"
                              value={[config.ledWallMode?.borderWidth ?? 2]}
                              onValueChange={(value) => {
                                const updatedConfig = {
                                  ...config,
                                  ledWallMode: {
                                    ...config.ledWallMode,
                                    borderWidth: value[0]
                                  }
                                };
                                setConfig(updatedConfig);
                              }}
                              max={5}
                              min={1}
                              step={1}
                              className="flex-1"
                            />
                            <div className="w-10 text-center">{config.ledWallMode?.borderWidth ?? 2}px</div>
                          </div>
                        </div>
                      )}

                      {/* Grid Offset Toggle - Masqué en mode Easy */}
                      {!isEasyMode && (
                        <>
                          <div className="flex items-center justify-between">
                            <Label htmlFor="showGridOffset">{t("config.gridOffset")}</Label>
                            <Switch
                              id="showGridOffset"
                              checked={showGridOffset}
                              onCheckedChange={(checked) => setShowGridOffset(checked)}
                            />
                          </div>

                          {/* Grid Offset Controls - masqués sur mobile et si désactivé */}
                          {!isMobile && showGridOffset && (
                        <div className="space-y-4 border-t pt-4">
                          <Label>{t("config.gridOffset")}</Label>
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <Label htmlFor="gridOffsetX">{t("config.gridOffsetX")}: {config.gridOffset?.x ?? 0}px</Label>
                            </div>
                            <div className="flex gap-2">
                              <Slider
                                id="gridOffsetX"
                                value={[config.gridOffset?.x ?? 0]}
                                onValueChange={(value) => {
                                  const newGridOffset = {
                                    ...(config.gridOffset ?? { x: 0, y: 0 }),
                                    x: value[0]
                                  };
                                  updateConfig("gridOffset", newGridOffset);
                                }}
                                min={-100}
                                max={100}
                                step={1}
                                className="w-full"
                              />
                              <MathInput
                                inputMode="numeric"
                                value={config.gridOffset?.x ?? 0}
                                onValueChange={(value) => {
                                  const newGridOffset = {
                                    ...(config.gridOffset ?? { x: 0, y: 0 }),
                                    x: value
                                  };
                                  updateConfig("gridOffset", newGridOffset);
                                }}
                                className="w-20"
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <Label htmlFor="gridOffsetY">{t("config.gridOffsetY")}: {config.gridOffset?.y ?? 0}px</Label>
                            </div>
                            <div className="flex gap-2">
                              <Slider
                                id="gridOffsetY"
                                value={[config.gridOffset?.y ?? 0]}
                                onValueChange={(value) => {
                                  const newGridOffset = {
                                    ...(config.gridOffset ?? { x: 0, y: 0 }),
                                    y: value[0]
                                  };
                                  updateConfig("gridOffset", newGridOffset);
                                }}
                                min={-100}
                                max={100}
                                step={1}
                                className="w-full"
                              />
                              <MathInput
                                inputMode="numeric"
                                value={config.gridOffset?.y ?? 0}
                                onValueChange={(value) => {
                                  const newGridOffset = {
                                    ...(config.gridOffset ?? { x: 0, y: 0 }),
                                    y: value
                                  };
                                  updateConfig("gridOffset", newGridOffset);
                                }}
                                className="w-20"
                              />
                            </div>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {t("config.gridOffsetDesc")}
                          </div>
                        </div>
                      )}
                        </>
                      )}

                      {/* Show Grid Toggle - Masqué en mode Easy */}
                      {!isEasyMode && (
                        <div className="flex items-center justify-between">
                          <Label htmlFor="showGrid">{t("config.showGrid")}</Label>
                          <Switch
                            id="showGrid"
                            checked={config.showGrid}
                            onCheckedChange={(checked) => {
                              setConfig(prev => ({
                                ...prev,
                                showGrid: checked,
                                showTechnicalDetails: checked && prev.showResolution && prev.showLEDNumbers
                              }));

                              // Broadcaster la mise à jour si en mode multi-écran
                              if (broadcastChannelRef.current) {
                                broadcastChannelRef.current.postMessage({
                                  type: 'CONFIG_UPDATE',
                                  data: { showGrid: checked }
                                });
                              }
                            }}
                          />
                        </div>
                      )}

                      {/* Grid Even Opacity Slider - Masqué en mode Easy */}
                      {!isEasyMode && config.showGrid && (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label htmlFor="gridEvenOpacity">{t("config.gridEvenOpacity") || "Opacité panneaux pairs"}</Label>
                            <div className="flex items-center gap-2">
                              <MathInput
                                inputMode="numeric"
                                value={config.gridEvenOpacity ?? 97}
                                onValueChange={(value) => {
                                  const clampedValue = Math.max(0, Math.min(100, value));
                                  setConfig(prev => ({
                                    ...prev,
                                    gridEvenOpacity: clampedValue
                                  }));

                                  // Broadcaster la mise à jour si en mode multi-écran
                                  if (broadcastChannelRef.current) {
                                    broadcastChannelRef.current.postMessage({
                                      type: 'CONFIG_UPDATE',
                                      data: { gridEvenOpacity: clampedValue }
                                    });
                                  }
                                }}
                                className="w-16 h-8 text-center text-sm"
                              />
                              <span className="text-sm text-muted-foreground">%</span>
                            </div>
                          </div>
                          <Slider
                            id="gridEvenOpacity"
                            min={0}
                            max={100}
                            step={1}
                            value={[config.gridEvenOpacity ?? 97]}
                            onValueChange={(value) => {
                              setConfig(prev => ({
                                ...prev,
                                gridEvenOpacity: value[0]
                              }));

                              // Broadcaster la mise à jour si en mode multi-écran
                              if (broadcastChannelRef.current) {
                                broadcastChannelRef.current.postMessage({
                                  type: 'CONFIG_UPDATE',
                                  data: { gridEvenOpacity: value[0] }
                                });
                              }
                            }}
                          />
                        </div>
                      )}

                      {/* Grid Odd Opacity Slider - Masqué en mode Easy */}
                      {!isEasyMode && config.showGrid && (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label htmlFor="gridOddOpacity">{t("config.gridOddOpacity") || "Opacité panneaux impairs"}</Label>
                            <div className="flex items-center gap-2">
                              <MathInput
                                inputMode="numeric"
                                value={config.gridOddOpacity ?? 85}
                                onValueChange={(value) => {
                                  const clampedValue = Math.max(0, Math.min(100, value));
                                  setConfig(prev => ({
                                    ...prev,
                                    gridOddOpacity: clampedValue
                                  }));

                                  // Broadcaster la mise à jour si en mode multi-écran
                                  if (broadcastChannelRef.current) {
                                    broadcastChannelRef.current.postMessage({
                                      type: 'CONFIG_UPDATE',
                                      data: { gridOddOpacity: clampedValue }
                                    });
                                  }
                                }}
                                className="w-16 h-8 text-center text-sm"
                              />
                              <span className="text-sm text-muted-foreground">%</span>
                            </div>
                          </div>
                          <Slider
                            id="gridOddOpacity"
                            min={0}
                            max={100}
                            step={1}
                            value={[config.gridOddOpacity ?? 85]}
                            onValueChange={(value) => {
                              setConfig(prev => ({
                                ...prev,
                                gridOddOpacity: value[0]
                              }));

                              // Broadcaster la mise à jour si en mode multi-écran
                              if (broadcastChannelRef.current) {
                                broadcastChannelRef.current.postMessage({
                                  type: 'CONFIG_UPDATE',
                                  data: { gridOddOpacity: value[0] }
                                });
                              }
                            }}
                          />
                        </div>
                      )}

                      {/* ===== SECTION MOTIF & ANIMATION ===== */}
                      <div className="flex items-center gap-2 pb-2 border-b mt-6">
                        <Grid className="w-4 h-4 text-muted-foreground" />
                        <h3 className="font-semibold text-sm">{isEasyMode ? "Couleurs" : "Motif & Animation"}</h3>
                      </div>

                      {/* Pattern Mode Selector - Masqué en mode Easy */}
                      {!isEasyMode && (
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label>
                              {t("config.patternMode") || "Mode de motif"}
                            </Label>
                            <ToggleGroup
                              type="single"
                              value={activePatternType}
                              onValueChange={(value: "custom" | "solid" | "standard") => {
                                if (value) {

                                  setActivePatternType(value);

                                  // La synchronisation automatique se fera via le useEffect de activePatternType

                                  // Si on passe en mode standard et qu'aucun motif standard n'est sélectionné, en choisir un par défaut
                                  if (value === 'standard' && config.standardPatternType === 'none') {
                                    updateConfig("standardPatternType", "smpteBar");
                                  }
                                }
                              }}
                              className="grid grid-cols-3"
                            >
                              <ToggleGroupItem value="custom">
                                {t("config.customPattern") || "Motif"}
                              </ToggleGroupItem>
                              <ToggleGroupItem value="solid">
                                {t("config.solidColor") || "Couleur unie"}
                              </ToggleGroupItem>
                              <ToggleGroupItem value="standard">
                                {t("config.standardPattern") || "Mire standard"}
                              </ToggleGroupItem>
                            </ToggleGroup>
                          </div>
                        </div>
                      )}

                      {/* Custom Pattern Type - Visible only when activePatternType is 'custom' */}
                      {activePatternType === 'custom' && (
                        <div className="space-y-2">
                          <Label>{t("config.pattern")}</Label>
                          <Select value={config.patternType ?? 'canvas'} onValueChange={(value) => updateConfig("patternType", value)}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {patternTypes.map((pattern) => (
                                <SelectItem key={pattern.value} value={pattern.value}>
                                  {pattern.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {/* Pattern Seed - masquée sur mobile, pour les patterns non-custom et en mode Easy */}
                      {!isMobile && !isEasyMode && activePatternType === 'custom' && (
                        <div className="space-y-2">
                          <Label>{t("config.patternSeed")}</Label>
                          <div className="flex gap-2">
                            <Slider
                              value={[config.patternSeed ?? 0]}
                              onValueChange={(value) => updateConfig("patternSeed", value[0])}
                              max={100}
                              min={0}
                              step={1}
                              className="w-full"
                            />
                            <MathInput
                              inputMode="numeric"
                              value={config.patternSeed ?? 0}
                              onValueChange={(value) => {
                                updateConfig("patternSeed", value);
                              }}
                              className="w-20"
                            />
                          </div>
                        </div>
                      )}

                      {/* Solid Color - Visible only when activePatternType is 'solid' */}
                      {activePatternType === 'solid' && (
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="backgroundColor">
                              {t("config.backgroundColor") || "Couleur de fond"}
                            </Label>
                            <div className="flex gap-2">
                              <Input
                                id="backgroundColor"
                                type="color"
                                value={config.backgroundColor ?? '#000000'}
                                onChange={(e) => {
                                  const newColor = e.target.value;
                                  setConfig(prev => ({ ...prev, backgroundColor: newColor }));
                                  // Redessiner immédiatement pour une fluidité parfaite
                                  requestAnimationFrame(() => drawPattern(0, true));
                                }}
                                onInput={(e) => {
                                  // Pour une réactivité en temps réel pendant la manipulation
                                  const newColor = (e.target as HTMLInputElement).value;
                                  setConfig(prev => ({ ...prev, backgroundColor: newColor }));
                                  requestAnimationFrame(() => drawPattern(0, true));
                                }}
                                className="w-12 p-1 h-10"
                              />
                              <Input
                                type="text"
                                value={config.backgroundColor ?? '#000000'}
                                onChange={(e) => {
                                  const newColor = e.target.value;
                                  setConfig(prev => ({ ...prev, backgroundColor: newColor }));

                                  // Redessiner seulement pour les couleurs valides
                                  if (/^#[0-9A-F]{6}$/i.test(newColor) || /^#[0-9A-F]{3}$/i.test(newColor)) {
                                    requestAnimationFrame(() => drawPattern(0, true));
                                  }
                                }}
                                onBlur={(e) => {
                                  // Validation finale à la perte de focus
                                  const newColor = e.target.value;
                                  if (/^#[0-9A-F]{6}$/i.test(newColor) || /^#[0-9A-F]{3}$/i.test(newColor)) {
                                    requestAnimationFrame(() => drawPattern(0, true));
                                  }
                                }}
                                placeholder="#000000"
                              />
                            </div>
                          </div>

                          {/* Couleurs rapides déplacées ici */}
                          <div className="space-y-2">
                            <Label>{t('config.quickColors')}</Label>
                            <div className="grid grid-cols-4 gap-2">
                              {["#FF0000", "#00FF00", "#0000FF", "#FFFF00", "#FF00FF", "#00FFFF", "#FFFFFF", "transparent"].map((color) => (
                                <Button
                                  key={color}
                                  variant="outline"
                                  className="h-12 p-0"
                                  style={color === "transparent" ? {
                                    backgroundColor: "white",
                                    backgroundImage: "linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)",
                                    backgroundSize: "10px 10px",
                                    backgroundPosition: "0 0, 0 5px, 5px -5px, -5px 0px"
                                  } : { backgroundColor: color }}
                                  onClick={() => {
                                    setConfig(prev => ({ ...prev, backgroundColor: color }));
                                    requestAnimationFrame(() => drawPattern(0, true));
                                  }}
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Standard Test Pattern - Visible only when activePatternType is 'standard' */}
                      {activePatternType === 'standard' && (
                        <div className="space-y-2">
                          <Label>{t("config.standardPattern")}</Label>
                          <Select
                            value={config.standardPatternType ?? 'none'}
                            onValueChange={(value) => updateConfig("standardPatternType", value)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">{t("config.none")}</SelectItem>

                              {/* Mires générales */}
                              <SelectItem value="liveVHScroll">{t("pattern.liveVHScroll")}</SelectItem>
                              <SelectItem value="liveVScroll">{t("pattern.liveVScroll")}</SelectItem>
                              <SelectItem value="lineHScroll">{t("pattern.lineHScroll")}</SelectItem>
                              <SelectItem value="grayRamp">{t("pattern.grayRamp")}</SelectItem>
                              <SelectItem value="greenRamp">{t("pattern.greenRamp")}</SelectItem>
                              <SelectItem value="redRamp">{t("pattern.redRamp")}</SelectItem>
                              <SelectItem value="blueRamp">{t("pattern.blueRamp")}</SelectItem>
                              <SelectItem value="whiteRamp">{t("pattern.whiteRamp")}</SelectItem>
                              <SelectItem value="movingBall">{t("pattern.movingBall")}</SelectItem>
                              <SelectItem value="colorBar">{t("pattern.colorBar")}</SelectItem>
                              <SelectItem value="grayBar8">{t("pattern.grayBar8")}</SelectItem>
                              <SelectItem value="grayBar16">{t("pattern.grayBar16")}</SelectItem>
                              <SelectItem value="smpteBar">{t("pattern.smpteBar")}</SelectItem>

                              {/* Écrans de couleur */}
                              <SelectItem value="redScreen">{t("pattern.redScreen")}</SelectItem>
                              <SelectItem value="blueScreen">{t("pattern.blueScreen")}</SelectItem>
                              <SelectItem value="greenScreen">{t("pattern.greenScreen")}</SelectItem>
                              <SelectItem value="cyanScreen">{t("pattern.cyanScreen")}</SelectItem>
                              <SelectItem value="yellowScreen">{t("pattern.yellowScreen")}</SelectItem>
                              <SelectItem value="blackScreen">{t("pattern.blackScreen")}</SelectItem>
                              <SelectItem value="whiteScreen">{t("pattern.whiteScreen")}</SelectItem>

                              {/* Motifs géométriques */}
                              <SelectItem value="verticalSplit">{t("pattern.verticalSplit")}</SelectItem>
                              <SelectItem value="horizontalSplit">{t("pattern.horizontalSplit")}</SelectItem>
                              <SelectItem value="quarterBlock">{t("pattern.quarterBlock")}</SelectItem>
                              <SelectItem value="alternateCheckboard">{t("pattern.alternateCheckboard")}</SelectItem>

                              {/* MIRES SPÉCIFIQUES VIDÉOPROJECTEURS */}
                              <SelectItem value="pluge">{t("pattern.pluge") || "PLUGE (Niveau noir)"}</SelectItem>
                              <SelectItem value="zonePlate">{t("pattern.zonePlate") || "Zone Plate (Convergence)"}</SelectItem>
                              <SelectItem value="crosshatch">{t("pattern.crosshatch") || "Crosshatch (Grille convergence)"}</SelectItem>
                              <SelectItem value="focusPattern">{t("pattern.focusPattern") || "Focus Pattern (Netteté)"}</SelectItem>
                              <SelectItem value="geometricDistortion">{t("pattern.geometricDistortion") || "Distorsion géométrique"}</SelectItem>
                              <SelectItem value="windowPattern">{t("pattern.windowPattern") || "Window Pattern (Positionnement)"}</SelectItem>
                              <SelectItem value="multiburst">{t("pattern.multiburst") || "Multiburst (Résolution H)"}</SelectItem>
                              <SelectItem value="gamma22">{t("pattern.gamma22") || "Gamma 2.2 Ramp"}</SelectItem>
                              <SelectItem value="whiteUniformity">{t("pattern.whiteUniformity") || "Uniformité blanc"}</SelectItem>
                              <SelectItem value="blackUniformity">{t("pattern.blackUniformity") || "Uniformité noir"}</SelectItem>
                              <SelectItem value="ansiCheckerboard">{t("pattern.ansiCheckerboard") || "ANSI Checkerboard"}</SelectItem>
                              <SelectItem value="colorTemperature">{t("pattern.colorTemperature") || "Température couleur"}</SelectItem>
                              <SelectItem value="horizontalResolution">{t("pattern.horizontalResolution") || "Résolution horizontale"}</SelectItem>
                              <SelectItem value="verticalResolution">{t("pattern.verticalResolution") || "Résolution verticale"}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {/* Direction des dégradés - Visible uniquement pour les motifs de type "Ramp" */}
                      {activePatternType === 'standard' &&
                        (config.standardPatternType === "grayRamp" ||
                          config.standardPatternType === "greenRamp" ||
                          config.standardPatternType === "redRamp" ||
                          config.standardPatternType === "blueRamp" ||
                          config.standardPatternType === "whiteRamp") && (
                          <div className="space-y-2 mt-2">
                            <Label>{t("config.rampDirection")}</Label>
                            <Select
                              value={config.rampDirection ?? 'horizontal'}
                              onValueChange={(value) => updateConfig("rampDirection", value as "horizontal" | "vertical")}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="horizontal">{t("config.horizontal")}</SelectItem>
                                <SelectItem value="vertical">{t("config.vertical")}</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}

                      {/* Animation Speed - masquée sur mobile et en mode Easy */}
                      {!isMobile && !isEasyMode && (
                        <div className="space-y-2">
                          <Label>{t("config.animationSpeed")}</Label>
                          <Slider
                            value={[config.animationSpeed ?? 1]}
                            onValueChange={(value) => updateConfig("animationSpeed", value[0])}
                            max={5}
                            min={0.1}
                            step={0.1}
                            className="w-full"
                          />
                          <div className="text-sm text-muted-foreground text-center">{(config.animationSpeed ?? 1).toFixed(1)}x</div>
                        </div>
                      )}

                      {/* ===== SECTION AFFICHAGE - Masquée en mode Easy */}
                      {!isEasyMode && (
                        <>
                          <div className="flex items-center gap-2 pb-2 border-b mt-6">
                            <Monitor className="w-4 h-4 text-muted-foreground" />
                            <h3 className="font-semibold text-sm">Affichage</h3>
                          </div>

                          {/* Rotation */}
                          <div className="space-y-2">
                        <Label>Rotation</Label>
                        <Select
                          value={String(config.rotationDeg ?? 0)}
                          onValueChange={(value) => {
                            const map: Record<string, 0 | 90 | 180 | -90> = {
                              "0": 0,
                              "90": 90,
                              "180": 180,
                              "-90": -90,
                            };
                            const rotationDeg = map[value] ?? 0;

                            setConfig(prev => ({ ...prev, rotationDeg }));

                            if (broadcastChannelRef.current) {
                              broadcastChannelRef.current.postMessage({
                                type: 'CONFIG_UPDATE',
                                data: { rotationDeg }
                              });
                            }
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0">0°</SelectItem>
                            <SelectItem value="90">+90°</SelectItem>
                            <SelectItem value="180">+180°</SelectItem>
                            <SelectItem value="-90">-90°</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Show Resolution Toggle */}
                      <div className="flex items-center justify-between">
                        <Label htmlFor="showResolution">{t("config.showResolution")}</Label>
                        <Switch
                          id="showResolution"
                          checked={config.showResolution}
                          onCheckedChange={(checked) => {
                            setConfig(prev => ({
                              ...prev,
                              showResolution: checked,
                              showTechnicalDetails: prev.showGrid && checked && prev.showLEDNumbers
                            }));

                            // Broadcaster la mise à jour si en mode multi-écran
                            if (broadcastChannelRef.current) {
                              broadcastChannelRef.current.postMessage({
                                type: 'CONFIG_UPDATE',
                                data: { showResolution: checked }
                              });
                            }
                          }}
                        />
                      </div>

                      {/* Resolution Font Size Slider - Only show when showResolution is enabled */}
                      {config.showResolution && (
                        <div className="space-y-2 pl-4">
                          <div className="flex items-center justify-between">
                            <Label htmlFor="resolutionFontSize" className="text-sm text-foreground">
                              {t("config.resolutionFontSize") || "Resolution Size"}
                            </Label>
                            <span className="text-sm text-foreground w-12 text-right">
                              {config.resolutionFontSize || 24}px
                            </span>
                          </div>
                          <Slider
                            id="resolutionFontSize"
                            min={12}
                            max={72}
                            step={1}
                            value={[config.resolutionFontSize || 24]}
                            onValueChange={(value) => {
                              setConfig(prev => ({
                                ...prev,
                                resolutionFontSize: value[0]
                              }));

                              // Broadcaster la mise à jour si en mode multi-écran
                              if (broadcastChannelRef.current) {
                                broadcastChannelRef.current.postMessage({
                                  type: 'CONFIG_UPDATE',
                                  data: { resolutionFontSize: value[0] }
                                });
                              }
                            }}
                          />
                        </div>
                      )}

                      {/* Show Default Logo Toggle - Activé: logo au centre, Désactivé: logo en bas à droite */}
                      <div className="flex items-center justify-between">
                        <Label htmlFor="showDefaultLogo">{t("config.showDefaultLogo")}</Label>
                        <Switch
                          id="showDefaultLogo"
                          checked={config.showDefaultLogo}
                          onCheckedChange={(checked) => {
                            setConfig(prev => ({
                              ...prev,
                              showDefaultLogo: checked
                            }));

                            // Broadcaster la mise à jour si en mode multi-écran
                            if (broadcastChannelRef.current) {
                              broadcastChannelRef.current.postMessage({
                                type: 'CONFIG_UPDATE',
                                data: { showDefaultLogo: checked }
                              });
                            }
                          }}
                        />
                      </div>

                      {/* Pixel Map Mode Toggle (Premium) */}
                      {isPro && (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label htmlFor="pixelMapMode" className="flex items-center gap-1">
                              {t("config.pixelMapMode")}
                              <span className="text-xs bg-yellow-500/20 text-yellow-400 px-1 rounded">PRO</span>
                            </Label>
                            <Switch
                              id="pixelMapMode"
                              checked={config.pixelMapMode?.enabled ?? false}
                              onCheckedChange={(checked) => {
                                setConfig(prev => ({
                                  ...prev,
                                  pixelMapMode: {
                                    ...prev.pixelMapMode,
                                    enabled: checked,
                                    resW: prev.width,
                                    resH: prev.height,
                                    panelW: prev.ledWallMode?.panelWidth || prev.gridSize || 192,
                                    panelH: prev.ledWallMode?.panelHeight || prev.gridSize || 192,
                                  },
                                  showDefaultLogo: !checked
                                }));
                                if (broadcastChannelRef.current) {
                                  broadcastChannelRef.current.postMessage({ type: 'CONFIG_UPDATE', data: { pixelMapMode: { ...(config.pixelMapMode ?? {}), enabled: checked }, showDefaultLogo: !checked } });
                                }
                              }}
                            />
                          </div>

                          {config.pixelMapMode?.enabled && (
                            <div className="pl-4 space-y-2 border-l border-border">
                              {/* Titre principal */}
                              <div className="space-y-1">
                                <Label className="text-xs text-muted-foreground">{t("config.pixelMapMode.mainScreenText")}</Label>
                                <input
                                  type="text"
                                  className="w-full rounded border border-input bg-background px-2 py-1 text-sm text-foreground"
                                  value={config.pixelMapMode.mainScreenText}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    setConfig(prev => ({ ...prev, pixelMapMode: { ...prev.pixelMapMode, mainScreenText: v } }));
                                  }}
                                />
                              </div>

                              {/* Position X / Y */}
                              <div className="space-y-1">
                                <Label className="text-xs text-muted-foreground">{t("config.pixelMapMode.position")}</Label>
                                <div className="flex gap-2">
                                  <MathInput
                                    inputMode="numeric"
                                    className="w-full rounded border border-input bg-background px-2 py-1 text-sm text-foreground"
                                    placeholder="X"
                                    value={config.pixelMapMode.posX ?? 0}
                                    onValueChange={(v) => {
                                      setConfig(prev => ({ ...prev, pixelMapMode: { ...prev.pixelMapMode, posX: v } }));
                                    }}
                                  />
                                  <MathInput
                                    inputMode="numeric"
                                    className="w-full rounded border border-input bg-background px-2 py-1 text-sm text-foreground"
                                    placeholder="Y"
                                    value={config.pixelMapMode.posY ?? 0}
                                    onValueChange={(v) => {
                                      setConfig(prev => ({ ...prev, pixelMapMode: { ...prev.pixelMapMode, posY: v } }));
                                    }}
                                  />
                                </div>
                              </div>

                              {/* Résolution mire */}
                              <div className="space-y-1">
                                <Label className="text-xs text-muted-foreground">{t("config.pixelMapMode.resolution")}</Label>
                                <div className="flex gap-2">
                                  <MathInput
                                    inputMode="numeric"
                                    className="w-full rounded border border-input bg-background px-2 py-1 text-sm text-foreground"
                                    placeholder="W"
                                    value={config.pixelMapMode.resW ?? 0}
                                    onValueChange={(v) => {
                                      setConfig(prev => ({ ...prev, pixelMapMode: { ...prev.pixelMapMode, resW: v } }));
                                    }}
                                  />
                                  <MathInput
                                    inputMode="numeric"
                                    className="w-full rounded border border-input bg-background px-2 py-1 text-sm text-foreground"
                                    placeholder="H"
                                    value={config.pixelMapMode.resH ?? 0}
                                    onValueChange={(v) => {
                                      setConfig(prev => ({ ...prev, pixelMapMode: { ...prev.pixelMapMode, resH: v } }));
                                    }}
                                  />
                                </div>
                              </div>

                              {/* Taille des panneaux */}
                              <div className="space-y-1">
                                <Label className="text-xs text-muted-foreground">{t("config.pixelMapMode.panel")}</Label>
                                <div className="flex gap-2">
                                  <MathInput
                                    inputMode="numeric"
                                    className="w-full rounded border border-input bg-background px-2 py-1 text-sm text-foreground"
                                    placeholder="W"
                                    value={config.pixelMapMode.panelW ?? 0}
                                    onValueChange={(v) => {
                                      setConfig(prev => ({ ...prev, pixelMapMode: { ...prev.pixelMapMode, panelW: v } }));
                                    }}
                                  />
                                  <MathInput
                                    inputMode="numeric"
                                    className="w-full rounded border border-input bg-background px-2 py-1 text-sm text-foreground"
                                    placeholder="H"
                                    value={config.pixelMapMode.panelH ?? 0}
                                    onValueChange={(v) => {
                                      setConfig(prev => ({ ...prev, pixelMapMode: { ...prev.pixelMapMode, panelH: v } }));
                                    }}
                                  />
                                </div>
                              </div>

                              {/* Size Slider */}
                              <div className="space-y-1">
                                <div className="flex items-center justify-between">
                                  <Label className="text-xs text-muted-foreground">{t("config.pixelMapMode.size")}</Label>
                                  <span className="text-xs text-muted-foreground">
                                    {Math.round((config.pixelMapMode.size ?? 1.0) * 100)}%
                                  </span>
                                </div>
                                <Slider
                                  value={[config.pixelMapMode.size ?? 1.0]}
                                  onValueChange={(value) => {
                                    setConfig(prev => ({ ...prev, pixelMapMode: { ...prev.pixelMapMode, size: value[0] } }));
                                  }}
                                  min={0.2}
                                  max={5.0}
                                  step={0.1}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                        </>
                      )}

                      {/* ===== SECTION SONAR - Masquée en mode Easy */}
                      {!isEasyMode && (
                        <>
                          <div className="flex items-center gap-2 pb-2 border-b mt-6">
                            <Zap className="w-4 h-4 text-muted-foreground" />
                            <h3 className="font-semibold text-sm">Sonar</h3>
                          </div>

                      {/* Show Sonar Toggle */}
                      <div className="flex items-center justify-between">
                        <Label htmlFor="showSonar">{t("config.showSonar")}</Label>
                        <Switch
                          id="showSonar"
                          checked={config.showSonar}
                          onCheckedChange={(checked) => {
                            setConfig(prev => ({
                              ...prev,
                              showSonar: checked
                            }));

                            // Broadcaster la mise à jour si en mode multi-écran
                            if (broadcastChannelRef.current) {
                              broadcastChannelRef.current.postMessage({
                                type: 'CONFIG_UPDATE',
                                data: { showSonar: checked }
                              });
                            }
                          }}
                        />
                      </div>

                      {/* Sonar Reveal Mode Toggle - affiché seulement si Show Sonar est activé */}
                      {config.showSonar && (
                        <div className="flex items-center justify-between">
                          <Label htmlFor="sonarRevealMode">{t("config.sonarMode")}</Label>
                          <Switch
                            id="sonarRevealMode"
                            checked={config.sonarRevealMode}
                            onCheckedChange={(checked) => updateConfig("sonarRevealMode", checked)}
                          />
                        </div>
                      )}

                      {/* Sonar Thickness Slider */}
                      {config.showSonar && (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label htmlFor="sonarThickness">{t("config.sonarThickness") || "Épaisseur du sonar"}</Label>
                            <div className="flex items-center gap-2">
                              <MathInput
                                inputMode="numeric"
                                value={config.sonarThickness ?? 4}
                                onValueChange={(value) => {
                                  const clampedValue = Math.max(1, Math.min(20, value));
                                  setConfig(prev => ({
                                    ...prev,
                                    sonarThickness: clampedValue
                                  }));

                                  // Broadcaster la mise à jour si en mode multi-écran
                                  if (broadcastChannelRef.current) {
                                    broadcastChannelRef.current.postMessage({
                                      type: 'CONFIG_UPDATE',
                                      data: { sonarThickness: clampedValue }
                                    });
                                  }
                                }}
                                className="w-16 h-8 text-center text-sm"
                              />
                              <span className="text-sm text-muted-foreground">px</span>
                            </div>
                          </div>
                          <Slider
                            id="sonarThickness"
                            min={1}
                            max={20}
                            step={1}
                            value={[config.sonarThickness ?? 4]}
                            onValueChange={(value) => {
                              setConfig(prev => ({
                                ...prev,
                                sonarThickness: value[0]
                              }));

                              // Broadcaster la mise à jour si en mode multi-écran
                              if (broadcastChannelRef.current) {
                                broadcastChannelRef.current.postMessage({
                                  type: 'CONFIG_UPDATE',
                                  data: { sonarThickness: value[0] }
                                });
                              }
                            }}
                          />
                        </div>
                      )}

                      {/* Sonar Speed - masquée sur mobile et si Show Sonar est off */}
                      {!isMobile && config.showSonar && (
                        <div className="space-y-2">
                          <Label>{t("config.sonarSpeed")}</Label>
                          <Slider
                            value={[config.sonarSpeed ?? 0.2]}
                            onValueChange={(value) => updateConfig("sonarSpeed", value[0])}
                            max={5}
                            min={0.1}
                            step={0.1}
                            className="w-full"
                          />
                          <div className="text-sm text-muted-foreground text-center">{(config.sonarSpeed ?? 0.2).toFixed(1)}x</div>
                        </div>
                      )}
                        </>
                      )}

                      {/* ===== SECTION FULLSCREEN - Masquée en mode Easy */}
                      {!isEasyMode && (
                        <>
                          <div className="flex items-center gap-2 pb-2 border-b mt-6">
                            <Maximize className="w-4 h-4 text-muted-foreground" />
                            <h3 className="font-semibold text-sm">Fullscreen settings</h3>
                          </div>

                      {/* Fullscreen Scroll Toggle - masquée sur mobile */}
                      {!isMobile && (
                        <div className="flex items-center justify-between">
                          <Label htmlFor="allowFullscreenScroll">{t("config.fullscreenScroll")}</Label>
                          <Switch
                            id="allowFullscreenScroll"
                            checked={config.allowFullscreenScroll}
                            onCheckedChange={(checked) => updateConfig("allowFullscreenScroll", checked)}
                          />
                        </div>
                      )}

                      {/* Fullscreen Stretch Toggle */}
                      <div className="flex items-center justify-between">
                        <Label htmlFor="stretchFullscreen">{t("config.stretchFullscreen")}</Label>
                        <Switch
                          id="stretchFullscreen"
                          checked={config.stretchFullscreen}
                          onCheckedChange={(checked) => updateConfig("stretchFullscreen", checked)}
                        />
                      </div>
                        </>
                      )}
                    </CardContent>
                  </Card>

                  {/* Dialogue de sauvegarde */}
                  {showSaveDialog && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                      <Card className="w-full max-w-md">
                        <CardHeader>
                          <CardTitle>{t("config.saveTitle")}</CardTitle>
                          <CardDescription>{t("config.configName")}</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="configName">{t("config.technicalName")}</Label>
                            <Input
                              id="configName"
                              placeholder={t("config.technicalNamePlaceholder")}
                              value={configName}
                              onChange={(e) => setConfigName(e.target.value)}
                            />
                            <p className="text-xs text-muted-foreground">
                              {t("config.technicalNameDesc")}
                            </p>
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="configDisplayName">{t("config.displayName")}</Label>
                            <Input
                              id="configDisplayName"
                              placeholder={t("config.displayNamePlaceholder")}
                              value={configDisplayName}
                              onChange={(e) => setConfigDisplayName(e.target.value)}
                            />
                            <p className="text-xs text-muted-foreground">
                              {t("config.displayNameDesc")}
                            </p>
                          </div>

                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="makeDefault"
                              checked={makeDefaultConfig}
                              onCheckedChange={(checked) => setMakeDefaultConfig(!!checked)}
                            />
                            <Label htmlFor="makeDefault">{t("config.makeDefault")}</Label>
                          </div>
                        </CardContent>
                        <div className="flex justify-end p-4 space-x-2">
                          <Button variant="outline" onClick={() => setShowSaveDialog(false)}>
                            {t("config.cancel")}
                          </Button>
                          <Button onClick={handleSaveConfigConfirm}>
                            {t("config.save")}
                          </Button>
                        </div>
                      </Card>
                    </div>
                  )}

                  {/* Dialogue de chargement */}
                  {showLoadDialog && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                      <Card className="w-full max-w-md max-h-[80vh] overflow-auto">
                        <CardHeader>
                          <CardTitle>{t("config.loadTitle")}</CardTitle>
                          <CardDescription>{t("config.selectConfig")}</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          {savedConfigs.length === 0 ? (
                            <p className="text-center py-4 text-muted-foreground">{t("config.noSavedConfigs")}</p>
                          ) : (
                            <div className="space-y-2">
                              {savedConfigs.map((config) => (
                                <div key={config.name} className="flex items-center justify-between border p-2 rounded-md">
                                  <div>
                                    <p className="font-medium">{config.displayName}</p>
                                    <p className="text-xs text-muted-foreground">
                                      {new Date(config.date).toLocaleDateString()} {new Date(config.date).toLocaleTimeString()}
                                      {config.isDefault && <span className="ml-2 text-green-500">{t("config.default")}</span>}
                                    </p>
                                  </div>
                                  <div className="flex space-x-1">
                                    <Button size="sm" onClick={() => handleLoadSpecificConfig(config.name)}>
                                      Charger
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={async () => {
                                        // Charger la configuration puis l'exporter
                                        const result = await loadConfig(config.name);
                                        if (result.success && result.config) {
                                          const filename = `${config.name}.visualgrid`;
                                          await exportConfigFile(result.config, filename);
                                          toast.success(t('toast.configExported'));
                                        }
                                      }}
                                    >
                                      <Download className="w-4 h-4" />
                                    </Button>
                                    <Button size="sm" variant="destructive" onClick={() => handleDeleteConfig(config.name)}>
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          <div className="border-t pt-4">
                            <p className="text-sm font-medium mb-2">{t("config.actions")}</p>
                            <div className="flex flex-wrap gap-2">
                              {/* Export .visualgrid (second) - masqué sur mobile */}
                              {!isMobile && (
                                <Button size="sm" variant="outline" onClick={handleExportConfigFile}>
                                  <Download className="w-4 h-4 mr-1" />
                                  {t("config.exportCurrent")}
                                </Button>
                              )}
                              <Button size="sm" variant="outline" onClick={handleImportConfigFile}>
                                <Upload className="w-4 h-4 mr-1" />
                                {t("config.importFile")}
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                        <div className="flex justify-end p-4">
                          <Button variant="outline" onClick={() => setShowLoadDialog(false)}>
                            {t("config.close")}
                          </Button>
                        </div>
                      </Card>
                    </div>
                  )}

                  {/* Logo Management - Masqué sur mobile en session */}
                  {!(effectiveIsMobile && isInSession) && (
                  <Card className="relative">
                    {/* Overlay pendant l'export */}
                    {isWebCodecsExporting && (
                      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-10 rounded-lg" />
                    )}
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <ImageIcon className="w-4 h-4" />
                        {t("logo.title")}
                      </CardTitle>
                      <CardDescription>{t("logo.description")}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                      <Button
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full bg-transparent"
                        variant="outline"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        {t("logo.import")}
                      </Button>

                      {logoElements.length > 0 && (
                        <div className="space-y-2">
                          <Label>{t("logo.imported")}</Label>
                          {logoElements.map((logo) => (
                            <div key={logo.id} className="flex items-center gap-2">
                              <Button
                                variant={selectedLogoId === logo.id ? "default" : "outline"}
                                size="sm"
                                className="flex-1 justify-start"
                                onClick={() => {
                                  setSelectedLogoId(logo.id)
                                  setSelectedTextId(null)
                                  setShowLogoPanel(true)
                                  setShowTextPanel(false)
                                }}
                              >
                                Logo {logo.id.slice(-4)}
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => deleteLogoElement(logo.id)}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}

                      {selectedLogo && showLogoPanel && (
                        <div className="space-y-4 border-t pt-4">
                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-2">
                              <Label>{t("logo.width")}</Label>
                              <MathInput
                                inputMode="numeric"
                                value={Math.round(selectedLogo.width)}
                                onValueChange={(newWidth) => {
                                  const aspectRatio = selectedLogo.originalWidth / selectedLogo.originalHeight
                                  updateLogoElement(selectedLogo.id, {
                                    width: newWidth,
                                    height: newWidth / aspectRatio,
                                  })
                                }}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>{t("logo.height")}</Label>
                              <MathInput
                                inputMode="numeric"
                                value={Math.round(selectedLogo.height)}
                                onValueChange={(newHeight) => {
                                  const aspectRatio = selectedLogo.originalWidth / selectedLogo.originalHeight
                                  updateLogoElement(selectedLogo.id, {
                                    height: newHeight,
                                    width: newHeight * aspectRatio,
                                  })
                                }}
                              />
                            </div>
                          </div>

                          <div className="space-y-2">
                            <Label>{t("logo.opacity")}</Label>
                            <Slider
                              value={[selectedLogo.opacity]}
                              onValueChange={(value) => updateLogoElement(selectedLogo.id, { opacity: value[0] })}
                              max={1}
                              min={0}
                              step={0.1}
                              className="w-full"
                            />
                            <div className="text-sm text-muted-foreground text-center">
                              {Math.round(selectedLogo.opacity * 100)}%
                            </div>
                          </div>

                          {/* Logo Alignment */}
                          <div className="space-y-2">
                            <Label>{t("logo.horizontalAlignment")}</Label>
                            <div className="flex gap-1">
                              {textAlignments.map((alignment) => (
                                <Button
                                  key={alignment.value}
                                  variant="outline"
                                  size="sm"
                                  onClick={() => alignLogo(alignment.value)}
                                  className="flex-1"
                                >
                                  <alignment.icon className="w-3 h-3" />
                                </Button>
                              ))}
                            </div>
                          </div>

                          {/* Vertical Alignment */}
                          <div className="space-y-2">
                            <Label>{t("logo.verticalAlignment")}</Label>
                            <div className="flex gap-1">
                              <Button variant="outline" size="sm" onClick={() => alignLogoVertical("top")} className="flex-1">
                                {t("logo.top")}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => alignLogoVertical("middle")}
                                className="flex-1"
                              >
                                {t("logo.middle")}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => alignLogoVertical("bottom")}
                                className="flex-1"
                              >
                                {t("logo.bottom")}
                              </Button>
                            </div>
                          </div>

                          {/* Position in pixels */}
                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-2">
                              <Label>{t("logo.positionX")}</Label>
                              <MathInput
                                inputMode="numeric"
                                value={Math.round(selectedLogo.x)}
                                onValueChange={(v) => updateLogoElement(selectedLogo.id, { x: v })}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>{t("logo.positionY")}</Label>
                              <MathInput
                                inputMode="numeric"
                                value={Math.round(selectedLogo.y)}
                                onValueChange={(v) => updateLogoElement(selectedLogo.id, { y: v })}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  )}

                  {/* Text Management */}
                  <Card className="relative">
                    {/* Overlay pendant l'export */}
                    {isWebCodecsExporting && (
                      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-10 rounded-lg" />
                    )}
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Type className="w-4 h-4" />
                        {t("text.title")}
                      </CardTitle>
                      <CardDescription>{t("text.description")}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <Button onClick={addTextElement} className="w-full bg-transparent" variant="outline">
                        <Plus className="w-4 h-4 mr-2" />
                        {t("text.add")}
                      </Button>

                      {textElements.length > 0 && (
                        <div className="space-y-2">
                          <Label>{t("text.elements")}</Label>
                          {textElements.map((text) => (
                            <div key={text.id} className="flex items-center gap-2">
                              <Button
                                variant={selectedTextId === text.id ? "default" : "outline"}
                                size="sm"
                                className="flex-1 justify-start"
                                onClick={() => {
                                  setSelectedTextId(text.id)
                                  setSelectedLogoId(null)
                                  setShowTextPanel(true)
                                  setShowLogoPanel(false)
                                }}
                              >
                                {text.text.substring(0, 15)}...
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => deleteTextElement(text.id)}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}

                      {selectedText && showTextPanel && (
                        <div className="space-y-4 border-t pt-4">
                          <div className="space-y-2">
                            <Label>{t("text.text")}</Label>
                            <Textarea
                              value={selectedText.text}
                              onChange={(e) => updateTextElement(selectedText.id, { text: e.target.value })}
                              placeholder={t("text.placeholder")}
                            />
                          </div>

                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-2">
                              <Label>{t("text.size")}</Label>
                              <MathInput
                                inputMode="numeric"
                                value={selectedText.fontSize}
                                onValueChange={(v) => updateTextElement(selectedText.id, { fontSize: v })}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>{t("text.color")}</Label>
                              <Input
                                type="color"
                                value={selectedText.color}
                                onChange={(e) => updateTextElement(selectedText.id, { color: e.target.value })}
                              />
                            </div>
                          </div>

                          <div className="space-y-2">
                            <Label>{t("text.font")}</Label>
                            <Select
                              value={selectedText.fontFamily}
                              onValueChange={(value) => updateTextElement(selectedText.id, { fontFamily: value })}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {fontFamilies.map((font) => (
                                  <SelectItem key={font.name} value={font.value}>
                                    {font.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-2">
                            <Label>{t("text.weight")}</Label>
                            <Slider
                              value={[selectedText.fontWeight]}
                              onValueChange={(value) => updateTextElement(selectedText.id, { fontWeight: value[0] })}
                              max={1000}
                              min={100}
                              step={50}
                              className="w-full"
                            />
                            <div className="text-sm text-muted-foreground text-center">
                              {selectedText.fontWeight}
                              {selectedText.fontWeight <= 300 && ` (${t("text.light")})`}
                              {selectedText.fontWeight > 300 && selectedText.fontWeight <= 500 && ` (${t("text.normal")})`}
                              {selectedText.fontWeight > 500 && selectedText.fontWeight <= 700 && ` (${t("text.bold")})`}
                              {selectedText.fontWeight > 700 && selectedText.fontWeight <= 900 && ` (${t("text.ultrabold")})`}
                              {selectedText.fontWeight > 900 && ` (${t("text.extrabold")})`}
                            </div>
                          </div>

                          <div className="space-y-2">
                            <Label>{t("text.stroke")}</Label>
                            <Slider
                              value={[selectedText.strokeWidth || 0]}
                              onValueChange={(value) => updateTextElement(selectedText.id, { strokeWidth: value[0] })}
                              max={10}
                              min={0}
                              step={1}
                              className="w-full"
                            />
                            <div className="text-sm text-muted-foreground text-center">{selectedText.strokeWidth || 0}px</div>
                          </div>

                          <div className="space-y-2">
                            <Label>{t("text.style")}</Label>
                            <Select
                              value={selectedText.fontStyle}
                              onValueChange={(value) => updateTextElement(selectedText.id, { fontStyle: value })}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {fontStyles.map((style) => (
                                  <SelectItem key={style.value} value={style.value}>
                                    {style.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Text Alignment */}
                          <div className="space-y-2">
                            <Label>{t("text.textAlign")}</Label>
                            <div className="flex gap-1">
                              {textAlignments.map((alignment) => (
                                <Button
                                  key={alignment.value}
                                  variant={selectedText.textAlign === alignment.value ? "default" : "outline"}
                                  size="sm"
                                  onClick={() => alignText(alignment.value)}
                                  className="flex-1"
                                >
                                  <alignment.icon className="w-3 h-3" />
                                </Button>
                              ))}
                            </div>
                          </div>

                          {/* Vertical Alignment */}
                          <div className="space-y-2">
                            <Label>{t("logo.verticalAlignment")}</Label>
                            <div className="flex gap-1">
                              <Button variant="outline" size="sm" onClick={() => alignVertical("top")} className="flex-1">
                                {t("logo.top")}
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => alignVertical("middle")} className="flex-1">
                                {t("logo.middle")}
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => alignVertical("bottom")} className="flex-1">
                                {t("logo.bottom")}
                              </Button>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-2">
                              <Label>{t("logo.positionX")}</Label>
                              <MathInput
                                inputMode="numeric"
                                value={Math.round(selectedText.x)}
                                onValueChange={(v) => updateTextElement(selectedText.id, { x: v })}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>{t("logo.positionY")}</Label>
                              <MathInput
                                inputMode="numeric"
                                value={Math.round(selectedText.y)}
                                onValueChange={(v) => updateTextElement(selectedText.id, { y: v })}
                              />
                            </div>
                          </div>

                          <div className="text-xs text-muted-foreground space-y-1">
                            {t("text.tips").split("\n").map((line, i) => (
                              <p key={i}>{line}</p>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Module Self-Test - Masqué en mode Easy */}
                  {!isEasyMode && config.ledWallMode?.enabled && !isEmbeddedInSynoptic && (
                    <Card className="relative">
                      {/* Overlay pendant l'export */}
                      {isWebCodecsExporting && (
                        <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-10 rounded-lg" />
                      )}
                      <CardHeader>
                        <CardTitle>{t('selfTest.title')}</CardTitle>
                        <CardDescription>{t('selfTest.description')}</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {!isEmbeddedInSynoptic && (
                          <>
                            <div className="flex items-center justify-between">
                              <Label htmlFor="selfTestEnabled" className="flex items-center gap-2">
                                {t('selfTest.title')}
                                {effectiveIsMobile && isInSession && isSessionRemote && (
                                  <span className="text-xs text-muted-foreground">(Contrôle à distance)</span>
                                )}
                              </Label>
                              <Switch
                                id="selfTestEnabled"
                                checked={selfTestMode.enabled}
                                onCheckedChange={(checked) => {
                                  // NOUVEAU : Si nous sommes sur mobile en mode remote, envoyer la commande à l'Host
                                  if (isMobile && isInSession && isSessionRemote && sendSyncMessage) {

                                    // Mettre à jour l'état local immédiatement pour feedback instantané
                                    if (checked) {
                                      const panelKey = `1,1`;
                                      setSelfTestMode(prev => ({
                                        ...prev,
                                        enabled: true,
                                        selectedPanelX: 1,
                                        selectedPanelY: 1,
                                        showBorders: true,
                                        activePanels: new Set([panelKey])
                                      }));
                                      // Réinitialiser les autres états
                                      setCyanPanels(new Set());
                                      setSubdivisionMode(false);
                                      setRedZones(new Set());
                                      setSelectedQuadrant('tl');
                                    } else {
                                      setSelfTestMode(prev => ({
                                        ...prev,
                                        enabled: false,
                                        showBorders: true,
                                        activePanels: new Set()
                                      }));
                                      // Nettoyer les autres états
                                      setCyanPanels(new Set());
                                      setSubdivisionMode(false);
                                      setRedZones(new Set());
                                      setSelectedQuadrant('tl');
                                    }

                                    // Puis envoyer la commande à l'Host pour synchronisation
                                    sendSyncMessage('SELFTEST_TOGGLE_MODE', { enabled: checked });
                                    return;
                                  }

                                  // Comportement normal pour l'Host ou mode local
                                  if (checked) {
                                    // Activer le mode et sélectionner le panneau 1,1
                                    const panelKey = `1,1`;
                                    setSelfTestMode(prev => ({
                                      ...prev,
                                      enabled: true,
                                      selectedPanelX: 1,
                                      selectedPanelY: 1,
                                      showBorders: true, // Par défaut, afficher les contours
                                      activePanels: new Set([panelKey])
                                    }));
                                  } else {
                                    // Désactiver le mode et vider les panneaux actifs
                                    setSelfTestMode(prev => ({
                                      ...prev,
                                      enabled: false,
                                      showBorders: true,
                                      activePanels: new Set()
                                    }));

                                    // Réinitialiser tous les panneaux cyan et rouge
                                    setCyanPanels(new Set<string>());
                                    setRedZones(new Set<string>());
                                    setSubdivisionMode(false);
                                    setSelectedQuadrant('tl');

                                    // Synchroniser la réinitialisation vers l'écran externe
                                    if (broadcastChannelRef.current) {
                                      broadcastChannelRef.current.postMessage({
                                        type: 'CYAN_PANELS_UPDATE',
                                        data: { cyanPanels: [] }
                                      });

                                      broadcastChannelRef.current.postMessage({
                                        type: 'SUBDIVISION_UPDATE',
                                        data: {
                                          subdivisionMode: false,
                                          redZones: [],
                                          selectedQuadrant: 'tl'
                                        }
                                      });
                                    }
                                  }
                                }}
                              />
                            </div>

                            {selfTestMode.enabled && (
                              <>
                                {/* Position X/Y - MASQUÉ SUR MOBILE */}
                                {!(isMobile && isInSession && isSessionRemote) && (
                                  <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                      <Label htmlFor="panelX">{t('selfTest.positionX')}</Label>
                                      <MathInput
                                        id="panelX"
                                        inputMode="numeric"
                                        value={selfTestMode.selectedPanelX}
                                        onValueChange={(newX) => {
                                          if (newX >= 1 && newX <= (config.ledWallMode?.columns || 10)) {
                                            const panelKey = `${newX},${selfTestMode.selectedPanelY}`;

                                            let newActivePanels = selfTestMode.trailMode
                                              ? new Set(selfTestMode.activePanels)
                                              : new Set<string>();

                                            newActivePanels.add(panelKey);

                                            // Marquer la navigation
                                            isNavigatingRef.current = true;

                                            // Forcer un redessinage immédiat
                                            tempSelfTestModeRef.current = {
                                              ...selfTestMode,
                                              enabled: true,
                                              selectedPanelX: newX,
                                              activePanels: newActivePanels,
                                              showBorders: selfTestMode.showBorders // Préserver l'état des bordures
                                            };

                                            drawPattern();

                                            setSelfTestMode(prev => ({
                                              ...prev,
                                              selectedPanelX: newX,
                                              activePanels: newActivePanels
                                            }));

                                            setTimeout(() => {
                                              tempSelfTestModeRef.current = null;
                                              isNavigatingRef.current = false;
                                            }, 150);
                                          }
                                        }}
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <Label htmlFor="panelY">{t('selfTest.positionY')}</Label>
                                      <MathInput
                                        id="panelY"
                                        inputMode="numeric"
                                        value={selfTestMode.selectedPanelY}
                                        onValueChange={(newY) => {
                                          if (newY >= 1 && newY <= (config.ledWallMode?.rows || 6)) {
                                            const panelKey = `${selfTestMode.selectedPanelX},${newY}`;

                                            let newActivePanels = selfTestMode.trailMode
                                              ? new Set(selfTestMode.activePanels)
                                              : new Set<string>();

                                            newActivePanels.add(panelKey);

                                            // Marquer la navigation
                                            isNavigatingRef.current = true;

                                            // Forcer un redessinage immédiat
                                            tempSelfTestModeRef.current = {
                                              ...selfTestMode,
                                              enabled: true,
                                              selectedPanelY: newY,
                                              activePanels: newActivePanels,
                                              showBorders: selfTestMode.showBorders // Préserver l'état des bordures
                                            };

                                            drawPattern();

                                            setSelfTestMode(prev => ({
                                              ...prev,
                                              selectedPanelY: newY,
                                              activePanels: newActivePanels
                                            }));

                                            setTimeout(() => {
                                              tempSelfTestModeRef.current = null;
                                              isNavigatingRef.current = false;
                                            }, 150);
                                          }
                                        }}
                                      />
                                    </div>
                                  </div>
                                )}

                                <div className="flex items-center justify-between">
                                  <Label htmlFor="trailMode">{t('selfTest.dragMode')}</Label>
                                  <Switch
                                    id="trailMode"
                                    checked={selfTestMode.trailMode}
                                    onCheckedChange={(checked) => {
                                      setSelfTestMode(prev => ({
                                        ...prev,
                                        trailMode: checked
                                      }));
                                    }}
                                  />
                                </div>

                                <div className="flex items-center justify-between">
                                  <Label htmlFor="showBorders">{t('selfTest.showOutlines')}</Label>
                                  <Switch
                                    id="showBorders"
                                    checked={selfTestMode.showBorders}
                                    onCheckedChange={(checked) => {
                                      setSelfTestMode(prev => ({
                                        ...prev,
                                        showBorders: checked
                                      }));
                                    }}
                                  />
                                </div>

                                {/* Mode PCB - MASQUÉ SUR MOBILE */}
                                {!(isMobile && isInSession && isSessionRemote) && (
                                  <div className="flex items-center justify-between">
                                    <Label htmlFor="subdivisionMode">
                                      {t('selfTest.pcbMode')}
                                      <div className="text-xs text-muted-foreground font-normal">
                                        {t('selfTest.pcbModeDesc')}
                                      </div>
                                    </Label>
                                    <Switch
                                      id="subdivisionMode"
                                      checked={subdivisionMode}
                                      onCheckedChange={(checked) => {
                                        setSubdivisionMode(checked);
                                        if (!checked) {
                                          // Réinitialiser les zones rouges quand on désactive
                                          setRedZones(new Set());
                                          setSelectedQuadrant('tl');
                                        }

                                        // Synchroniser vers l'externe
                                        if (broadcastChannelRef.current) {
                                          broadcastChannelRef.current.postMessage({
                                            type: 'SUBDIVISION_UPDATE',
                                            data: {
                                              subdivisionMode: checked,
                                              redZones: checked ? Array.from(redZones) : [],
                                              selectedQuadrant: checked ? selectedQuadrant : 'tl'
                                            }
                                          });
                                        }
                                      }}
                                    />
                                  </div>
                                )}

                                <div className="flex gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      setSelfTestMode(prev => ({
                                        ...prev,
                                        activePanels: new Set()
                                      }));
                                    }}
                                  >
                                    {t('selfTest.turnOffAll')}
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      const allPanels = new Set<string>();
                                      for (let x = 1; x <= (config.ledWallMode?.columns || 10); x++) {
                                        for (let y = 1; y <= (config.ledWallMode?.rows || 6); y++) {
                                          allPanels.add(`${x},${y}`);
                                        }
                                      }
                                      setSelfTestMode(prev => ({
                                        ...prev,
                                        activePanels: allPanels
                                      }));
                                    }}
                                  >
                                    {t('selfTest.turnOnAll')}
                                  </Button>
                                </div>

                                {/* Instructions détaillées - MASQUÉES SUR MOBILE */}
                                {!(isMobile && isInSession && isSessionRemote) && (
                                  <div className="text-xs text-muted-foreground space-y-1">
                                    <div>{t('selfTest.keyboardNavTip')}</div>
                                    <div>{t('selfTest.clickPanelTip')}</div>
                                    <div>{t('selfTest.spaceTip')}</div>
                                    {subdivisionMode ? (
                                      <div>{t('selfTest.subdivisionTip')}</div>
                                    ) : (
                                      <div>{t('selfTest.enterTip')}</div>
                                    )}
                                    {subdivisionMode && (
                                      <>
                                        <div className="border-t pt-1 mt-2">
                                          <div className="font-medium text-blue-600 dark:text-blue-400">Mode PCB actif :</div>
                                          <div>Utilisez <kbd className="bg-gray-100 dark:bg-gray-700 px-1 rounded text-xs">Z</kbd><kbd className="bg-gray-100 dark:bg-gray-700 px-1 rounded text-xs">Q</kbd><kbd className="bg-gray-100 dark:bg-gray-700 px-1 rounded text-xs">S</kbd><kbd className="bg-gray-100 dark:bg-gray-700 px-1 rounded text-xs">D</kbd> pour naviguer dans les quadrants</div>
                                          <div>⚠️ Les zones rouges ne peuvent être marquées que sur les panneaux cyan</div>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                )}
                              </>
                            )}

                            {/* NOUVEAU : Interface mobile Self-Test intégrée */}
                            {!isEmbeddedInSynoptic && selfTestMode.enabled && isMobile && isInSession && isSessionRemote && (
                              <div className="border-t pt-4 mt-4">
                                <MobileSelfTestControls
                                  selfTestMode={selfTestMode}
                                  cyanPanels={cyanPanels}
                                  subdivisionMode={subdivisionMode}
                                  selectedQuadrant={selectedQuadrant}
                                  redZones={redZones}
                                  ledWallConfig={config.ledWallMode}
                                  onNavigate={handleMobileSelfTestNavigate}
                                  onToggleCyan={handleMobileSelfTestToggleCyan}
                                  onToggleRedZone={handleMobileSelfTestToggleRedZone}
                                  onNavigateQuadrant={handleMobileSelfTestNavigateQuadrant}
                                />
                              </div>
                            )}
                          </>
                        )}

                            {/* Bouton pour envoyer manuellement la config au Synoptique */}

                      </CardContent>
                    </Card>
                  )}

                </>
              )}
            </div>
          )} {/* Fin condition !isExternalDisplay */}

          {/* Canvas Display - Toujours affiché, fullscreen en mode externe */}
          {(displayMode !== 'mobile' || isInSession || isExternalDisplay) && (
            <div className={`${isExternalDisplay ? 'fixed inset-0 z-50' : isMobile ? 'order-1' : 'lg:col-span-3'}`}>

              {/* Indicateur de session - masqué en mode externe */}
              {!isExternalDisplay && isInSession && !effectiveIsMobile && (
                <div className="mb-4 p-4 rounded-lg bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-950 dark:to-green-950 border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center gap-2">
                    {isSessionServer ? (
                      <>
                        <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                        <span className="font-medium text-green-800 dark:text-green-200">
                          🖥️ {t('session.displayMode.title')}
                        </span>
                      </>
                    ) : (
                      <>
                        <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
                        <span className="font-medium text-blue-800 dark:text-blue-200">
                          📱 {t('session.remoteMode.title')}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Card masquée en mode externe - seul le canvas est affiché */}
              {!isExternalDisplay && (
                <Card className="sticky top-4">
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <div>
                        <CardTitle>{t("preview.title")}</CardTitle>
                        <CardDescription>
                          {config.width} × {config.height} {t("preview.grid")}: {config.gridSize}px
                          {(selectedTextId || selectedLogoId) && (
                            <span className="ml-2 text-green-600">
                              <Move className="w-3 h-3 inline mr-1" />
                              {selectedTextId ? t("text.text") : "Logo"} {t("preview.selected")}
                            </span>
                          )}
                          {isExternalDisplayConnected && (
                            <span className="ml-2 text-blue-600">
                              <Monitor className="w-3 h-3 inline mr-1" />
                              {t("external.connected")}
                              {isExternalDisplayFullscreen && ` ${t("external.fullscreen")}`}
                            </span>
                          )}
                        </CardDescription>
                      </div>
                      {/* Boutons de contrôle aperçu - masqués sur mobile */}
                      {!isMobile && (
                        <div className="flex gap-2">
                          {!isEmbeddedInSynoptic && (
                            <Button
                              onClick={() => {
                                toggleAnimation();
                              }}
                              variant={isAnimating ? "destructive" : "default"}
                              size="sm"
                            >
                              {isAnimating ? (
                                <>
                                  <Pause className="w-4 h-4 mr-1" />
                                  {t("preview.stop")}
                                </>
                              ) : (
                                <>
                                  <Play className="w-4 h-4 mr-1" />
                                  {t("preview.start")}
                                </>
                              )}
                            </Button>
                          )}

                          {!isEmbeddedInSynoptic && (
                            <Button onClick={resetAnimation} variant="outline" size="sm">
                              <RotateCcw className="w-4 h-4 mr-1" />
                              {t("preview.reset")}
                            </Button>
                          )}

                          {!isEmbeddedInSynoptic && (
                            <Button onClick={handleToggleFullscreen} variant="outline" size="sm">
                              <Maximize className="w-4 h-4 mr-1" />
                              {t("preview.fullscreen")}
                            </Button>
                          )}

                          {/* Bouton Affichage Externe - masqué sur mobile */}
                          {!isMobile && !isEmbeddedInSynoptic && !externalDisplayWindow ? (
                            <div className="flex">
                              <Button
                                onClick={openExternalDisplay}
                                variant="outline"
                                size="sm"
                                className={`border-gray-400 text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800 ${availableScreens.length > 1 ? 'rounded-r-none' : 'rounded'}`}
                              >
                                <ScreenShare className="w-4 h-4 mr-1" />
                                {t("external.button")}
                              </Button>
                              {availableScreens.length > 1 && (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="border-gray-400 text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800 rounded-l-none border-l-0 px-2"
                                    >
                                      <ChevronDown className="w-3 h-3" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    {availableScreens.map((screen, index) => (
                                      <DropdownMenuItem
                                        key={screen.id}
                                        onClick={() => setSelectedScreenIndex(index)}
                                        className={selectedScreenIndex === index ? "bg-accent" : ""}
                                      >
                                        <Monitor className="w-4 h-4 mr-2" />
                                        <div className="flex flex-col">
                                          <span>{screen.name}</span>
                                          <span className="text-xs text-muted-foreground">
                                            {screen.width}×{screen.height}
                                          </span>
                                        </div>
                                      </DropdownMenuItem>
                                    ))}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              )}
                            </div>
                          ) : null}

                          {/* Bouton de contrôle quand l'affichage externe est ouvert */}
                          {!isEmbeddedInSynoptic && externalDisplayWindow && (
                            !isExternalDisplayConnected ? (
                              <Button
                                onClick={openExternalDisplay}
                                variant="outline"
                                size="sm"
                                className="border-orange-400 text-orange-700 hover:bg-orange-50 dark:border-orange-600 dark:text-orange-300 dark:hover:bg-orange-900/20"
                              >
                                <ScreenShare className="w-4 h-4 mr-1" />
                                {t("external.reconnecting")}
                              </Button>
                            ) : (
                              <Button
                                onClick={closeExternalDisplay}
                                variant="outline"
                                size="sm"
                                className="group relative overflow-hidden 
                                   bg-emerald-900 border-emerald-600 text-emerald-100 shadow-lg
                                   hover:border-red-500 hover:text-white 
                                   transition-all duration-500 ease-out
                                   before:absolute before:inset-0 before:bg-gradient-to-b 
                                   before:from-white/20 before:via-transparent before:to-transparent
                                   before:opacity-60
                                   after:absolute after:inset-0 after:bg-red-700 after:z-0
                                   after:transform after:-translate-x-full hover:after:translate-x-0
                                   after:transition-transform after:duration-500 after:ease-out
                                   backdrop-blur-sm"
                                style={{
                                  boxShadow: '0 4px 15px rgba(6, 78, 59, 0.4), inset 0 1px 0 rgba(255,255,255,0.2)',
                                }}
                              >
                                <ScreenShare className="w-4 h-4 mr-1 text-emerald-200 group-hover:text-white transition-colors duration-300 relative z-10 group-hover:opacity-0" />
                                <span className="group-hover:opacity-0 transition-opacity duration-300 relative z-10">
                                  {t("external.synchronized")}
                                </span>
                                <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
                                  <ScreenShareOff className="w-4 h-4 mr-1" />
                                  {t("external.quit")}
                                </span>
                                {isExternalDisplayFullscreen && (
                                  <div className="w-2 h-2 bg-emerald-300 rounded-full ml-2 group-hover:bg-white relative z-10" title="Plein écran actif"></div>
                                )}
                              </Button>
                            )
                          )}

                          {/* Bouton pour contrôler l'aperçu/performance quand l'affichage externe est actif */}
                          {isExternalDisplayConnected && (
                            <Button
                              onClick={() => setDisablePreviewWhenExternal(!disablePreviewWhenExternal)}
                              variant="outline"
                              size="sm"
                              className={`group relative overflow-hidden transition-all duration-500 ease-out ${disablePreviewWhenExternal
                                ? "bg-yellow-600 border-yellow-500 text-white shadow-lg hover:border-blue-500 hover:text-white before:absolute before:inset-0 before:bg-gradient-to-b before:from-white/20 before:via-transparent before:to-transparent before:opacity-60 after:absolute after:inset-0 after:bg-blue-600 after:z-0 after:transform after:-translate-x-full hover:after:translate-x-0 after:transition-transform after:duration-500 after:ease-out backdrop-blur-sm"
                                : "bg-blue-600 border-blue-500 text-white shadow-lg hover:border-yellow-500 hover:text-white before:absolute before:inset-0 before:bg-gradient-to-b before:from-white/20 before:via-transparent before:to-transparent before:opacity-60 after:absolute after:inset-0 after:bg-yellow-600 after:z-0 after:transform after:-translate-x-full hover:after:translate-x-0 after:transition-transform after:duration-500 after:ease-out backdrop-blur-sm"
                                }`}
                              style={{
                                boxShadow: disablePreviewWhenExternal
                                  ? '0 4px 15px rgba(202, 138, 4, 0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                                  : '0 4px 15px rgba(37, 99, 235, 0.4), inset 0 1px 0 rgba(255,255,255,0.2)',
                              }}
                            >
                              {disablePreviewWhenExternal ? (
                                <>
                                  <Zap className="w-4 h-4 text-yellow-200 group-hover:text-white transition-colors duration-300 relative z-10 group-hover:opacity-0" />
                                  <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
                                    <MonitorCog className="w-4 h-4" />
                                  </span>
                                </>
                              ) : (
                                <>
                                  <MonitorCog className="w-4 h-4 text-blue-200 group-hover:text-white transition-colors duration-300 relative z-10 group-hover:opacity-0" />
                                  <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
                                    <Zap className="w-4 h-4" />
                                  </span>
                                </>
                              )}
                            </Button>
                          )}

                          {/* Snake Game Controls - only visible when snake pattern is selected */}
                          {config.patternType === "snake" && (
                            <>
                              <Button
                                onClick={() => {
                                  if (snakeGameState?.isPlaying) {
                                    snakeGameControls.stopGame();
                                  } else {
                                    snakeGameControls.startGame();
                                    // Auto-start animation when starting Snake game
                                    if (!isAnimating) {
                                      setIsAnimating(true);
                                      startTimeRef.current = Date.now();
                                    }
                                  }
                                }}
                                variant={snakeGameState?.isPlaying ? "destructive" : "default"}
                                size="sm"
                              >
                                <Gamepad2 className="w-4 h-4 mr-1" />
                                {snakeGameState?.isPlaying ? t("snake.stopGame") : t("snake.startGame")}
                              </Button>

                              {(snakeGameState?.isGameOver || snakeGameState?.isVictory) && (
                                <>
                                  <SnakeScoreSubmit
                                    score={snakeGameState.score}
                                    totalPanels={snakeGameState.gameStartDimensions?.totalPanels || Math.floor(config.width / config.gridSize) * Math.floor(config.height / config.gridSize)}
                                    gridCols={snakeGameState.gameStartDimensions?.cols || Math.floor(config.width / config.gridSize)}
                                    gridRows={snakeGameState.gameStartDimensions?.rows || Math.floor(config.height / config.gridSize)}
                                    gridSize={snakeGameState.gameStartDimensions?.gridSize || config.gridSize}
                                    gridWidth={snakeGameState.gameStartDimensions?.gridWidth || config.width}
                                    gridHeight={snakeGameState.gameStartDimensions?.gridHeight || config.height}
                                    isGameOver={snakeGameState.isGameOver}
                                    isVictory={snakeGameState.isVictory}
                                    onScoreSubmitted={() => snakeLeaderboardRef.current?.refresh()}
                                  />
                                  <Button
                                    onClick={() => snakeGameControls.resetGame()}
                                    variant="outline"
                                    size="sm"
                                  >
                                    <RotateCcw className="w-4 h-4 mr-1" />
                                    {t("snake.resetGame")}
                                  </Button>
                                </>
                              )}


                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div
                      ref={fullscreenContainerRef}
                      className={`${isFullscreen ? "fixed inset-0 z-50 bg-black overflow-hidden" : "border rounded-lg overflow-hidden bg-black relative"}`}
                      style={isFullscreen ? { overflow: config.allowFullscreenScroll ? "auto" : "hidden" } : {}}
                    >
                      {renderError && (
                        <div className="absolute inset-0 flex items-center justify-center z-10 bg-black bg-opacity-70">
                          <div className="bg-red-900 text-white p-4 rounded-md max-w-md text-center">
                            <h3 className="text-lg font-bold mb-2">{t("config.renderErrorTitle")}</h3>
                            <p>{renderError}</p>
                          </div>
                        </div>
                      )}

                      <div className={isFullscreen ? "w-full h-full overflow-hidden bg-black" : "relative"} style={isFullscreen ? { overflow: config.allowFullscreenScroll ? "auto" : "hidden", position: 'relative' } : {}}>
                        {/* Canvas masqué sur mobile */}
                        {effectiveIsMobile ? (
                          // Pas d'aperçu sur mobile en session
                          <div className="hidden"></div>
                        ) : (
                          <>
                            <div
                              className={`${isFullscreen ? "absolute top-0 left-0" : "max-w-full h-auto"}`}
                              style={{
                                ...(activePatternType === 'solid' && config.backgroundColor === 'transparent' ? {
                                  backgroundImage: "linear-gradient(45deg, #808080 25%, transparent 25%), linear-gradient(-45deg, #808080 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #808080 75%), linear-gradient(-45deg, transparent 75%, #808080 75%)",
                                  backgroundSize: "20px 20px",
                                  backgroundPosition: "0 0, 0 10px, 10px -10px, -10px 0px"
                                } : {})
                              }}
                            >
                              <canvas
                                ref={canvasRef}
                                onClick={handleCanvasClick}
                                onMouseDown={handleCanvasMouseDown}
                                onMouseMove={handleCanvasMouseMove}
                                onMouseUp={handleCanvasMouseUp}
                                onMouseLeave={endDrag}
                                tabIndex={0}
                                className={`${isFullscreen ? "absolute top-0 left-0" : "max-w-full h-auto"} ${isFullscreen ? "cursor-none" : cursorStyle === 'move' ? "cursor-move" : cursorStyle === 'pointer' ? "cursor-pointer" : "cursor-crosshair"}`}
                                style={{
                                  objectFit: isFullscreen && config.stretchFullscreen ? "fill" : "contain",
                                width: isFullscreen && config.stretchFullscreen ? "100vw" : undefined,
                                height: isFullscreen && config.stretchFullscreen ? "100vh" : undefined,
                                transformOrigin: "top left",
                                display: "block",
                                // Pixel perfect positioning for fullscreen mode (same as external-display)
                                imageRendering: isFullscreen ? 'pixelated' : undefined,
                                maxWidth: isFullscreen && config.stretchFullscreen ? '100vw' : isFullscreen ? 'none' : undefined,
                                maxHeight: isFullscreen && config.stretchFullscreen ? '100vh' : isFullscreen ? 'none' : undefined,
                                position: isFullscreen ? 'absolute' : undefined,
                                top: isFullscreen ? 0 : undefined,
                                left: isFullscreen ? 0 : undefined,
                              }}
                            />
                            {/* Afficher les GIF au-dessus du canvas */}
                            {!selfTestMode.enabled && logoElements.filter(logo => logo.isGif).map((logoElement) => (
                              <img
                                key={logoElement.id}
                                src={logoElement.imageSrc}
                                alt="GIF Logo"
                                style={{
                                  position: 'absolute',
                                  left: `${logoElement.x * canvasScale.scaleX}px`,
                                  top: `${logoElement.y * canvasScale.scaleY}px`,
                                  width: `${logoElement.width * canvasScale.scaleX}px`,
                                  height: `${logoElement.height * canvasScale.scaleY}px`,
                                  opacity: logoElement.opacity,
                                  pointerEvents: 'none',
                                  imageRendering: 'auto',
                                  zIndex: 10,
                                  transformOrigin: 'top left',
                                }}
                                draggable={false}
                              />
                            ))}
                            </div>
                          </>
                        )}

                        {/* Message futuriste quand l'aperçu est désactivé - maintenant seulement sur le canvas */}
                        {disablePreviewWhenExternal && isExternalDisplayConnected && !isMobile && (
                          <div className="absolute inset-0 flex items-center justify-center z-[5] bg-black bg-opacity-90 pointer-events-auto">
                            <FuturisticMessage
                              onActivate={() => setDisablePreviewWhenExternal(false)}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {!isFullscreen && !isExternalDisplayFullscreen && !isMobile && !isEmbeddedInSynoptic && (
                      <div className="mt-4 text-sm text-muted-foreground space-y-1">
                      </div>
                    )}

                    {/* Boutons de contrôles dans l'aperçu - masqués sur mobile */}
                    {!isMobile && (
                      <div className={`mt-4 ${isEmbeddedInSynoptic ? 'flex justify-center gap-2' : 'grid grid-cols-2 md:grid-cols-5 gap-2'}`}>
                        <Button onClick={handleSaveConfig} variant="outline" className="bg-transparent" disabled={isWebCodecsExporting}>
                          <Save className="w-4 h-4 mr-2" />
                          <span className="hidden sm:inline">{t('action.save')}</span>
                          <span className="sm:hidden">{t('action.save')}</span>
                        </Button>

                        <Button onClick={handleLoadConfig} variant="outline" className="bg-transparent" disabled={isWebCodecsExporting}>
                          <FolderOpen className="w-4 h-4 mr-2" />
                          {t('action.load')}
                        </Button>

                        <Button onClick={handleExportConfigFile} variant="outline" className="bg-transparent" disabled={isWebCodecsExporting}>
                          <Download className="w-4 h-4 mr-2" />
                          <span className="hidden lg:inline">{t('action.exportVisualGrid')}</span>
                          <span className="lg:hidden hidden md:inline">.visualgrid</span>
                        </Button>

                        <Button onClick={handleImportConfigFile} variant="outline" className="bg-transparent" disabled={isWebCodecsExporting}>
                          <Upload className="w-4 h-4 mr-2" />
                          <span className="hidden lg:inline">{t('action.importVisualGrid')}</span>
                          <span className="lg:hidden hidden sm:inline">{t('action.import')}</span>
                          <span className="sm:hidden">{t('action.import')}</span>
                        </Button>

                        {!isEmbeddedInSynoptic && !isSessionRemote && (
                          <>
                            <Button onClick={handleExportCanvas} variant="outline" className="bg-transparent" disabled={isWebCodecsExporting}>
                              <Download className="w-4 h-4 mr-2" />
                              <span className="hidden sm:inline">{t('action.exportPNG')}</span>
                              <span className="sm:hidden">PNG</span>
                            </Button>
                            <Button
                              onClick={handleExportLoopVideo}
                              variant="outline"
                              className="bg-transparent border-green-500/50 hover:bg-green-500/10"
                              disabled={isWebCodecsExporting}
                            >
                              <Zap className="w-4 h-4 mr-2 text-green-500" />
                              <span className="hidden sm:inline">{t('action.exportVideo')}</span>
                              <span className="sm:hidden">MP4</span>
                            </Button>
                          </>
                        )}
                      </div>
                    )}

                    {/* Boutons Actions Rapides - Affichés sur mobile uniquement */}
                    {isMobile && !isEmbeddedInSynoptic && (
                      <div className="mt-4 space-y-3">
                        {/* Bouton Play/Pause Animation - Identique à la version desktop */}
                        <Button
                          onClick={() => {
                            toggleAnimation();
                          }}
                          variant={isAnimating ? "destructive" : "default"}
                          className="w-full"
                        >
                          {isAnimating ? (
                            <>
                              <Pause className="w-4 h-4 mr-1" />
                              {t("preview.stop")}
                            </>
                          ) : (
                            <>
                              <Play className="w-4 h-4 mr-1" />
                              {t("preview.start")}
                            </>
                          )}
                        </Button>

                        {/* Bouton Reset - Identique à la version desktop */}
                        <Button
                          onClick={resetAnimation}
                          variant="outline"
                          className="w-full"
                        >
                          <RotateCcw className="w-4 h-4 mr-1" />
                          {t("preview.reset")}
                        </Button>

                        {/* Bouton Export PNG - Identique à la version desktop */}
                        {!isSessionRemote && (
                          <Button
                            onClick={handleExportCanvas}
                            variant="outline"
                            className="w-full bg-transparent"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            {t('action.exportPNG')}
                          </Button>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )} {/* Fin condition !isExternalDisplay pour Card */}

              {/* En mode externe, afficher seulement le canvas en fullscreen */}
              {isExternalDisplay && (
                <div
                  ref={fullscreenContainerRef}
                  className="fixed inset-0 z-50 bg-black overflow-hidden"
                  style={{ overflow: 'hidden' }}
                >
                  <div className="w-full h-full overflow-hidden bg-black relative" style={{ overflow: 'hidden', position: 'relative' }}>
                    <div
                      className="absolute top-0 left-0 w-full h-full"
                      style={{
                        ...(activePatternType === 'solid' && config.backgroundColor === 'transparent' ? {
                          backgroundImage: "linear-gradient(45deg, #808080 25%, transparent 25%), linear-gradient(-45deg, #808080 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #808080 75%), linear-gradient(-45deg, transparent 75%, #808080 75%)",
                          backgroundSize: "20px 20px",
                          backgroundPosition: "0 0, 0 10px, 10px -10px, -10px 0px"
                        } : {})
                      }}
                    >
                      <canvas
                        ref={canvasRef}
                        onClick={handleCanvasClick}
                        onMouseDown={handleCanvasMouseDown}
                        onMouseMove={handleCanvasMouseMove}
                        onMouseUp={handleCanvasMouseUp}
                        onMouseLeave={endDrag}
                        tabIndex={0}
                        className="absolute top-0 left-0 cursor-none"
                        style={{
                          objectFit: config.stretchFullscreen ? "fill" : "contain",
                        width: config.stretchFullscreen ? "100vw" : undefined,
                        height: config.stretchFullscreen ? "100vh" : undefined,
                        transformOrigin: "top left",
                        display: "block",
                        imageRendering: 'pixelated',
                        maxWidth: config.stretchFullscreen ? '100vw' : 'none',
                        maxHeight: config.stretchFullscreen ? '100vh' : 'none',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                      }}
                    />
                    {/* Afficher les GIF au-dessus du canvas en mode externe */}
                    {!selfTestMode.enabled && logoElements.filter(logo => logo.isGif).map((logoElement) => (
                      <img
                        key={logoElement.id}
                        src={logoElement.imageSrc}
                        alt="GIF Logo"
                        style={{
                          position: 'absolute',
                          left: `${logoElement.x * canvasScale.scaleX}px`,
                          top: `${logoElement.y * canvasScale.scaleY}px`,
                          width: `${logoElement.width * canvasScale.scaleX}px`,
                          height: `${logoElement.height * canvasScale.scaleY}px`,
                          opacity: logoElement.opacity,
                          pointerEvents: 'none',
                          imageRendering: 'auto',
                          zIndex: 10,
                          transformOrigin: 'top left',
                        }}
                        draggable={false}
                      />
                    ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>


        {/* Snake Leaderboard - masqué en mode externe */}
        {!isExternalDisplay && config.patternType === "snake" && (
          <div className="w-full max-w-2xl mx-auto mt-6">
            <SnakeLeaderboard ref={snakeLeaderboardRef} />
          </div>
        )}
      </div>

      {/* Export Video Dialog - WebCodecs GPU Export */}
      <FfmpegExportDialog
        open={showExportDialog}
        onOpenChange={setShowExportDialog}
        onExport={handleWebCodecsExportWithSettings}
        defaultFps={60}
        canvasWidth={canvasRef.current?.width || 0}
        canvasHeight={canvasRef.current?.height || 0}
        isWebCodecsSupported={isWebCodecsSupported}
        isTauriApp={isTauriApp}
      />

      {/* WebCodecs Export Progress */}
      {isWebCodecsExporting && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-background border rounded-lg p-6 max-w-md w-full mx-4 space-y-4">
            {/* Header with phase-aware icon */}
            <div className="flex items-center gap-3">
              {webCodecsPhase === 'done' ? (
                <CheckCircle className="w-6 h-6 text-green-500" />
              ) : webCodecsPhase === 'converting' ? (
                <Video className="w-6 h-6 text-blue-500 animate-pulse" />
              ) : (
                <Loader2 className="w-6 h-6 text-primary animate-spin" />
              )}
              <div>
                <h3 className="font-semibold text-lg">
                  {webCodecsPhase === 'done' ? t('export.progress.title.complete') : 
                   webCodecsPhase === 'converting' ? t('export.progress.title.transcoding', { format: webCodecsSelectedFormat }) :
                   webCodecsPhase === 'saving' ? t('export.progress.title.saving') :
                   t('export.progress.title.encoding')}
                </h3>
                {webCodecsPhase === 'converting' && (
                  <p className="text-sm text-muted-foreground">{webCodecsSelectedFormat}</p>
                )}
              </div>
            </div>
            
            {/* Pipeline indicator */}
            <div className="flex items-center justify-center gap-2 text-xs py-2">
              <span className={`px-2 py-1 rounded transition-all ${
                webCodecsPhase === 'encoding' 
                  ? 'bg-primary/20 text-primary font-medium' 
                  : webCodecsPhase === 'idle' 
                    ? 'bg-muted text-muted-foreground'
                    : 'bg-green-500/10 text-green-600'
              }`}>
                {webCodecsPhase !== 'encoding' && webCodecsPhase !== 'idle' ? '✓ ' : ''}{t('export.progress.encoding')}
              </span>
              <span className="text-muted-foreground">→</span>
              <span className={`px-2 py-1 rounded transition-all ${
                webCodecsPhase === 'converting' || webCodecsPhase === 'saving'
                  ? 'bg-blue-500/20 text-blue-600 font-medium' 
                  : webCodecsPhase === 'done'
                    ? 'bg-green-500/10 text-green-600'
                    : 'bg-muted text-muted-foreground'
              }`}>
                {webCodecsPhase === 'done' ? '✓ ' : ''}{t('export.progress.transcoding.step')}
              </span>
              <span className="text-muted-foreground">→</span>
              <span className={`px-2 py-1 rounded transition-all ${
                webCodecsPhase === 'done' 
                  ? 'bg-green-500/20 text-green-600 font-medium' 
                  : 'bg-muted text-muted-foreground'
              }`}>
                {webCodecsPhase === 'done' ? '✓' : '○'}
              </span>
            </div>
            
            {/* Progress bar */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>{t('export.progress.progression')}</span>
                <span>{Math.round(webCodecsProgress)}%</span>
              </div>
              <div className="w-full bg-secondary rounded-full h-3 overflow-hidden">
                <div 
                  className={`h-3 rounded-full transition-all duration-500 ease-out ${
                    webCodecsPhase === 'done' 
                      ? 'bg-green-500' 
                      : webCodecsPhase === 'converting' || webCodecsPhase === 'saving'
                        ? 'bg-gradient-to-r from-green-500 to-blue-500' 
                        : 'bg-primary'
                  }`}
                  style={{ width: `${webCodecsProgress}%` }}
                />
              </div>
            </div>
            
            {/* Stats - only show during encoding */}
            {webCodecsPhase === 'encoding' && (
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">{t('export.progress.frame')}</p>
                  <p className="font-medium">{webCodecsCurrentFrame} / {webCodecsTotalFrames}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">{t('export.progress.speed')}</p>
                  <p className="font-medium">{webCodecsExportSpeed.toFixed(0)} fps</p>
                </div>
                <div>
                  <p className="text-muted-foreground">{t('export.progress.timeRemaining')}</p>
                  <p className="font-medium">{webCodecsTimeRemaining > 0 ? `~${Math.ceil(webCodecsTimeRemaining)}s` : '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">{t('export.progress.step')}</p>
                  <p className="font-medium text-primary">{t('export.progress.encoding')}</p>
                </div>
              </div>
            )}

            {/* Success message */}
            {webCodecsPhase === 'done' && (
              <div className="p-3 bg-green-500/10 border border-green-500/50 rounded-lg text-center">
                <p className="text-sm text-green-600 dark:text-green-400 font-medium">
                  {t('export.progress.success')}
                </p>
              </div>
            )}

            {webCodecsError && (
              <div className="p-3 bg-destructive/10 border border-destructive rounded-lg">
                <p className="text-sm text-destructive">{webCodecsError}</p>
              </div>
            )}

            {/* Show Close button when done, Cancel during export */}
            {webCodecsPhase === 'done' ? (
              <Button 
                onClick={closeWebCodecsExport} 
                variant="outline"
                className="w-full bg-white text-black hover:bg-gray-100 dark:bg-white dark:text-black dark:hover:bg-gray-200"
              >
                {t('export.progress.close')}
              </Button>
            ) : (
              <Button 
                onClick={cancelWebCodecsExport} 
                variant="outline" 
                className="w-full"
              >
                <X className="w-4 h-4 mr-2" />
                {t('export.dialog.cancel')}
              </Button>
            )}
          </div>
        </div>
      )}

      {!isExternalDisplay && <Footer showNavLinks={true} />}
    </div>
  )
}

export default function LEDTestPatternGenerator() {
  return (
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
      <LEDTestPatternGeneratorContent />
    </ThemeProvider>
  )
}